'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Input,
  MessagePlugin,
  RadioGroup,
  Skeleton,
  Textarea,
  Upload,
} from 'tdesign-react';
import {
  AddIcon,
  CloseIcon,
  DeleteIcon,
  Edit1Icon,
  PoweroffIcon,
  RefreshIcon,
  SaveIcon,
  SendIcon,
} from 'tdesign-icons-react';
import type { UploadFile, UploadProps } from 'tdesign-react';
import styles from './ConferenceChatPage.module.css';
import avatarDisabled from './assets/avatar-disabled.svg';
import detailAgendaDecoration from './assets/detail-agenda-decoration.png';
import mobileContactEmptyState from './assets/mobile-contact-empty-state.svg';
import mobileContactDetailGuestBg from './assets/mobile-contact-detail-guest-bg.png';
import mobileContactDetailPartnerBg from './assets/mobile-contact-detail-partner-bg.png';
import mobileAgendaBg from './assets/mobile-agenda-bg.png';
import mobileMessagesBg from './assets/mobile-messages-bg.png';
import mobileProfileHeroBg from './assets/mobile-profile-hero-bg.png';
import mobileProfileHeroOverlay from './assets/mobile-profile-hero-overlay.png';
import {
  HERO_SUBTITLE as GUEST_HERO_SUBTITLE,
  HERO_TITLE as GUEST_HERO_TITLE,
  MEETING_DATE_TEXT as GUEST_MEETING_DATE_TEXT,
  MEETING_DESCRIPTION as GUEST_MEETING_DESCRIPTION,
  MEETING_LOCATION_TEXT as GUEST_MEETING_LOCATION_TEXT,
  MEETING_TITLE as GUEST_MEETING_TITLE,
} from '../s-2026-signup/config';
import {
  HERO_SUBTITLE as PARTNER_HERO_SUBTITLE,
  HERO_TITLE as PARTNER_HERO_TITLE,
  MEETING_DATE_TEXT as PARTNER_MEETING_DATE_TEXT,
  MEETING_DESCRIPTION as PARTNER_MEETING_DESCRIPTION,
  MEETING_LOCATION_TEXT as PARTNER_MEETING_LOCATION_TEXT,
  MEETING_TITLE as PARTNER_MEETING_TITLE,
} from '../s-2026-industry-partner/config';
import {
  MEETING_DATE_TEXT as GUEST_MEETING_DATE_TEXT_EN,
  MEETING_DESCRIPTION as GUEST_MEETING_DESCRIPTION_EN,
} from '../s-2026-signup-en/config';
import {
  CHAT_MESSAGE_ALG,
  CHAT_MESSAGE_KEY_ALG,
  CHAT_PUBLIC_KEY_ALG,
  decryptChatMessage,
  encryptChatMessage,
  generateChatSessionKeyPair,
  unsealChatPrivateKey,
} from './conferenceChatCrypto';
import {
  buildChatConversationCacheKey,
  buildChatMessageCacheKey,
  listCachedChatMessages,
  saveCachedChatMessages,
  type CachedChatMessageRecord,
} from './conferenceChatCache';
import { createConferenceChatPolling } from './conferenceChatPolling';
import {
  buildConferenceChatSystemParticipantSummary,
  isConferenceChatSystemParticipant,
  normalizeConferenceChatParticipantType as normalizeConferenceChatSystemParticipantType,
} from './conferenceChatSystemParticipant';
import {
  filterConferenceChatThreadMessages,
  isConferenceChatThreadRequestCurrent,
} from './conferenceChatThreadGuards';
import {
  ClientApi,
  getPreviewUrlsEn,
  getUserInfo,
  postUploadFileEn,
} from '@ssv-website/hooks';
import {
  getConferenceChatHomeURL,
  isConferenceChatLogoutInProgress,
  isConferenceChatGuestSelf,
} from '../../navbar/conferenceChat';
import { DesktopEmptyState } from './DesktopEmptyState';
import {
  AccountDeletionDangerEntry,
  AccountDeletionFlow,
} from '../../account-deletion';
import ConferenceChatConferenceAgendaSection from './ConferenceChatConferenceAgendaSection';
import {
  loadLatestConferenceAgendaSectionData,
  type LatestConferenceAgendaSectionData,
} from './conferenceAgenda.helpers';

const DEFAULT_CHAT_API_PREFIX = '/portalWebAPI/conference/chat';
const DEFAULT_CONTACT_PAGE_SIZE = 1000;
const PROFILE_UPDATE_REVIEW_STATUS_PENDING = 1;
const PROFILE_UPDATE_REVIEW_STATUS_APPROVED = 2;
const CHAT_CONVERSATION_PAGE_SIZE = 100;
const CHAT_MESSAGE_PAGE_SIZE = 100;
const CHAT_MESSAGE_PAGE_LIMIT = 10;
const CHAT_MESSAGE_POLL_INTERVAL_MS = 5000;
const CHAT_MAX_MESSAGE_LENGTH = 200;
const CONFERENCE_CHAT_ACCESS_REVOKED_MSG = 'conference_chat_access_revoked';
const CONFERENCE_CHAT_SELF_CONTACT_NOT_ALLOWED_MSG =
  'conference_chat_self_contact_not_allowed';
const CONFERENCE_CHAT_PEER_OFFLINE_MSG = 'conference_chat_peer_offline';
const CONFERENCE_CHAT_LOGIN_CODES = new Set([4000, 4001, 4002]);
const CONFERENCE_CHAT_LOGIN_EXPIRED_KEYWORDS = [
  '登录',
  '未登录',
  'login',
  'expired',
  'unauthorized',
];
const CONFERENCE_CHAT_CONTACT_NOT_ALLOWED_MSGS = new Set([
  '对方不允许通过私信联系，发送失败',
  'The recipient does not allow direct messages. Sending failed.',
]);
const CONFERENCE_CHAT_PEER_DISPLAY_STATUS_OFFLINE = 'offline';
const CONFERENCE_CHAT_DISABLE_REASON_PEER_OFFLINE = 'peer_offline';
const MOBILE_CONTACT_SEARCH_HISTORY_STORAGE_KEY =
  'conference-chat-mobile-contact-search-history';
const EN_PRIVACY_ALERT_DISMISSED_STORAGE_KEY =
  'conference-chat-en-privacy-alert-dismissed';
const EN_PRIVACY_ALERT_MESSAGE =
  'For your privacy, please avoid sharing sensitive information here, such as ID numbers, financial details, or health data.';
const MOBILE_CONTACT_SEARCH_HISTORY_LIMIT = 20;
const getAssetSrc = (asset: unknown) =>
  typeof asset === 'string' ? asset : (asset as { src: string }).src;

const CONFERENCE_CHAT_DISABLED_AVATAR_SRC = getAssetSrc(avatarDisabled);
const DETAIL_AGENDA_DECORATION_SRC = getAssetSrc(detailAgendaDecoration);
const MOBILE_CONTACT_EMPTY_STATE_SRC = getAssetSrc(mobileContactEmptyState);
const MOBILE_CONTACT_DETAIL_GUEST_BG_SRC = getAssetSrc(
  mobileContactDetailGuestBg
);
const MOBILE_CONTACT_DETAIL_PARTNER_BG_SRC = getAssetSrc(
  mobileContactDetailPartnerBg
);
const MOBILE_AGENDA_BG_SRC = getAssetSrc(mobileAgendaBg);
const MOBILE_MESSAGES_BG_SRC = getAssetSrc(mobileMessagesBg);
const MOBILE_PROFILE_HERO_BG_SRC = getAssetSrc(mobileProfileHeroBg);
const MOBILE_PROFILE_HERO_OVERLAY_SRC = getAssetSrc(mobileProfileHeroOverlay);
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

type ChatLocale = 'zh' | 'en';
type ContactType = 'guest' | 'partner' | 'audience';
type MainTab = 'contacts' | 'messages' | 'agenda';
type MobileProfileViewMode = 'display' | 'edit';
type DesktopProfileViewMode = 'display' | 'edit';
type ChatNavItem = {
  key: MainTab;
  label: string;
  icon: JSX.Element;
  dot?: boolean;
};

const getConferenceChatAccessFallbackURL = (site: ChatLocale) => {
  if (site !== 'en') {
    return getConferenceChatHomeURL('zh');
  }
  if (typeof window === 'undefined') {
    return '/';
  }
  const fallbackURL = new URL(
    getConferenceChatHomeURL('en'),
    window.location.origin
  );
  fallbackURL.pathname = '/';
  fallbackURL.search = '';
  fallbackURL.hash = '';
  return fallbackURL.toString();
};

const CHAT_CONTACT_TYPE_GUEST = 'guest';
const CHAT_CONTACT_TYPE_PARTNER = 'partner';
const CHAT_CONTACT_TYPE_AUDIENCE = 'audience';
const AVATAR_ACCEPT = '.jpg,.jpeg,.png';
const AVATAR_UPLOAD_MODULE = 'team';
const GUEST_AVATAR_UPLOAD_API = '/portalWebAPI/conference/guest/avatar/upload';
const INDUSTRY_PARTNER_AVATAR_UPLOAD_API =
  '/portalWebAPI/conference/industryPartner/avatar/upload';
const AVATAR_SIZE_LIMIT_MB = 2;
const AVATAR_TYPE_LIST = ['image/jpeg', 'image/jpg', 'image/png'];
const AVATAR_EXT_LIST = ['jpg', 'jpeg', 'png'];
const AVATAR_UPLOAD_ERROR_MESSAGE_ZH = '头像上传失败，请重试';
const AVATAR_UPLOAD_ERROR_MESSAGE_EN =
  'Avatar upload failed. Please try again.';
const AVATAR_FILE_TYPE_ERROR_MESSAGE_ZH =
  '上传文件类型错误，请选择 .jpg、.jpeg、.png 格式的图片文件';
const AVATAR_FILE_TYPE_ERROR_MESSAGE_EN =
  'Invalid file type. Please upload a .jpg, .jpeg, or .png image file.';
const AVATAR_SIZE_LIMIT_BYTES = AVATAR_SIZE_LIMIT_MB * 1024 * 1024;
const PROFILE_TITLE_MAX_COUNT = 3;
const EN_PROFILE_BASE_TITLE_COUNT = 2;
const MAIN_TAB_QUERY_KEY = 'tab';
const CONTACT_ID_QUERY_KEY = 'contactId';
const GUEST_DATA_SOURCE_QUERY_KEY = 'guestDataSource';
// PortalResp 承接国内 Portal 和海外 BFF 统一响应格式，便于中英文页面复用同一组件。
interface PortalResp<T> {
  code?: number;
  msg?: string;
  data?: T;
}

const isConferenceChatLoginExpiredPayload = (payload?: PortalResp<unknown>) => {
  if (!payload || payload.code === undefined) {
    return false;
  }
  if (CONFERENCE_CHAT_LOGIN_CODES.has(payload.code)) {
    return true;
  }
  const msg = payload.msg?.trim().toLowerCase() || '';
  if (payload.code === 401) {
    return true;
  }
  if (payload.code !== 403 || !msg) {
    return false;
  }
  return CONFERENCE_CHAT_LOGIN_EXPIRED_KEYWORDS.some((keyword) =>
    msg.includes(keyword)
  );
};

// ChatAuthInfo 是会聊入口和页面二次鉴权结果，前端只根据 CanAccess 决定是否展示业务内容。
interface ChatAuthInfo {
  CanAccess?: boolean;
  Identity?: string;
  IdentityList?: string[];
  ConferenceYear?: number;
  ChatLocale?: string;
  DenyToast?: string;
  ContactID?: string;
  ContactType?: string;
  GuestDataSource?: string;
  NameCN?: string;
  NameEN?: string;
  Avatar?: string;
  UserGUID?: string;
}

// ContactTitle 对应嘉宾库多头衔展示，按当前语言优先取对应字段。
interface ContactTitle {
  TitleCN?: string;
  TitleEN?: string;
}

// AgendaItem 是嘉宾详情和我的议程共用的展示结构；当前后端无结构化数据源时会返回空数组。
interface AgendaItem {
  AgendaID?: string;
  AgendaItemID?: string;
  agendaId?: string;
  agendaItemId?: string;
  TitleCN?: string;
  titleCn?: string;
  TitleEN?: string;
  titleEn?: string;
  ForumTitleCN?: string;
  forumTitleCn?: string;
  ForumTitleEN?: string;
  forumTitleEn?: string;
  SessionTitleCN?: string;
  sessionTitleCn?: string;
  SessionTitleEN?: string;
  sessionTitleEn?: string;
  StartTime?: number | string;
  startTime?: number | string;
  EndTime?: number | string;
  endTime?: number | string;
  AddressCN?: string;
  addressCn?: string;
  AddressEN?: string;
  addressEn?: string;
  LocationCN?: string;
  locationCn?: string;
  LocationEN?: string;
  locationEn?: string;
  Role?: string;
  role?: string;
}

// ContactItem 是会聊联系人卡片和详情页共用结构，嘉宾和行业伙伴通过 ContactType 区分。
interface ContactItem {
  ContactID?: string;
  ContactType?: string;
  GuestDataSource?: string;
  HasPendingProfileUpdate?: boolean;
  ProfileUpdateReviewStatus?: number;
  ProfileUpdateReviewID?: string;
  NameCN?: string;
  NameEN?: string;
  Avatar?: string;
  Organization?: string;
  Position?: string;
  Titles?: ContactTitle[];
  BioCN?: string;
  BioEN?: string;
  Agenda?: AgendaItem[];
}

// ContactListResp 对齐后端联系人列表响应，列表页不做前端分页 UI。
interface ContactListResp {
  list?: ContactItem[];
}

// AgendaMineResp 对齐“我的议程”接口；Identity 预留给后续按身份差异展示。
interface AgendaMineResp {
  list?: AgendaItem[];
  List?: AgendaItem[];
  conferenceList?: AgendaItem[];
  ConferenceList?: AgendaItem[];
  Identity?: string;
}

interface AgendaSection {
  key: string;
  title: string;
  items: AgendaItem[];
}

interface ProfileTitleItem {
  titleCn?: string;
  titleEn?: string;
  TitleCN?: string;
  TitleEN?: string;
}

interface ConferenceChatProfileDTO {
  registerId?: string;
  partnerRegisterId?: string;
  avatar?: string;
  avatarPath?: string;
  firstName?: string;
  lastName?: string;
  nameCn?: string;
  nameEn?: string;
  titles?: ProfileTitleItem[];
  nationality?: string;
  phone?: string;
  email?: string;
  organization?: string;
  position?: string;
  bioCn?: string;
  bioEn?: string;
  allowContact?: number;
  hasPendingProfileUpdate?: boolean;
  profileUpdateReviewStatus?: number;
  profileUpdateReviewId?: string;
}

interface ConferenceChatProfileDetailResp {
  ContactType?: string;
  GuestDataSource?: string;
  Profile?: ConferenceChatProfileDTO;
}

interface ConferenceChatProfileForm {
  registerId?: string;
  partnerRegisterId?: string;
  avatarPreviewUrl: string;
  avatarSubmitPath: string;
  firstName: string;
  lastName: string;
  nameCn: string;
  nameEn: string;
  titles: Array<{ titleCn: string; titleEn: string }>;
  nationality: string;
  phone: string;
  email: string;
  organization: string;
  position: string;
  bioCn: string;
  bioEn: string;
  allowContact: number;
}

type ConferenceChatProfileUpdatePayload = {
  registerId?: string;
  partnerRegisterId?: string;
  avatar: string;
  firstName: string;
  lastName: string;
  nameCn: string;
  nameEn: string;
  titles: Array<{ titleCn: string; titleEn: string }>;
  nationality: string;
  phone: string;
  email: string;
  organization: string;
  position: string;
  bioCn: string;
  bioEn: string;
  allowContact: number;
} & Record<string, unknown>;

type UploadResponseRecord = Record<string, unknown>;

interface MobileProfileMeetingMeta {
  heroTitle: string;
  heroSubtitle: string;
  meetingTitle: string;
  meetingDescription: string;
  meetingDateText: string;
  meetingLocationText: string;
  meetingLocationSubline?: string;
  editButtonText?: string;
}

interface ChatParticipantDTO {
  Region?: number;
  Type?: number;
  SourceID?: string;
}

interface ConferenceChatBootstrapResp {
  ConferenceYear?: number;
  Region?: number;
  Participant?: ChatParticipantDTO;
  UserGUID?: string;
  KeyVersion?: number;
  StorageMode?: number;
}

interface ConferenceChatPublicKeyResp {
  Participant?: ChatParticipantDTO;
  PublicKey?: string;
  PublicKeyAlg?: string;
  KeyVersion?: number;
}

interface ConferenceChatKeyUnsealResp {
  WrappedPrivateKey?: string;
  WrappedPrivateKeyNonce?: string;
  EncryptedSessionKey?: string;
  EncryptedSessionKeyAlg?: string;
  PrivateKeyAlg?: string;
  KeyVersion?: number;
}

interface ConferenceChatConversationItem {
  ConversationID?: string;
  ConversationHash?: string;
  PeerParticipant?: ChatParticipantDTO;
  PeerNameCN?: string;
  PeerNameEN?: string;
  PeerAvatar?: string;
  PeerDisplayStatus?: string;
  CanSendMessage?: boolean;
  DisableReason?: string;
  DisableHint?: string;
  LastMessageID?: string;
  LastMessageRegion?: number;
  LastMessageTime?: string;
  LastMessageCiphertext?: string;
  LastMessageNonce?: string;
  LastMessageCipherAlg?: string;
  LastEncryptedMessageKey?: string;
  LastMessageKeyAlg?: string;
  Unread?: boolean;
  StorageMode?: number;
}

interface ConferenceChatConversationListResp {
  list?: ConferenceChatConversationItem[];
  page?: number;
  pageSize?: number;
}

interface ConferenceChatMessageItem {
  MessageID?: string;
  ConversationID?: string;
  MessageRegion?: number;
  Sender?: ChatParticipantDTO;
  Receiver?: ChatParticipantDTO;
  ClientMsgID?: string;
  Ciphertext?: string;
  CiphertextNonce?: string;
  CiphertextAlg?: string;
  SendTime?: string;
  EncryptedMessageKey?: string;
  MessageKeyAlg?: string;
  KeyVersion?: number;
  ReadStatus?: number;
}

interface ConferenceChatMessageListResp {
  list?: ConferenceChatMessageItem[];
  page?: number;
  pageSize?: number;
}

interface ConferenceChatMessageSendResp {
  MessageID?: string;
  ConversationID?: string;
  MessageRegion?: number;
  SendTime?: string;
  Duplicated?: boolean;
}

interface ChatParticipantRef {
  region: number;
  type: number;
  sourceID: string;
}

interface ChatPeerSummary {
  participant: ChatParticipantRef;
  name: string;
  title: string;
  avatar?: string;
}

interface ChatConversationPreview {
  text: string;
  status: 'ready' | 'empty' | 'failed';
}

interface ChatConversationView {
  key: string;
  peer: ChatParticipantRef;
  peerSummary: ChatPeerSummary;
  unread: boolean;
  preview: ChatConversationPreview;
  lastMessageTime?: string;
  conversationID: string;
  conversationHash: string;
  storageMode?: number;
  peerDisplayStatus?: string;
  canSendMessage: boolean;
  disableReason?: string;
  disableHint?: string;
  raw?: ConferenceChatConversationItem;
  isDraft?: boolean;
}

interface ChatMessageView {
  key: string;
  messageID: string;
  clientMsgID?: string;
  plaintext: string;
  fromSelf: boolean;
  sendTime: string;
  readStatus: number;
  decryptStatus: 'ready' | 'failed' | 'sending';
  sender: ChatParticipantRef;
  receiver: ChatParticipantRef;
}

interface ChatSessionState {
  conferenceYear: number;
  userGuid: string;
  participant: ChatParticipantRef;
  privateKey: CryptoKey;
  storageMode: number;
}

// ConferenceChatPageProps 允许中文站和英文站指定不同接口前缀，其余交互共用。
interface ConferenceChatPageProps {
  locale?: ChatLocale;
  apiPrefix?: string;
  contactPageSize?: number;
  embedded?: boolean;
  initialMainTab?: MainTab;
  initialIsMobileViewport?: boolean;
  initialJumpContactID?: string;
  initialJumpGuestDataSource?: string;
}

type AgendaCardMode = 'simple' | 'detail';

type DirectChatJumpState =
  | {
      status: 'invalid';
    }
  | {
      status: 'pending' | 'loading';
      contactId: string;
      guestDataSource: string;
    };

const TEXT = {
  zh: {
    title: '会聊',
    subtitle: '可持续社会价值创新大会',
    contacts: '联系人',
    messages: '消息',
    agenda: '议程',
    guest: '大会嘉宾',
    partner: '行业伙伴',
    searchPlaceholder: '请输入关键词',
    mobileContactSearchPlaceholder: '搜索名字、职位、机构等',
    mobileContactSearchPageTitle: '搜索',
    mobileContactSearchInputPlaceholder: '请搜索你想要的内容',
    mobileContactSearchCancel: '取消',
    mobileContactSearchHistory: '历史记录',
    mobileContactSearchEmpty: '暂未找到相关联系人',
    loading: '加载中...',
    noAccess: '暂无访问权限',
    loadAuthFailed: '会聊权限获取失败',
    contactFailed: '联系人获取失败',
    detailFailed: '联系人详情获取失败',
    agendaFailed: '我的议程获取失败',
    conferenceAgenda: '大会议程',
    emptyContacts: '暂无联系人',
    emptyAgenda: '暂无议程',
    selectContact: '请选择联系人',
    intro: '简介',
    guestAgenda: '嘉宾议程',
    signupAgenda: '报名议程',
    guestDetail: '嘉宾详情',
    startChat: '发消息',
    backToContacts: '返回联系人',
    editInfo: '编辑信息',
    profileUpdateReviewing: '更新信息审核中',
    profileEditTitle: '编辑个人资料',
    profileBaseInfoTitle: '基础信息',
    profileEditSubtitle: '资料提交后将进入后台审核，审核通过后生效',
    profileLoadFailed: '资料获取失败',
    profileSubmitFailed: '资料提交失败',
    profileSubmitSuccess: '提交成功，等待审核中',
    profileNoChange: '信息未修改，无需提交审核',
    profileBack: '取消编辑',
    profileExit: '退出',
    profileSubmit: '提交信息',
    profileDisplayClose: '返回',
    profileDisplaySubmit: '提交并保存',
    profileAddTitle: '新增头衔',
    profileRemoveTitle: '删除',
    clearHistory: '清空历史记录',
    profileAvatar: '头像',
    profileNameCN: '中文姓名',
    profileNameEN: '英文姓名',
    profileFirstName: 'First Name',
    profileLastName: 'Last Name',
    profileTitle: '头衔',
    profileTitleCN: '中文头衔',
    profileTitleEN: '英文头衔',
    profileNationality: '国家或地区',
    profilePhone: '电话',
    profileEmail: '邮箱',
    profileOrganization: '机构',
    profilePosition: '职位',
    profileBioCN: '中文简介',
    profileBioEN: '英文简介',
    profileAllowContact: '是否允许通过站内信联系您',
    profileAllow: '允许',
    profileReject: '拒绝',
    profileAvatarHint: '请上传您的高清真实头像，大小不超过2M',
    profileIntro: '个人简介',
    profileIdentityGuest: '嘉宾',
    profileIdentityPartner: '行业伙伴',
    unnamed: '未命名',
    summitName: '可持续社会价值创新大会',
    refresh: '刷新',
    messageLoading: '消息加载中...',
    conversationFailed: '会话列表获取失败',
    messageFailed: '消息列表获取失败',
    messageSendFailed: '消息发送失败',
    messageSelfContactNotAllowed:
      '您当前已关闭私信联系功能，请前往个人信息编辑中开启后再发送消息。',
    messageContactNotAllowed: '对方不允许通过私信联系，发送失败',
    messagePeerOffline: '该用户已无法接收消息',
    messageAccessRevoked: '当前账号已失去会聊访问资格',
    conversationPeerExited: '该用户已退出',
    messageAckFailed: '消息已读状态同步失败',
    messageInitFailed: '消息能力初始化失败',
    messageEmpty: '请输入消息内容',
    messageTooLong: '消息最多 200 字',
    messageListEmpty: '暂无会话',
    messageThreadEmpty: '还没有消息，发一条开始会聊',
    messageSelectEmpty: '请选择会话',
    messageDraftTitle: '新会话',
    messagePreviewFallback: '消息暂不可预览',
    messageDecryptFallback: '该消息暂不可查看',
    messageWeakHint: '部分消息可能因保留期或密钥变化暂不可恢复',
    messageInputPlaceholder: '发送给',
    messageSend: '发送',
    unknownContact: '未识别联系人',
    guestTag: '嘉宾',
    partnerTag: '行业伙伴',
    audienceTag: '观众',
    domesticTag: '国内',
    overseasTag: '海外',
  },
  en: {
    title: 'Chat',
    subtitle: 'Tencent SSV Sustainable Social Value Innovation Summit',
    contacts: 'Contacts',
    messages: 'Messages',
    agenda: 'Agenda',
    guest: 'Summit Guests',
    partner: 'Industry Partners',
    searchPlaceholder: 'Please enter keywords',
    mobileContactSearchPlaceholder: 'Search by name, title, organization, etc.',
    mobileContactSearchPageTitle: 'Search',
    mobileContactSearchInputPlaceholder: 'Search for you need.',
    mobileContactSearchCancel: 'Cancel',
    mobileContactSearchHistory: 'History',
    mobileContactSearchEmpty: 'No relevant contacts found',
    loading: 'Loading...',
    noAccess: 'Access unavailable',
    loadAuthFailed: 'Failed to verify chat access',
    contactFailed: 'Failed to load contacts',
    detailFailed: 'Failed to load contact details',
    agendaFailed: 'Failed to load agenda',
    conferenceAgenda: 'Conference Agenda',
    emptyContacts: 'No contacts',
    emptyAgenda: 'No agenda',
    selectContact: 'Select a contact',
    intro: 'Introduction',
    guestAgenda: 'Guest Agenda',
    signupAgenda: 'Registered Sessions',
    guestDetail: 'Guest Details',
    startChat: 'Send Messages',
    backToContacts: 'Back to Contacts',
    editInfo: 'Edit Info',
    profileUpdateReviewing: 'Information update under review',
    profileEditTitle: 'Edit Profile',
    profileBaseInfoTitle: 'Basic Information',
    profileEditSubtitle: 'Submitted information will take effect after review',
    profileLoadFailed: 'Failed to load profile',
    profileSubmitFailed: 'Failed to submit profile',
    profileSubmitSuccess: 'Submitted successfully. Waiting for review.',
    profileNoChange: 'No changes detected. No review request was submitted.',
    profileBack: 'Cancel Editing',
    profileExit: 'Exit',
    profileSubmit: 'Submit',
    profileDisplayClose: 'Back',
    profileDisplaySubmit: 'Submit and Save',
    profileAddTitle: 'Add title',
    profileRemoveTitle: 'Remove',
    clearHistory: 'Clear history',
    profileAvatar: 'Avatar',
    profileNameCN: 'Chinese Name',
    profileNameEN: 'English Name',
    profileFirstName: 'First Name',
    profileLastName: 'Last Name',
    profileTitle: 'Title',
    profileTitleCN: 'Chinese Title',
    profileTitleEN: 'English Title',
    profileNationality: 'Country / Region',
    profilePhone: 'Phone',
    profileEmail: 'Email',
    profileOrganization: 'Organization',
    profilePosition: 'Position',
    profileBioCN: 'Chinese Bio',
    profileBioEN: 'English Bio',
    profileAllowContact: 'Allow others to contact you via chat',
    profileAllow: 'Allow',
    profileReject: 'Reject',
    profileAvatarHint: 'Please upload a clear real avatar not exceeding 2 MB',
    profileIntro: 'Profile',
    profileIdentityGuest: 'Guest',
    profileIdentityPartner: 'Industry Partner',
    unnamed: 'Unnamed',
    summitName: 'Tencent SSV Sustainable Social Value Innovation Summit',
    refresh: 'Refresh',
    messageLoading: 'Loading messages...',
    conversationFailed: 'Failed to load conversations',
    messageFailed: 'Failed to load messages',
    messageSendFailed: 'Failed to send message',
    messageSelfContactNotAllowed:
      'Direct messages are currently turned off. Please go to Edit Profile and enable them before sending a message.',
    messageContactNotAllowed:
      'The recipient does not allow direct messages. Sending failed.',
    messagePeerOffline: 'The user can no longer receive messages.',
    messageAccessRevoked: 'Your account no longer has access to chat',
    conversationPeerExited: 'The user has logged out',
    messageAckFailed: 'Failed to sync read status',
    messageInitFailed: 'Failed to initialize chat',
    messageEmpty: 'Please enter a message',
    messageTooLong: 'Messages can be up to 200 characters',
    messageListEmpty: 'No conversations',
    messageThreadEmpty: 'No messages yet. Send one to start chatting.',
    messageSelectEmpty: 'Select a conversation',
    messageDraftTitle: 'New conversation',
    messagePreviewFallback: 'Preview unavailable',
    messageDecryptFallback: 'This message is unavailable',
    messageWeakHint:
      'Some messages may be unavailable because of retention or key changes',
    messageInputPlaceholder: 'Send to',
    messageSend: 'Send',
    unknownContact: 'Unknown contact',
    guestTag: 'Guest',
    partnerTag: 'Partner',
    audienceTag: 'Audience',
    domesticTag: 'Domestic',
    overseasTag: 'Overseas',
  },
};

const getInitial = (name?: string) => {
  const normalized = name?.trim();
  return normalized ? normalized.slice(0, 1).toUpperCase() : '-';
};

const pickLocaleText = (
  locale: ChatLocale,
  zh?: string,
  en?: string,
  fallback = '-'
) => {
  const primary = locale === 'en' ? en : zh;
  const secondary = locale === 'en' ? zh : en;
  return primary || secondary || fallback;
};

const getContactName = (
  locale: ChatLocale,
  item?: ContactItem | ChatAuthInfo
) => pickLocaleText(locale, item?.NameCN, item?.NameEN, TEXT[locale].unnamed);

const getContactTitleLines = (locale: ChatLocale, item?: ContactItem) => {
  return (item?.Titles || [])
    .map(
      (entry) => (locale === 'en' ? entry.TitleEN : entry.TitleCN)?.trim() || ''
    )
    .filter(Boolean);
};

const getContactTitle = (locale: ChatLocale, item?: ContactItem) =>
  getContactTitleLines(locale, item).join(' / ');

const getContactSearchFieldsByLocale = (
  locale: ChatLocale,
  item?: ContactItem
) => {
  if (locale === 'en') {
    return [
      item?.NameEN || '',
      ...(item?.Titles || []).map((entry) => entry.TitleEN || ''),
    ];
  }
  return [
    item?.NameCN || '',
    ...(item?.Titles || []).map((entry) => entry.TitleCN || ''),
  ];
};

const matchContactByLocaleKeyword = (
  locale: ChatLocale,
  item: ContactItem,
  keyword: string
) => {
  const normalizedKeyword = normalizeSearchKeyword(keyword);
  if (!normalizedKeyword) {
    return true;
  }
  return getContactSearchFieldsByLocale(locale, item).some((field) =>
    normalizeSearchKeyword(field).includes(normalizedKeyword)
  );
};

const buildMessagePeerSummaryContact = (
  locale: ChatLocale,
  item?: ConferenceChatConversationItem
): ContactItem | undefined => {
  const participant = normalizeParticipant(item?.PeerParticipant);
  if (!participant) {
    return undefined;
  }
  // 系统通知仍走原会话列表；当后端资料为空时，前端按语言补固定名称、头衔和头像兜底。
  const systemSummary = buildConferenceChatSystemParticipantSummary({
    participant,
    nameCN: item?.PeerNameCN,
    nameEN: item?.PeerNameEN,
    avatar: item?.PeerAvatar,
    locale,
  });
  if (systemSummary) {
    return {
      ContactID: participant.sourceID,
      ContactType: CHAT_CONTACT_TYPE_GUEST,
      GuestDataSource: participant.region === 2 ? 'intl' : 'cn',
      NameCN: systemSummary.nameCN,
      NameEN: systemSummary.nameEN,
      Avatar: systemSummary.avatar,
      Titles: [
        {
          TitleCN: systemSummary.titleCN,
          TitleEN: systemSummary.titleEN,
        },
      ],
    };
  }
  const nameCN = item?.PeerNameCN?.trim() || '';
  const nameEN = item?.PeerNameEN?.trim() || '';
  if (!nameCN && !nameEN) {
    return undefined;
  }
  const contactType =
    participant.type === 2
      ? CHAT_CONTACT_TYPE_PARTNER
      : participant.type === 3
      ? CHAT_CONTACT_TYPE_AUDIENCE
      : CHAT_CONTACT_TYPE_GUEST;
  return {
    ContactID: participant.sourceID,
    ContactType: contactType,
    GuestDataSource:
      contactType === CHAT_CONTACT_TYPE_GUEST && participant.region === 2
        ? 'intl'
        : 'cn',
    NameCN: nameCN,
    NameEN: nameEN,
    Avatar: item?.PeerAvatar?.trim() || '',
  };
};

const isSameContactSummary = (left?: ContactItem, right?: ContactItem) =>
  left?.ContactID === right?.ContactID &&
  normalizeConferenceChatContactType(left?.ContactType) ===
    normalizeConferenceChatContactType(right?.ContactType) &&
  (left?.GuestDataSource || '') === (right?.GuestDataSource || '') &&
  (left?.NameCN || '') === (right?.NameCN || '') &&
  (left?.NameEN || '') === (right?.NameEN || '') &&
  (left?.Avatar || '') === (right?.Avatar || '') &&
  (left?.Organization || '') === (right?.Organization || '') &&
  (left?.Position || '') === (right?.Position || '');

const mergeContactSummary = (
  previous?: ContactItem,
  incoming?: ContactItem
): ContactItem | undefined => {
  if (!incoming) {
    return previous;
  }
  if (!previous) {
    return incoming;
  }
  return {
    ...previous,
    ...incoming,
    ContactID: incoming.ContactID || previous.ContactID,
    ContactType: incoming.ContactType || previous.ContactType,
    GuestDataSource: incoming.GuestDataSource || previous.GuestDataSource,
    NameCN: incoming.NameCN || previous.NameCN,
    NameEN: incoming.NameEN || previous.NameEN,
    Avatar: incoming.Avatar || previous.Avatar,
    Organization: incoming.Organization || previous.Organization,
    Position: incoming.Position || previous.Position,
    Titles:
      incoming.Titles && incoming.Titles.length
        ? incoming.Titles
        : previous.Titles,
    BioCN: incoming.BioCN || previous.BioCN,
    BioEN: incoming.BioEN || previous.BioEN,
    Agenda:
      incoming.Agenda && incoming.Agenda.length
        ? incoming.Agenda
        : previous.Agenda,
    HasPendingProfileUpdate:
      incoming.HasPendingProfileUpdate ?? previous.HasPendingProfileUpdate,
    ProfileUpdateReviewStatus:
      incoming.ProfileUpdateReviewStatus ?? previous.ProfileUpdateReviewStatus,
    ProfileUpdateReviewID:
      incoming.ProfileUpdateReviewID || previous.ProfileUpdateReviewID,
  };
};

// 详情接口可能比已加载列表拿到更新的头像和资料；合并到列表，避免左右两侧展示不一致。
const mergeContactListItem = (item: ContactItem, incoming: ContactItem) => {
  if (!isSameConferenceChatContact(item, incoming)) {
    return item;
  }
  return (mergeContactSummary(item, incoming) || item) as ContactItem;
};

const getContactTagLabel = (locale: ChatLocale, item?: ContactItem) => {
  switch (normalizeConferenceChatContactType(item?.ContactType)) {
    case CHAT_CONTACT_TYPE_PARTNER:
      return TEXT[locale].partner;
    case CHAT_CONTACT_TYPE_AUDIENCE:
      return TEXT[locale].audienceTag;
    default:
      return TEXT[locale].guest;
  }
};

const CONTACT_PINYIN_COLLATOR =
  typeof Intl !== 'undefined'
    ? new Intl.Collator('zh-Hans-CN-u-co-pinyin', { sensitivity: 'base' })
    : undefined;
const CONTACT_PINYIN_INITIAL_BOUNDARIES = [
  ['Z', '匝'],
  ['Y', '丫'],
  ['X', '夕'],
  ['W', '哇'],
  ['T', '他'],
  ['S', '仨'],
  ['R', '然'],
  ['Q', '七'],
  ['P', '啪'],
  ['O', '噢'],
  ['N', '拿'],
  ['M', '妈'],
  ['L', '垃'],
  ['K', '喀'],
  ['J', '机'],
  ['H', '哈'],
  ['G', '旮'],
  ['F', '发'],
  ['E', '讹'],
  ['D', '哒'],
  ['C', '嚓'],
  ['B', '八'],
  ['A', '阿'],
] as const;

const getContactSortName = (locale: ChatLocale, item?: ContactItem) => {
  if (locale === 'zh') {
    return item?.NameCN?.trim() || getContactName(locale, item).trim();
  }
  const englishName = item?.NameEN?.trim() || '';
  const localizedName = getContactName(locale, item).trim();
  return englishName || localizedName;
};

const compareContactByLocale = (
  locale: ChatLocale,
  left?: ContactItem,
  right?: ContactItem
) =>
  getContactSortName(locale, left).localeCompare(
    getContactSortName(locale, right),
    locale === 'zh' ? 'zh-Hans-CN-u-co-pinyin' : 'en',
    { sensitivity: 'base' }
  );

const getContactIndexInitial = (value: string) => {
  const initial = value.trim().match(/[^\s]/)?.[0] || '';
  const upperInitial = initial.toUpperCase();
  if (/^[A-Z]$/.test(upperInitial)) {
    return upperInitial;
  }
  if (!CONTACT_PINYIN_COLLATOR || !/[\u4e00-\u9fff]/.test(initial)) {
    return '#';
  }
  const matchedBoundary = CONTACT_PINYIN_INITIAL_BOUNDARIES.find(
    ([, boundary]) => CONTACT_PINYIN_COLLATOR.compare(initial, boundary) >= 0
  );
  return matchedBoundary?.[0] || '#';
};

const getContactIndexLetter = (locale: ChatLocale, item?: ContactItem) => {
  const sortName = getContactSortName(locale, item);
  return getContactIndexInitial(sortName);
};

const normalizeSearchKeyword = (value?: string) =>
  value?.replace(/\s+/g, ' ').trim().toLowerCase() || '';

const safeReadMobileSearchHistory = () => {
  if (typeof window === 'undefined') {
    return [] as string[];
  }
  try {
    const raw = window.localStorage.getItem(
      MOBILE_CONTACT_SEARCH_HISTORY_STORAGE_KEY
    );
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean)
      .slice(0, MOBILE_CONTACT_SEARCH_HISTORY_LIMIT);
  } catch (error) {
    console.error('[ConferenceChatPage read search history error]', error);
    return [];
  }
};

const safeWriteMobileSearchHistory = (history: string[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      MOBILE_CONTACT_SEARCH_HISTORY_STORAGE_KEY,
      JSON.stringify(history.slice(0, MOBILE_CONTACT_SEARCH_HISTORY_LIMIT))
    );
  } catch (error) {
    console.error('[ConferenceChatPage write search history error]', error);
  }
};

const safeReadEnPrivacyAlertDismissed = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return (
      window.localStorage.getItem(EN_PRIVACY_ALERT_DISMISSED_STORAGE_KEY) ===
      '1'
    );
  } catch (error) {
    console.error('[ConferenceChatPage read privacy alert error]', error);
    return false;
  }
};

const safeWriteEnPrivacyAlertDismissed = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(EN_PRIVACY_ALERT_DISMISSED_STORAGE_KEY, '1');
  } catch (error) {
    console.error('[ConferenceChatPage write privacy alert error]', error);
  }
};

const getAgendaTitle = (locale: ChatLocale, item?: AgendaItem) => {
  if (!item) return '-';
  const forumTitle = pickLocaleText(
    locale,
    item.ForumTitleCN || item.forumTitleCn,
    item.ForumTitleEN || item.forumTitleEn,
    ''
  );
  const title = pickLocaleText(
    locale,
    item.TitleCN || item.titleCn,
    item.TitleEN || item.titleEn,
    ''
  );
  return title || forumTitle || '-';
};

const getAgendaForumTitle = (locale: ChatLocale, item?: AgendaItem) =>
  pickLocaleText(
    locale,
    item?.ForumTitleCN || item?.forumTitleCn,
    item?.ForumTitleEN || item?.forumTitleEn,
    ''
  );

const getAgendaSessionTitle = (locale: ChatLocale, item?: AgendaItem) =>
  pickLocaleText(
    locale,
    item?.SessionTitleCN || item?.sessionTitleCn,
    item?.SessionTitleEN || item?.sessionTitleEn,
    ''
  );

const getAgendaAddress = (locale: ChatLocale, item?: AgendaItem) =>
  pickLocaleText(
    locale,
    item?.AddressCN || item?.addressCn || item?.LocationCN || item?.locationCn,
    item?.AddressEN || item?.addressEn || item?.LocationEN || item?.locationEn,
    ''
  );

const getAgendaRole = (item?: AgendaItem) =>
  `${item?.Role || item?.role || ''}`.trim().toLowerCase();

const sortAgendaItemsByStartTime = (items: AgendaItem[]) =>
  [...items].sort((left, right) => {
    const leftStart = normalizeTimeValue(left.StartTime || left.startTime) || 0;
    const rightStart =
      normalizeTimeValue(right.StartTime || right.startTime) || 0;
    if (leftStart !== rightStart) {
      return leftStart - rightStart;
    }
    const leftEnd = normalizeTimeValue(left.EndTime || left.endTime) || 0;
    const rightEnd = normalizeTimeValue(right.EndTime || right.endTime) || 0;
    if (leftEnd !== rightEnd) {
      return leftEnd - rightEnd;
    }
    const leftID =
      left.AgendaID ||
      left.AgendaItemID ||
      left.agendaId ||
      left.agendaItemId ||
      '';
    const rightID =
      right.AgendaID ||
      right.AgendaItemID ||
      right.agendaId ||
      right.agendaItemId ||
      '';
    return String(leftID).localeCompare(String(rightID));
  });

const isGuestAgendaItem = (item?: AgendaItem) => {
  const role = getAgendaRole(item);
  return Boolean(
    role &&
      /(guest|speaker|嘉宾|讲者|演讲|主持|host|moderator|panelist)/.test(role)
  );
};

const isSignupAgendaItem = (item?: AgendaItem) => {
  const role = getAgendaRole(item);
  return Boolean(
    role &&
      /(signup|register|registered|报名|参会|观众|audience|attendee|participant)/.test(
        role
      )
  );
};

// 联系人详情页继续保留原有“嘉宾议程/报名议程”分组，避免这次只针对主议程菜单的需求影响详情体验。
const buildAgendaSections = (
  items: AgendaItem[],
  text: (typeof TEXT)[ChatLocale],
  identity?: string
) => {
  const guestAgendaItems: AgendaItem[] = [];
  const signupAgendaItems: AgendaItem[] = [];
  const normalizedIdentity = (identity || '').trim().toLowerCase();

  items.forEach((item) => {
    if (isGuestAgendaItem(item)) {
      guestAgendaItems.push(item);
      return;
    }
    if (isSignupAgendaItem(item)) {
      signupAgendaItems.push(item);
      return;
    }
    if (normalizedIdentity === CHAT_CONTACT_TYPE_GUEST) {
      guestAgendaItems.push(item);
      return;
    }
    if (normalizedIdentity === CHAT_CONTACT_TYPE_AUDIENCE) {
      signupAgendaItems.push(item);
      return;
    }
    signupAgendaItems.push(item);
  });

  return [
    {
      key: 'guest',
      title: text.guestAgenda,
      items: guestAgendaItems,
    },
    {
      key: 'signup',
      title: text.signupAgenda,
      items: signupAgendaItems,
    },
  ].filter((section) => section.items.length);
};

const buildGuestAgendaSections = (
  guestAgenda: AgendaItem[],
  text: (typeof TEXT)[ChatLocale]
): AgendaSection[] => [
  {
    key: 'guest',
    title: text.guestAgenda,
    items: sortAgendaItemsByStartTime(guestAgenda),
  },
];

const getMobileContactDetailBg = (item?: { ContactType?: string }) =>
  normalizeConferenceChatContactType(item?.ContactType) ===
  CHAT_CONTACT_TYPE_PARTNER
    ? MOBILE_CONTACT_DETAIL_PARTNER_BG_SRC
    : MOBILE_CONTACT_DETAIL_GUEST_BG_SRC;

const getMobileMainBackgroundSrc = (mainTab: MainTab, detail?: ContactItem) => {
  if (mainTab === 'messages') {
    return MOBILE_MESSAGES_BG_SRC;
  }
  if (mainTab === 'agenda') {
    return MOBILE_AGENDA_BG_SRC;
  }
  if (detail) {
    return getMobileContactDetailBg(detail);
  }
  return '';
};

const normalizeTimeValue = (value?: number | string) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') {
    return value > 100000000000 ? value : value * 1000;
  }
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) {
    const timestamp = Number(trimmed);
    return timestamp > 100000000000 ? timestamp : timestamp * 1000;
  }
  const parsed = Date.parse(trimmed.replace(/-/g, '/'));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const formatTime = (start?: number | string, end?: number | string) => {
  const startMs = normalizeTimeValue(start);
  const endMs = normalizeTimeValue(end);
  if (!startMs && !endMs) return '-';

  const format = (timestamp: number, withDate: boolean) => {
    const date = new Date(timestamp);
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`;
    return withDate
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')} ${time}`
      : time;
  };

  if (startMs && endMs) {
    const sameDay =
      new Date(startMs).toDateString() === new Date(endMs).toDateString();
    return `${format(startMs, true)}-${format(endMs, !sameDay)}`;
  }
  return format((startMs || endMs) as number, true);
};

const ENGLISH_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getEnglishOrdinalDay = (day: number) => {
  const remainder100 = day % 100;
  if (remainder100 >= 11 && remainder100 <= 13) {
    return `${day}th`;
  }

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const formatDetailAgendaTime = (
  locale: ChatLocale,
  start?: number | string,
  end?: number | string
) => {
  const startMs = normalizeTimeValue(start);
  const endMs = normalizeTimeValue(end);
  if (!startMs) return '-';

  const startDate = new Date(startMs);
  const startTimeText = `${String(startDate.getHours()).padStart(
    2,
    '0'
  )}:${String(startDate.getMinutes()).padStart(2, '0')}`;

  if (locale === 'en') {
    const dateText = `${getEnglishOrdinalDay(startDate.getDate())} ${
      ENGLISH_MONTH_NAMES[startDate.getMonth()]
    } ${startDate.getFullYear()}`;

    if (!endMs) {
      return `${dateText} ${startTimeText}`;
    }

    const endDate = new Date(endMs);
    const endTimeText = `${String(endDate.getHours()).padStart(
      2,
      '0'
    )}:${String(endDate.getMinutes()).padStart(2, '0')}`;

    return `${dateText} ${startTimeText} to ${endTimeText}`;
  }

  const dateText = `${startDate.getFullYear()}年${String(
    startDate.getMonth() + 1
  ).padStart(2, '0')}月${String(startDate.getDate()).padStart(2, '0')}日`;

  if (!endMs) {
    return `${dateText} ${startTimeText}`;
  }

  const endDate = new Date(endMs);
  const endTimeText = `${String(endDate.getHours()).padStart(2, '0')}:${String(
    endDate.getMinutes()
  ).padStart(2, '0')}`;

  return `${dateText} ${startTimeText}至${endTimeText}`;
};

const normalizeGuestDataSource = (value?: string) => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (
    normalized === 'intl' ||
    normalized === 'oversea' ||
    normalized === 'overseas'
  ) {
    return 'intl';
  }
  if (normalized === 'cn') {
    return 'cn';
  }
  return undefined;
};

const getGuestRegionByDataSource = (guestDataSource?: string) =>
  normalizeGuestDataSource(guestDataSource) === 'intl' ? 2 : 1;

const getProfileUpdateReviewStatus = (
  item?: Pick<
    ContactItem,
    'HasPendingProfileUpdate' | 'ProfileUpdateReviewStatus'
  > | null
) =>
  item?.HasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEW_STATUS_PENDING
    : item?.ProfileUpdateReviewStatus;

const getProfileDTOUpdateReviewStatus = (
  profile?: Pick<
    ConferenceChatProfileDTO,
    'hasPendingProfileUpdate' | 'profileUpdateReviewStatus'
  > | null
) =>
  profile?.hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEW_STATUS_PENDING
    : profile?.profileUpdateReviewStatus;

const isProfileUpdatePending = (item?: ContactItem) =>
  getProfileUpdateReviewStatus(item) === PROFILE_UPDATE_REVIEW_STATUS_PENDING;

const isProfileUpdateApproved = (item?: ContactItem | null) =>
  getProfileUpdateReviewStatus(item) === PROFILE_UPDATE_REVIEW_STATUS_APPROVED;

const isProfileUpdateBlocked = (item?: ContactItem | null) =>
  isProfileUpdatePending(item || undefined) || isProfileUpdateApproved(item);

const isProfileDTOUpdatePending = (profile?: ConferenceChatProfileDTO | null) =>
  getProfileDTOUpdateReviewStatus(profile) ===
  PROFILE_UPDATE_REVIEW_STATUS_PENDING;

const isProfileDTOUpdateApproved = (
  profile?: ConferenceChatProfileDTO | null
) =>
  getProfileDTOUpdateReviewStatus(profile) ===
  PROFILE_UPDATE_REVIEW_STATUS_APPROVED;

const isProfileDTOUpdateBlocked = (profile?: ConferenceChatProfileDTO | null) =>
  isProfileDTOUpdatePending(profile) || isProfileDTOUpdateApproved(profile);

const isSameConferenceChatContact = (left?: ContactItem, right?: ContactItem) =>
  Boolean(left?.ContactID && right?.ContactID) &&
  left?.ContactID === right?.ContactID &&
  normalizeConferenceChatContactType(left?.ContactType) ===
    normalizeConferenceChatContactType(right?.ContactType) &&
  (normalizeConferenceChatContactType(left?.ContactType) !==
    CHAT_CONTACT_TYPE_GUEST ||
    normalizeGuestDataSource(left?.GuestDataSource) ===
      normalizeGuestDataSource(right?.GuestDataSource));

const normalizeConferenceChatContactType = (value?: string): ContactType =>
  value === CHAT_CONTACT_TYPE_PARTNER || value === CHAT_CONTACT_TYPE_AUDIENCE
    ? value
    : CHAT_CONTACT_TYPE_GUEST;

const isConferenceChatPeerOffline = (
  peerDisplayStatus?: string,
  disableReason?: string
) =>
  peerDisplayStatus === CONFERENCE_CHAT_PEER_DISPLAY_STATUS_OFFLINE ||
  disableReason === CONFERENCE_CHAT_DISABLE_REASON_PEER_OFFLINE;

const getConversationPeerAvatar = (conversation?: ChatConversationView) =>
  conversation &&
  isConferenceChatPeerOffline(
    conversation?.peerDisplayStatus,
    conversation?.disableReason
  )
    ? CONFERENCE_CHAT_DISABLED_AVATAR_SRC
    : conversation?.peerSummary.avatar;

const isExplicitConferenceChatGuest = (value?: string) =>
  (value || '').trim().toLowerCase() === CHAT_CONTACT_TYPE_GUEST;

const getParticipantTypeByContactType = (contactType?: string) => {
  switch ((contactType || '').trim().toLowerCase()) {
    case 'partner':
      return 2;
    case 'audience':
      return 3;
    default:
      return 1;
  }
};

const normalizeParticipant = (
  participant?: ChatParticipantDTO
): ChatParticipantRef | undefined => {
  const sourceID = participant?.SourceID?.trim();
  if (!sourceID) {
    return undefined;
  }
  const region = participant?.Region === 2 ? 2 : 1;
  const type = normalizeConferenceChatSystemParticipantType(participant?.Type);
  return {
    region,
    type,
    sourceID,
  };
};

const buildParticipantKey = (participant?: ChatParticipantRef) =>
  participant
    ? `${participant.region}:${participant.type}:${participant.sourceID}`
    : '';

const sameParticipant = (
  left?: ChatParticipantRef,
  right?: ChatParticipantRef
) =>
  !!left &&
  !!right &&
  left.region === right.region &&
  left.type === right.type &&
  left.sourceID === right.sourceID;

const inferParticipantFromContact = (
  item?: ContactItem
): ChatParticipantRef | undefined => {
  const sourceID = item?.ContactID?.trim();
  if (!sourceID) {
    return undefined;
  }
  return {
    region:
      item?.ContactType === 'guest'
        ? getGuestRegionByDataSource(item.GuestDataSource)
        : 1,
    type: getParticipantTypeByContactType(item?.ContactType),
    sourceID,
  };
};

const buildConversationKey = (
  conversation?: ConferenceChatConversationItem,
  peer?: ChatParticipantRef
) =>
  conversation?.ConversationHash?.trim() ||
  conversation?.ConversationID?.trim() ||
  buildParticipantKey(peer);

const buildDraftConversationKey = (participant?: ChatParticipantRef) =>
  `draft:${buildParticipantKey(participant)}`;

const getParticipantTypeLabel = (locale: ChatLocale, type?: number) => {
  if (type === 2) return TEXT[locale].partnerTag;
  if (type === 3) return TEXT[locale].audienceTag;
  return TEXT[locale].guestTag;
};

const getParticipantRegionLabel = (locale: ChatLocale, region?: number) =>
  region === 2 ? TEXT[locale].overseasTag : TEXT[locale].domesticTag;

const getSelfIdentityLabel = (locale: ChatLocale, contactType?: string) => {
  switch ((contactType || '').trim().toLowerCase()) {
    case 'partner':
      return locale === 'zh' ? '行业伙伴' : 'Industry Partners';
    case 'audience':
      return locale === 'zh' ? '大会观众' : 'Summit Attendees';
    default:
      return locale === 'zh' ? '大会嘉宾' : 'ConferenceGuest';
  }
};

const getMobileSelfIdentityIcon = (contactType?: string) => {
  switch (normalizeConferenceChatContactType(contactType)) {
    case CHAT_CONTACT_TYPE_PARTNER:
      return <SelfPartnerIdentityIcon />;
    case CHAT_CONTACT_TYPE_AUDIENCE:
      return <SelfAudienceIdentityIcon />;
    default:
      return <SelfGuestIdentityIcon />;
  }
};

// 会聊头像的蓝边和右下角身份角标仅服务于嘉宾，其它身份保持普通圆形头像。
const shouldDecorateConferenceChatAvatarByContactType = (
  contactType?: string
) => {
  const normalizedType = (contactType || '').trim().toLowerCase();
  return normalizedType === CHAT_CONTACT_TYPE_GUEST;
};

const shouldDecorateConferenceChatAvatarByParticipantType = (type?: number) =>
  type === 1;

const canEditConferenceChatProfile = (contactType?: string) => {
  const normalizedType = normalizeConferenceChatContactType(contactType);
  return (
    normalizedType === CHAT_CONTACT_TYPE_GUEST ||
    normalizedType === CHAT_CONTACT_TYPE_PARTNER
  );
};

const getMobileProfileMeetingMeta = (
  locale: ChatLocale,
  contactType?: string
): MobileProfileMeetingMeta => {
  const normalizedType = (contactType || '').trim().toLowerCase();
  if (normalizedType === CHAT_CONTACT_TYPE_PARTNER) {
    return {
      heroTitle: PARTNER_HERO_TITLE,
      heroSubtitle: PARTNER_HERO_SUBTITLE,
      meetingTitle: PARTNER_MEETING_TITLE,
      meetingDescription: PARTNER_MEETING_DESCRIPTION,
      meetingDateText: PARTNER_MEETING_DATE_TEXT,
      meetingLocationText: PARTNER_MEETING_LOCATION_TEXT,
    };
  }
  if (locale === 'en') {
    return {
      heroTitle:
        'Join us at the Sustainable Social Value Innovation Summit 2026',
      heroSubtitle: 'AI for Good · Social Innovation',
      meetingTitle:
        'TENCENT SSV SUSTAINABLE SOCIAL VALUE INNOVATION SUMMIT 2026',
      meetingDescription: GUEST_MEETING_DESCRIPTION_EN,
      meetingDateText: GUEST_MEETING_DATE_TEXT_EN,
      meetingLocationText: 'Zhongguancun International Innovation Center,',
      meetingLocationSubline: 'Haidian District, Beijing, China',
      editButtonText: 'Edit Information',
    };
  }
  return {
    heroTitle: GUEST_HERO_TITLE,
    heroSubtitle: GUEST_HERO_SUBTITLE,
    meetingTitle: GUEST_MEETING_TITLE,
    meetingDescription: GUEST_MEETING_DESCRIPTION,
    meetingDateText: GUEST_MEETING_DATE_TEXT,
    meetingLocationText: GUEST_MEETING_LOCATION_TEXT,
  };
};

const parseChatDate = (value?: string) => {
  if (!value) return undefined;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  if (!Number.isNaN(date.getTime())) {
    return date;
  }
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? undefined : fallback;
};

const isSameCalendarDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatHourMinute = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;

const formatChatTime = (value?: string) => {
  const date = parseChatDate(value);
  if (!date) {
    return '-';
  }
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;
};

const formatConversationCardTime = (locale: ChatLocale, value?: string) => {
  const date = parseChatDate(value);
  if (!date) {
    return '-';
  }
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (isSameCalendarDay(date, now) && diffMs >= 0 && diffMinutes < 60) {
    const minutes = Math.max(diffMinutes, 1);
    return locale === 'zh' ? `${minutes}分钟前` : `${minutes} min ago`;
  }
  const time = formatHourMinute(date);
  if (isSameCalendarDay(date, now)) {
    const hours = date.getHours();
    const minutes = time.split(':')[1];
    if (locale === 'zh') {
      return `${hours >= 12 ? '下午' : '上午'} ${hours % 12 || 12}:${minutes}`;
    }
    return `${hours >= 12 ? 'PM' : 'AM'} ${hours % 12 || 12}:${minutes}`;
  }
  if (isSameCalendarDay(date, yesterday)) {
    return locale === 'zh' ? `昨天 ${time}` : `Yesterday ${time}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
};

const CHAT_MESSAGE_DIVIDER_INTERVAL_MS = 15 * 60 * 1000;

const shouldShowMessageDivider = (
  previousValue?: string,
  currentValue?: string
) => {
  const currentDate = parseChatDate(currentValue);
  if (!currentDate) {
    return true;
  }
  const previousDate = parseChatDate(previousValue);
  if (!previousDate) {
    return true;
  }
  if (!isSameCalendarDay(previousDate, currentDate)) {
    return true;
  }
  return (
    currentDate.getTime() - previousDate.getTime() >=
    CHAT_MESSAGE_DIVIDER_INTERVAL_MS
  );
};

const buildMessageDividerFlags = (list: ChatMessageView[]) => {
  let lastDividerTime: string | undefined;
  return list.map((item) => {
    const needDivider = shouldShowMessageDivider(
      lastDividerTime,
      item.sendTime
    );
    if (needDivider) {
      lastDividerTime = item.sendTime;
    }
    return needDivider;
  });
};

const formatMessageDividerTime = (locale: ChatLocale, value?: string) => {
  const date = parseChatDate(value);
  if (!date) {
    return '';
  }
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const time = formatHourMinute(date);
  if (isSameCalendarDay(date, now)) {
    return time;
  }
  if (isSameCalendarDay(date, yesterday)) {
    return locale === 'zh' ? `昨天 ${time}` : `Yesterday ${time}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')} ${time}`;
};

const truncateMessage = (value: string, max = 40) =>
  value.length > max ? `${value.slice(0, max)}...` : value;

const sortChatMessages = (list: ChatMessageView[]) =>
  [...list].sort((left, right) => {
    if (left.sendTime === right.sendTime) {
      return left.messageID.localeCompare(right.messageID);
    }
    return left.sendTime.localeCompare(right.sendTime);
  });

const getChatMessageIdentityKeys = (item: {
  messageID: string;
  clientMsgID?: string;
}) => {
  const keys = [item.messageID.trim()];
  const clientMsgID = item.clientMsgID?.trim();
  if (clientMsgID && clientMsgID !== item.messageID.trim()) {
    keys.unshift(clientMsgID);
  }
  return keys;
};

const mergeChatMessages = (
  previous: ChatMessageView[],
  next: ChatMessageView[]
) => {
  const merged = new Map<string, ChatMessageView>();
  const previousIdentityToKey = new Map<string, string>();
  previous.forEach((item) => {
    merged.set(item.key, item);
    getChatMessageIdentityKeys(item).forEach((identityKey) => {
      previousIdentityToKey.set(identityKey, item.key);
    });
  });
  next.forEach((item) => {
    const matchedKey = getChatMessageIdentityKeys(item)
      .map((identityKey) => previousIdentityToKey.get(identityKey))
      .find((value): value is string => Boolean(value));
    const nextItem = matchedKey ? { ...item, key: matchedKey } : item;
    merged.set(nextItem.key, nextItem);
    getChatMessageIdentityKeys(nextItem).forEach((identityKey) => {
      previousIdentityToKey.set(identityKey, nextItem.key);
    });
  });
  return sortChatMessages([...merged.values()]);
};

const getThreadIdentity = (conversation?: ChatConversationView) =>
  conversation
    ? `${buildParticipantKey(conversation.peer)}::${
        conversation.isDraft
          ? 'draft'
          : conversation.conversationID ||
            conversation.conversationHash ||
            'unknown'
      }`
    : '';

const normalizeProfileTitles = (titles?: ProfileTitleItem[]) => {
  const list = (titles || [])
    .map((item) => ({
      titleCn: item.titleCn || item.TitleCN || '',
      titleEn: item.titleEn || item.TitleEN || '',
    }))
    .filter((item) => item.titleCn.trim() || item.titleEn.trim());
  return list;
};

const getProfileTitleByLocale = (
  locale: ChatLocale,
  title?: ProfileTitleItem
) => {
  if (!title) {
    return '';
  }
  return locale === 'en'
    ? title.titleEn || title.TitleEN || ''
    : title.titleCn || title.TitleCN || '';
};

const getProfileTitleLines = (
  locale: ChatLocale,
  profile?: ConferenceChatProfileDTO
) => {
  return (profile?.titles || [])
    .map((item) => getProfileTitleByLocale(locale, item).trim())
    .filter(Boolean);
};

const createEmptyProfileTitle = () => ({
  titleCn: '',
  titleEn: '',
});

const ensureProfileTitleCount = (
  titles: Array<{ titleCn: string; titleEn: string }>,
  minimumCount: number
) => {
  const normalized = [...titles];
  while (normalized.length < minimumCount) {
    normalized.push(createEmptyProfileTitle());
  }
  return normalized;
};

const normalizeFileName = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === 0) return fileName;
  return fileName.slice(0, dotIndex) + fileName.slice(dotIndex).toLowerCase();
};

const getUploadResponse = (file?: UploadFile) => {
  if (!file?.response || typeof file.response !== 'object') {
    return undefined;
  }
  return file.response as UploadResponseRecord;
};

const buildProfileAvatarUploadFiles = (avatarUrl?: string): UploadFile[] => {
  if (!avatarUrl) {
    return [];
  }
  return [
    {
      url: avatarUrl,
      name: avatarUrl.split('/').pop() || 'avatar.png',
      status: 'success',
      response: {
        url: avatarUrl,
        value: avatarUrl,
      },
    } as UploadFile,
  ];
};

const getProfileAvatarUrlFromUpload = (files: UploadFile[]) => {
  const [firstFile] = files;
  if (!firstFile) {
    return '';
  }
  const response = getUploadResponse(firstFile);
  return String(response?.url ?? firstFile.url ?? response?.value ?? '');
};

const getProfileAvatarSubmitPathFromUpload = (files: UploadFile[]) => {
  const [firstFile] = files;
  if (!firstFile) {
    return '';
  }
  const response = getUploadResponse(firstFile);
  return String(response?.path ?? response?.value ?? firstFile.url ?? '');
};

const isValidAvatarFileType = (file: UploadFile) => {
  const fileType = String(file.type ?? '').toLowerCase();
  if (fileType && AVATAR_TYPE_LIST.includes(fileType)) {
    return true;
  }
  const ext = String(file.name ?? '')
    .split('.')
    .pop()
    ?.toLowerCase();
  return Boolean(ext && AVATAR_EXT_LIST.includes(ext));
};

const uploadDomesticGuestAvatar = async (file: UploadFile) => {
  if (!file.raw) {
    throw new Error('avatar file is required');
  }
  const formData = new FormData();
  formData.append('file', file.raw, String(file.name ?? 'avatar.png'));

  // 国内嘉宾会聊头像统一交给后端写入国内 COS 资源目录。
  const response = await fetch(GUEST_AVATAR_UPLOAD_API, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('upload failed');
  }

  const payload = (await response.json()) as {
    code?: number;
    msg?: string;
    data?: {
      path?: string;
      url?: string;
    };
  };
  if (payload.code !== 0 || !payload.data?.path) {
    throw new Error(payload.msg || 'upload failed');
  }
  return payload.data;
};

const uploadDomesticIndustryPartnerAvatar = async (file: UploadFile) => {
  if (!file.raw) {
    throw new Error('avatar file is required');
  }
  const formData = new FormData();
  formData.append('file', file.raw, String(file.name ?? 'avatar.png'));

  const response = await fetch(INDUSTRY_PARTNER_AVATAR_UPLOAD_API, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('upload failed');
  }

  const payload = (await response.json()) as {
    code?: number;
    msg?: string;
    data?: {
      path?: string;
      url?: string;
    };
  };
  if (payload.code !== 0 || !payload.data?.path) {
    throw new Error(payload.msg || 'upload failed');
  }
  return payload.data;
};

const uploadDomesticAvatar = async (file: UploadFile, contactType?: string) => {
  if (
    normalizeConferenceChatContactType(contactType) ===
    CHAT_CONTACT_TYPE_PARTNER
  ) {
    return uploadDomesticIndustryPartnerAvatar(file);
  }
  return uploadDomesticGuestAvatar(file);
};

const buildProfileForm = (
  profile?: ConferenceChatProfileDTO,
  locale: ChatLocale = 'zh'
): ConferenceChatProfileForm => ({
  registerId: profile?.registerId || '',
  partnerRegisterId: profile?.partnerRegisterId || '',
  avatarPreviewUrl: profile?.avatar || profile?.avatarPath || '',
  avatarSubmitPath: profile?.avatarPath || profile?.avatar || '',
  firstName: profile?.firstName || '',
  lastName: profile?.lastName || '',
  nameCn: profile?.nameCn || '',
  nameEn: profile?.nameEn || '',
  titles: ensureProfileTitleCount(
    normalizeProfileTitles(profile?.titles),
    locale === 'en' ? EN_PROFILE_BASE_TITLE_COUNT : 1
  ),
  nationality: profile?.nationality || '',
  phone: profile?.phone || '',
  email: profile?.email || '',
  organization: profile?.organization || '',
  position: profile?.position || '',
  bioCn: profile?.bioCn || '',
  bioEn: profile?.bioEn || '',
  allowContact: profile?.allowContact === 0 ? 0 : 1,
});

const buildProfileUpdatePayload = (
  form: ConferenceChatProfileForm,
  locale: ChatLocale
): ConferenceChatProfileUpdatePayload => ({
  registerId: form.registerId?.trim() || '',
  partnerRegisterId: form.partnerRegisterId?.trim() || '',
  avatar: form.avatarSubmitPath.trim(),
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  nameCn: form.nameCn.trim(),
  nameEn: form.nameEn.trim(),
  titles: form.titles
    .map((item) => ({
      titleCn: item.titleCn.trim(),
      titleEn: item.titleEn.trim(),
    }))
    .filter((item, index) => index === 0 || item.titleCn || item.titleEn),
  nationality: form.nationality.trim(),
  phone: form.phone.trim(),
  email: form.email.trim(),
  organization: form.organization.trim(),
  position: form.position.trim(),
  bioCn: form.bioCn.trim(),
  bioEn: form.bioEn.trim(),
  allowContact: form.allowContact === 0 ? 0 : 1,
});

const isProfileUpdatePayloadChanged = (
  current: ConferenceChatProfileForm,
  initial: ConferenceChatProfileForm,
  locale: ChatLocale
) => {
  // 会聊内编辑同样比较最终提交体，避免空头衔行、首尾空格等造成无效审核单。
  const currentPayload = buildProfileUpdatePayload(current, locale);
  const initialPayload = buildProfileUpdatePayload(initial, locale);

  return JSON.stringify(currentPayload) !== JSON.stringify(initialPayload);
};

const isValidProfileEmail = (value: string) =>
  !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getProfileFieldRequiredMessage = (
  fieldNameCn: string,
  fieldNameEn: string,
  locale: ChatLocale
) => (locale === 'en' ? `${fieldNameEn} is required.` : `${fieldNameCn}必填`);

const getProfileFieldLengthLimitMessage = (
  fieldNameCn: string,
  fieldNameEn: string,
  limit: number,
  locale: ChatLocale
) =>
  locale === 'en'
    ? `${fieldNameEn}: Please enter no more than ${limit} characters`
    : `${fieldNameCn}不能超过${limit}个字符`;

// 与后端 utf8.RuneCountInString 保持一致，避免非 ASCII 字符被按字节或 UTF-16 码元误判超长。
const getUnicodeCharacterCount = (value = '') => Array.from(value).length;

const validateConferenceChatProfileForm = (
  form: ConferenceChatProfileForm,
  locale: ChatLocale,
  isOverseaGuest: boolean
) => {
  const trimmedForm = {
    ...form,
    avatarSubmitPath: form.avatarSubmitPath.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    nameCn: form.nameCn.trim(),
    nameEn: form.nameEn.trim(),
    nationality: form.nationality.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    bioCn: form.bioCn.trim(),
    bioEn: form.bioEn.trim(),
  };
  const titles = form.titles.map((item) => ({
    titleCn: item.titleCn.trim(),
    titleEn: item.titleEn.trim(),
  }));

  if (!trimmedForm.avatarSubmitPath) {
    return locale === 'en'
      ? 'Please upload a clear, recent headshot. Max file size: 2MB.'
      : '请上传您的高清真实头像，大小不超过 2M';
  }

  if (isOverseaGuest) {
    if (!trimmedForm.firstName) {
      return locale === 'en' ? 'Please enter First Name' : '请输入 First Name';
    }
    if (getUnicodeCharacterCount(trimmedForm.firstName) > 50) {
      return getProfileFieldLengthLimitMessage(
        'First Name',
        'First Name',
        50,
        locale
      );
    }
    if (!trimmedForm.lastName) {
      return locale === 'en' ? 'Please enter Last Name' : '请输入 Last Name';
    }
    if (getUnicodeCharacterCount(trimmedForm.lastName) > 50) {
      return getProfileFieldLengthLimitMessage(
        'Last Name',
        'Last Name',
        50,
        locale
      );
    }
    if (getUnicodeCharacterCount(trimmedForm.nameCn) > 20) {
      return getProfileFieldLengthLimitMessage(
        '中文姓名',
        'Chinese Name',
        20,
        locale
      );
    }
    if (!titles[0]?.titleEn) {
      return getProfileFieldRequiredMessage(
        '英文头衔',
        'English Title',
        locale
      );
    }
    if (getUnicodeCharacterCount(titles[0].titleEn) > 100) {
      return getProfileFieldLengthLimitMessage(
        '英文头衔',
        'English Title',
        100,
        locale
      );
    }
    if (!titles[0]?.titleCn) {
      return getProfileFieldRequiredMessage(
        '中文头衔',
        'Chinese Title',
        locale
      );
    }
    if (getUnicodeCharacterCount(titles[0].titleCn) > 50) {
      return getProfileFieldLengthLimitMessage(
        '中文头衔',
        'Chinese Title',
        50,
        locale
      );
    }
    for (const item of titles.slice(1)) {
      if (getUnicodeCharacterCount(item.titleEn) > 100) {
        return getProfileFieldLengthLimitMessage(
          '英文头衔',
          'English Title',
          100,
          locale
        );
      }
      if (getUnicodeCharacterCount(item.titleCn) > 50) {
        return getProfileFieldLengthLimitMessage(
          '中文头衔',
          'Chinese Title',
          50,
          locale
        );
      }
    }
    if (!trimmedForm.email) {
      return getProfileFieldRequiredMessage('邮箱', 'Email', locale);
    }
    if (!isValidProfileEmail(trimmedForm.email)) {
      return locale === 'en'
        ? 'Please enter a valid email address'
        : '请输入正确的邮箱格式';
    }
    if (!trimmedForm.bioEn) {
      return getProfileFieldRequiredMessage(
        '英文个人简介',
        'English Personal Bio',
        locale
      );
    }
    if (getUnicodeCharacterCount(trimmedForm.bioEn) > 600) {
      return getProfileFieldLengthLimitMessage(
        '英文个人简介',
        'English Personal Bio',
        600,
        locale
      );
    }
    if (!trimmedForm.bioCn) {
      return getProfileFieldRequiredMessage(
        '中文个人简介',
        'Chinese Personal Bio',
        locale
      );
    }
    if (getUnicodeCharacterCount(trimmedForm.bioCn) > 600) {
      return getProfileFieldLengthLimitMessage(
        '中文个人简介',
        'Chinese Personal Bio',
        600,
        locale
      );
    }
    return '';
  }

  if (!trimmedForm.nameCn) {
    return '中文姓名必填';
  }
  if (getUnicodeCharacterCount(trimmedForm.nameCn) > 20) {
    return '中文姓名不能超过20个字符';
  }
  if (!trimmedForm.nameEn) {
    return '英文姓名必填';
  }
  if (getUnicodeCharacterCount(trimmedForm.nameEn) > 50) {
    return '英文姓名不能超过50个字符';
  }
  if (!titles[0]?.titleCn) {
    return '中文头衔必填';
  }
  if (getUnicodeCharacterCount(titles[0].titleCn) > 50) {
    return '中文头衔不能超过50个字符';
  }
  if (!titles[0]?.titleEn) {
    return '英文头衔必填';
  }
  if (getUnicodeCharacterCount(titles[0].titleEn) > 100) {
    return '英文头衔不能超过100个字符';
  }
  for (const item of titles.slice(1)) {
    if (getUnicodeCharacterCount(item.titleCn) > 50) {
      return '中文头衔不能超过50个字符';
    }
    if (getUnicodeCharacterCount(item.titleEn) > 100) {
      return '英文头衔不能超过100个字符';
    }
  }
  if (!trimmedForm.phone) {
    return '手机号必填';
  }
  if (trimmedForm.email && !isValidProfileEmail(trimmedForm.email)) {
    return '请输入正确的邮箱格式';
  }
  if (!trimmedForm.bioCn) {
    return '中文个人简介必填';
  }
  if (getUnicodeCharacterCount(trimmedForm.bioCn) > 600) {
    return '中文个人简介不能超过600个字符';
  }
  if (!trimmedForm.bioEn) {
    return '英文个人简介必填';
  }
  if (getUnicodeCharacterCount(trimmedForm.bioEn) > 600) {
    return '英文个人简介不能超过600个字符';
  }
  return '';
};

const Avatar = ({ src, name }: { src?: string; name?: string }) => {
  if (src) {
    return <img className={styles.avatar} src={src} alt={name || ''} />;
  }
  return <div className={styles.avatarFallback}>{getInitial(name)}</div>;
};

const ProfileFieldLabel = ({
  text,
  required = false,
}: {
  text: string;
  required?: boolean;
}) => (
  <span className={styles.profileFieldLabelText}>
    <span>{text}</span>
    {required ? <span className={styles.profileFieldRequired}>*</span> : null}
  </span>
);

const ContactMenuIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 10.9995C13.8655 10.9996 16.9989 12.7906 16.999 14.9995C16.999 17.2085 13.8656 18.9994 10 18.9995C6.13422 18.9995 3 17.2085 3 14.9995C3.00011 12.7905 6.13429 10.9995 10 10.9995ZM10 1.00049C12.2088 1.00069 13.999 2.79159 13.999 5.00049C13.9989 7.20928 12.2088 9.00028 10 9.00049C7.79105 9.00049 6.00012 7.20941 6 5.00049C6 2.79147 7.79098 1.00049 10 1.00049Z"
      fill="currentColor"
    />
  </svg>
);

const MessageMenuIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      opacity="0.4"
      d="M8.73508 1.75012H7.74744C3.92925 1.75012 0.833984 4.88407 0.833984 8.75V11.7499C0.833984 12.8545 1.71834 13.7499 2.80926 13.7499H8.73508C12.0078 13.7499 14.6609 11.0637 14.6609 7.75002C14.6609 7.26726 14.6046 6.79781 14.4983 6.34805C13.8747 3.71067 11.531 1.75012 8.73508 1.75012Z"
      fill="currentColor"
    />
    <path
      d="M8.73445 13.7501C12.0072 13.7501 14.6603 11.0639 14.6603 7.75023C14.6603 7.26747 14.604 6.79803 14.4976 6.34827C16.9207 7.43642 18.6108 9.89344 18.6108 12.7501V15.7501C18.6108 16.8546 17.7265 17.7501 16.6355 17.7501H10.7097C8.12959 17.7501 5.93458 16.0805 5.12109 13.7501H8.73445Z"
      fill="currentColor"
    />
  </svg>
);

const AgendaMenuIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.332 1.0415C13.6772 1.0415 13.957 1.32133 13.957 1.6665L13.957 4.1665C13.957 4.51168 13.6772 4.7915 13.332 4.7915C12.9869 4.7915 12.707 4.51168 12.707 4.1665L12.707 1.6665C12.707 1.32133 12.9869 1.0415 13.332 1.0415Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.66602 1.0415C7.01119 1.0415 7.29102 1.32133 7.29102 1.6665L7.29102 4.1665C7.29102 4.51168 7.01119 4.7915 6.66602 4.7915C6.32084 4.7915 6.04102 4.51168 6.04102 4.1665L6.04102 1.6665C6.04102 1.32133 6.32084 1.0415 6.66602 1.0415Z"
      fill="currentColor"
    />
    <path
      opacity="0.4"
      d="M14.1667 2.9165H5.83333C3.99238 2.9165 2.5 4.40889 2.5 6.24984V14.9998C2.5 16.8408 3.99238 18.3332 5.83333 18.3332H14.1667C16.0076 18.3332 17.5 16.8408 17.5 14.9998V6.24984C17.5 4.40889 16.0076 2.9165 14.1667 2.9165Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.04102 7.49988C6.04102 7.1547 6.32084 6.87488 6.66602 6.87488H9.99935C10.3445 6.87488 10.6243 7.1547 10.6243 7.49988C10.6243 7.84506 10.3445 8.12488 9.99935 8.12488H6.66602C6.32084 8.12488 6.04102 7.84506 6.04102 7.49988Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.04102 10.8333C6.04102 10.4881 6.32084 10.2083 6.66602 10.2083H13.3327C13.6779 10.2083 13.9577 10.4881 13.9577 10.8333C13.9577 11.1784 13.6779 11.4583 13.3327 11.4583H6.66602C6.32084 11.4583 6.04102 11.1784 6.04102 10.8333Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.04102 14.1665C6.04102 13.8213 6.32084 13.5415 6.66602 13.5415H13.3327C13.6779 13.5415 13.9577 13.8213 13.9577 14.1665C13.9577 14.5117 13.6779 14.7915 13.3327 14.7915H6.66602C6.32084 14.7915 6.04102 14.5117 6.04102 14.1665Z"
      fill="currentColor"
    />
  </svg>
);

const ProfileDecorIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect width="16" height="16" rx="8" fill="#0040DD" />
    <path
      d="M8 6.5C8.9665 6.5 9.75 5.7165 9.75 4.75C9.75 3.7835 8.9665 3 8 3C7.0335 3 6.25 3.7835 6.25 4.75C6.25 5.7165 7.0335 6.5 8 6.5Z"
      fill="white"
      stroke="white"
      strokeWidth="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.75 13H7.25L4 8H12L8.75 13Z"
      fill="white"
      stroke="white"
      strokeWidth="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SelfGuestIdentityIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 4.5C6.9665 4.5 7.75 3.7165 7.75 2.75C7.75 1.7835 6.9665 1 6 1C5.0335 1 4.25 1.7835 4.25 2.75C4.25 3.7165 5.0335 4.5 6 4.5Z"
      fill="#0040DD"
      stroke="#0040DD"
      strokeWidth="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 11H5.25L2 6H10L6.75 11Z"
      fill="#0040DD"
      stroke="#0040DD"
      strokeWidth="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SelfPartnerIdentityIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1.25093 10.5578C1.25093 10.6639 1.33952 10.75 1.4488 10.75L10.5506 10.75C10.6599 10.75 10.7485 10.6639 10.7485 10.5578V10.3283C10.753 10.2591 10.7623 9.91387 10.5347 9.53222C10.3912 9.29157 10.183 9.08367 9.9157 8.91432C9.5924 8.70945 9.18113 8.561 8.68368 8.47162C8.68 8.47115 8.31115 8.42225 7.93328 8.32575C7.27525 8.15767 7.21773 8.00892 7.21735 8.00747C7.21348 7.99277 7.20788 7.97865 7.2007 7.96537C7.19533 7.93762 7.18203 7.8332 7.20745 7.5534C7.272 6.84275 7.6532 6.42275 7.9595 6.0853C8.0561 5.97887 8.14733 5.87835 8.2176 5.77977C8.52068 5.35452 8.5488 4.87097 8.55008 4.841C8.55008 4.78027 8.5431 4.73035 8.52818 4.68407C8.49843 4.59147 8.44245 4.53377 8.40158 4.49165L8.4013 4.49135C8.391 4.48075 8.38128 4.4707 8.37333 4.46147C8.3703 4.45795 8.36225 4.44862 8.36958 4.40075C8.39648 4.22452 8.41263 4.07697 8.42038 3.9364C8.43418 3.68595 8.44495 3.3114 8.38035 2.94687C8.37238 2.88462 8.35868 2.81887 8.33735 2.74055C8.2691 2.48953 8.15945 2.27493 8.0071 2.09781C7.9809 2.0693 7.3439 1.3982 5.4947 1.2605C5.239 1.24146 4.98623 1.25172 4.7374 1.26444C4.67743 1.2674 4.5953 1.27147 4.51845 1.29138C4.32753 1.34084 4.27658 1.46186 4.2632 1.52958C4.24103 1.64177 4.28 1.72904 4.30578 1.78679C4.30953 1.79517 4.31415 1.80553 4.30608 1.83249C4.26315 1.89897 4.19563 1.9589 4.12678 2.01568C4.10688 2.03259 3.64318 2.43242 3.61768 2.95472C3.54893 3.35195 3.55413 3.97085 3.63543 4.3986C3.64015 4.42222 3.64713 4.4572 3.6358 4.48082C3.54838 4.55917 3.44928 4.64797 3.44953 4.8506C3.45058 4.87097 3.4787 5.35452 3.7818 5.77977C3.852 5.87827 3.94315 5.97872 4.03968 6.08507L4.0399 6.0853C4.3462 6.42275 4.72738 6.84275 4.79195 7.55337C4.81735 7.83317 4.80405 7.93762 4.79868 7.96535C4.7915 7.97862 4.7859 7.99275 4.78205 8.00745C4.78165 8.0089 4.72435 8.15717 4.0693 8.3249C3.6914 8.42167 3.31938 8.47112 3.30828 8.47272C2.82485 8.55432 2.4161 8.69907 2.09338 8.90287C1.82703 9.0711 1.61839 9.27938 1.47322 9.52192C1.24129 9.90948 1.24756 10.2624 1.25093 10.3268V10.5578Z"
      fill="#0040DD"
      stroke="#0040DD"
      strokeWidth="0.75"
      strokeLinejoin="round"
    />
  </svg>
);

const SelfAudienceIdentityIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 5C6.9665 5 7.75 4.2165 7.75 3.25C7.75 2.2835 6.9665 1.5 6 1.5C5.0335 1.5 4.25 2.2835 4.25 3.25C4.25 4.2165 5.0335 5 6 5Z"
      fill="#0040DD"
      stroke="#0040DD"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.5 10.2V10.5H10.5V10.2C10.5 9.0799 10.5 8.51985 10.282 8.09202C10.0903 7.7157 9.7843 7.40973 9.40798 7.21798C8.98015 7 8.4201 7 7.3 7H4.7C3.5799 7 3.01985 7 2.59203 7.21798C2.2157 7.40973 1.90973 7.7157 1.71799 8.09202C1.5 8.51985 1.5 9.0799 1.5 10.2Z"
      fill="#0040DD"
      stroke="#0040DD"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BackChevronIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M12.5 4.1665L6.66667 9.99984L12.5 15.8332"
      stroke="currentColor"
      strokeWidth="1.67"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchInputIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M7.33398 12.0002C9.91131 12.0002 12.001 9.9105 12.001 7.33317C12.001 4.75584 9.91131 2.6665 7.33398 2.6665C4.75665 2.6665 2.66699 4.75584 2.66699 7.33317C2.66699 9.9105 4.75665 12.0002 7.33398 12.0002Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3337 13.3332L10.793 10.7932"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AgendaTimeIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M6 1.5C3.51472 1.5 1.5 3.51472 1.5 6C1.5 8.48528 3.51472 10.5 6 10.5C8.48528 10.5 10.5 8.48528 10.5 6C10.5 3.51472 8.48528 1.5 6 1.5Z"
      stroke="#0040DD"
      strokeWidth="1"
    />
    <path
      d="M6 3.5V6.25L7.75 7.25"
      stroke="#0040DD"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AgendaLocationIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M6 10.5C6 10.5 9 8.05558 9 5.25C9 3.59315 7.65685 2.25 6 2.25C4.34315 2.25 3 3.59315 3 5.25C3 8.05558 6 10.5 6 10.5Z"
      stroke="#0040DD"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="6" cy="5.25" r="1" fill="#0040DD" />
  </svg>
);

interface ConferenceChatAgendaCardProps {
  item: AgendaItem;
  locale: ChatLocale;
  mode?: AgendaCardMode;
}

const ConferenceChatAgendaCard = ({
  item,
  locale,
  mode = 'detail',
}: ConferenceChatAgendaCardProps) => {
  const forumTitle = getAgendaForumTitle(locale, item);
  const sessionTitle = getAgendaSessionTitle(locale, item);
  const title = getAgendaTitle(locale, item);
  const address = getAgendaAddress(locale, item);
  const detailTitle = forumTitle || title;
  const detailSecondaryTitle = detailTitle === title ? '' : title;
  const timeText =
    mode === 'detail'
      ? formatDetailAgendaTime(
          locale,
          item.StartTime ?? item.startTime,
          item.EndTime ?? item.endTime
        )
      : formatTime(
          item.StartTime ?? item.startTime,
          item.EndTime ?? item.endTime
        );

  if (mode === 'simple') {
    return (
      <div className={styles.agendaItem}>
        <div className={styles.agendaItemMeta}>
          {forumTitle && <span>{forumTitle}</span>}
        </div>
        <strong>{title}</strong>
        <em>{timeText}</em>
        {address && <small>{address}</small>}
      </div>
    );
  }

  return (
    <div className={styles.detailAgendaItem}>
      <img
        className={styles.detailAgendaDecoration}
        src={DETAIL_AGENDA_DECORATION_SRC}
        alt=""
        aria-hidden="true"
      />
      <div className={styles.detailAgendaItemTop}>
        <span className={styles.detailAgendaTimeIcon}>
          <AgendaTimeIcon />
        </span>
        <div className={styles.detailAgendaTime}>
          <span>{timeText}</span>
        </div>
      </div>
      <div className={styles.detailAgendaItemBody}>
        <strong>{detailTitle}</strong>
        {detailSecondaryTitle ? <em>{detailSecondaryTitle}</em> : null}
        {address ? <small>{address}</small> : null}
      </div>
    </div>
  );
};

interface ConferenceChatAgendaCardSkeletonProps {
  mode?: AgendaCardMode;
  mobile?: boolean;
}

const ConferenceChatAgendaCardSkeleton = ({
  mode = 'detail',
  mobile = false,
}: ConferenceChatAgendaCardSkeletonProps) => {
  if (mode === 'simple') {
    return (
      <div className={`${styles.agendaItem} ${styles.agendaItemSkeleton}`}>
        <div className={styles.agendaItemMeta}>
          <Skeleton
            animation="gradient"
            rowCol={[[{ width: '96px', height: '16px' }]]}
          />
        </div>
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: '100%', height: mobile ? '44px' : '48px' }]]}
        />
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: mobile ? '136px' : '160px', height: '18px' }]]}
        />
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: mobile ? '72%' : '64%', height: '16px' }]]}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.detailAgendaItem} ${styles.detailAgendaItemSkeleton}`}
    >
      <div className={styles.detailAgendaItemTop}>
        <span
          className={`${styles.detailAgendaTimeIcon} ${styles.detailAgendaTimeIconSkeleton}`}
        >
          <Skeleton
            animation="gradient"
            rowCol={[[{ type: 'circle', size: '12px' }]]}
          />
        </span>
        <div className={styles.detailAgendaTime}>
          <Skeleton
            animation="gradient"
            rowCol={[[{ width: mobile ? '112px' : '136px', height: '18px' }]]}
          />
        </div>
      </div>
      <div className={styles.detailAgendaItemBody}>
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: '100%', height: mobile ? '44px' : '48px' }]]}
        />
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: mobile ? '78%' : '68%', height: '18px' }]]}
        />
        <Skeleton
          animation="gradient"
          rowCol={[[{ width: mobile ? '62%' : '56%', height: '16px' }]]}
        />
      </div>
    </div>
  );
};

const HoverChatIcon = () => (
  <svg viewBox="0 0 74 70" fill="none" aria-hidden="true">
    <g filter="url(#hover-chat-filter)">
      <circle
        cx="36.5996"
        cy="32.6"
        r="24.6"
        fill="#EBF1FF"
        stroke="#D7E1F7"
        strokeWidth="0.8"
      />
      <path
        d="M23.5996 24.6C23.5996 23.4954 24.495 22.6 25.5996 22.6H46.5996C47.7042 22.6 48.5996 23.4954 48.5996 24.6V38.161C48.5996 39.2655 47.7042 40.161 46.5996 40.161H28.2755C28.0495 40.161 27.8302 40.2375 27.6533 40.3781L23.5996 43.6V24.6Z"
        fill="#4775E7"
      />
      <circle cx="30.0996" cy="31.1016" r="1.5" fill="#EBF1FF" />
      <circle cx="36.0957" cy="31.1016" r="1.5" fill="#EBF1FF" />
      <circle cx="42.1016" cy="31.1016" r="1.5" fill="#EBF1FF" />
    </g>
    <defs>
      <filter
        id="hover-chat-filter"
        x="-0.000391006"
        y="-2.47955e-05"
        width="73.2"
        height="73.2"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="5.8" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.25098 0 0 0 0 0.866667 0 0 0 0.12 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

export const ConferenceChatPage = ({
  locale = 'zh',
  apiPrefix = DEFAULT_CHAT_API_PREFIX,
  contactPageSize = DEFAULT_CONTACT_PAGE_SIZE,
  embedded = false,
  initialMainTab = 'contacts',
  initialIsMobileViewport = false,
  initialJumpContactID = '',
  initialJumpGuestDataSource = '',
}: ConferenceChatPageProps) => {
  const text = TEXT[locale];
  const directChatInvalidText =
    locale === 'en'
      ? 'Invalid chat target. Returned to the default page.'
      : '目标联系人参数错误，已返回默认页面';
  const directChatFailedText =
    locale === 'en'
      ? 'Failed to open the target chat. Returned to the default page.'
      : '目标联系人加载失败，已返回默认页面';
  const [auth, setAuth] = useState<ChatAuthInfo>();
  const [mainTab, setMainTab] = useState<MainTab>(initialMainTab);
  const [contactType, setContactType] = useState<ContactType>('guest');
  const [keyword, setKeyword] = useState('');
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [selected, setSelected] = useState<ContactItem>();
  const [preferredContactID, setPreferredContactID] = useState('');
  const [detail, setDetail] = useState<ContactItem>();
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [agendaLoaded, setAgendaLoaded] = useState(false);
  const [conferenceAgendaLoading, setConferenceAgendaLoading] = useState(false);
  const [conferenceAgendaLoaded, setConferenceAgendaLoaded] = useState(false);
  const [conferenceAgendaSection, setConferenceAgendaSection] =
    useState<LatestConferenceAgendaSectionData>();
  const [profileMode, setProfileMode] = useState(false);
  const [accountDeletionOpen, setAccountDeletionOpen] = useState(false);
  const [mobileProfileViewMode, setMobileProfileViewMode] =
    useState<MobileProfileViewMode>('display');
  const [desktopProfileViewMode, setDesktopProfileViewMode] =
    useState<DesktopProfileViewMode>('display');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileDetail, setProfileDetail] =
    useState<ConferenceChatProfileDetailResp>();
  const [selfProfile, setSelfProfile] = useState<ConferenceChatProfileDTO>();
  const [profileForm, setProfileForm] = useState<ConferenceChatProfileForm>(
    () => buildProfileForm(undefined, locale)
  );
  const [profileAvatarFiles, setProfileAvatarFiles] = useState<UploadFile[]>(
    []
  );
  const previousProfileContactTypeRef = useRef<string>('');
  const [chatSession, setChatSession] = useState<ChatSessionState>();
  const [contactSummaryMap, setContactSummaryMap] = useState<
    Record<string, ContactItem>
  >({});
  const [contactDetailLoading, setContactDetailLoading] = useState(false);
  const [conversationItems, setConversationItems] = useState<
    ConferenceChatConversationItem[]
  >([]);
  const [conversationPreviewMap, setConversationPreviewMap] = useState<
    Record<string, ChatConversationPreview>
  >({});
  const [selectedConversationKey, setSelectedConversationKey] = useState('');
  const selectedConversationRef = useRef<ChatConversationView>();
  const [messageDraftPeer, setMessageDraftPeer] = useState<ContactItem>();
  const [preferredConversationPeer, setPreferredConversationPeer] =
    useState<ChatParticipantRef>();
  const [threadMessages, setThreadMessages] = useState<ChatMessageView[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [messageHint, setMessageHint] = useState('');
  const [messageBootstrapping, setMessageBootstrapping] = useState(false);
  const [messageListLoaded, setMessageListLoaded] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadRefreshing, setThreadRefreshing] = useState(false);
  // 页面隐藏时暂停轮询，避免后台标签页持续请求会聊消息接口。
  const [isPageVisible, setIsPageVisible] = useState(
    () =>
      typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const [sending, setSending] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    initialIsMobileViewport
  );
  const [directChatJump, setDirectChatJump] = useState<DirectChatJumpState>();
  const [activeMobileContactLetter, setActiveMobileContactLetter] =
    useState('');
  const [mobileContactSearchVisible, setMobileContactSearchVisible] =
    useState(false);
  const [mobileContactSearchKeyword, setMobileContactSearchKeyword] =
    useState('');
  const [
    mobileContactSearchAppliedKeyword,
    setMobileContactSearchAppliedKeyword,
  ] = useState('');
  const [mobileContactSearchHistory, setMobileContactSearchHistory] = useState<
    string[]
  >([]);
  const [mobileContactSearchComposing, setMobileContactSearchComposing] =
    useState(false);
  const [showEnPrivacyAlert, setShowEnPrivacyAlert] = useState(false);
  const sessionPromiseRef = useRef<Promise<ChatSessionState> | null>(null);
  const publicKeyCacheRef = useRef<Record<string, ConferenceChatPublicKeyResp>>(
    {}
  );
  const threadRequestIdRef = useRef(0);
  const contactListRequestIdRef = useRef(0);
  const contactDetailRequestIdRef = useRef(0);
  const sendingRef = useRef(false);
  const threadIdentityRef = useRef('');
  const threadBodyRef = useRef<HTMLDivElement | null>(null);
  const mobileMessageDetailBodyRef = useRef<HTMLDivElement | null>(null);
  const agendaRequestIdRef = useRef(0);
  const conferenceAgendaRequestIdRef = useRef(0);
  const mobileContactListRef = useRef<HTMLDivElement | null>(null);
  const mobileContactSectionRefs = useRef<Record<string, HTMLElement | null>>(
    {}
  );
  const mobileContactSectionOffsetsRef = useRef<
    Array<{ letter: string; top: number }>
  >([]);
  const activeMobileContactLetterRef = useRef('');

  const closeEnPrivacyAlert = useCallback(() => {
    setShowEnPrivacyAlert(false);
    safeWriteEnPrivacyAlertDismissed();
  }, []);

  const renderEnPrivacyAlert = useCallback(
    (className: string) => {
      if (!showEnPrivacyAlert || locale !== 'en') {
        return null;
      }

      return (
        <Alert
          className={className}
          theme="warning"
          close
          message={EN_PRIVACY_ALERT_MESSAGE}
          onClose={closeEnPrivacyAlert}
        />
      );
    },
    [closeEnPrivacyAlert, locale, showEnPrivacyAlert]
  );

  useEffect(() => {
    if (locale !== 'en') {
      setShowEnPrivacyAlert(false);
      return;
    }
    setShowEnPrivacyAlert(!safeReadEnPrivacyAlertDismissed());
  }, [locale]);
  const mobileContactScrollRafRef = useRef<number>();
  const mobileMessageViewportRafRef = useRef<number>();
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const accessRevokedHandledRef = useRef(false);
  const accessDeniedRedirectHandledRef = useRef(false);
  const loginRedirectHandledRef = useRef(false);

  const scrollMessageThreadToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const scrollToBottom = (node: HTMLDivElement | null) => {
        if (!node) return;
        node.scrollTo({ top: node.scrollHeight, behavior });
      };

      scrollToBottom(threadBodyRef.current);
      scrollToBottom(mobileMessageDetailBodyRef.current);
    },
    []
  );

  const redirectToLogin = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      loginRedirectHandledRef.current ||
      isConferenceChatLogoutInProgress()
    ) {
      return;
    }
    loginRedirectHandledRef.current = true;
    const langParam = locale === 'en' ? '&s_language=en' : '';
    window.location.href = `/login?s_url=${encodeURIComponent(
      window.location.href
    )}${langParam}`;
  }, [locale]);

  const postChatApi = useMemo(
    () =>
      async <T,>(path: string, body: Record<string, unknown> = {}) => {
        // 会聊接口全部使用 POST，并携带登录态 Cookie；中英文站只通过 apiPrefix 切换后端服务。
        const response = await fetch(apiPrefix + path, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(body),
          cache: 'no-store',
        });
        const rawText = await response.text();
        if (
          response.status === 401 ||
          response.status === 403 ||
          response.status === 404
        ) {
          if (!rawText.trim()) {
            if (response.status === 401 || response.status === 403) {
              redirectToLogin();
            }
            return {
              code: response.status,
              msg: response.statusText,
            } as PortalResp<T>;
          }
          try {
            const payload = rawText
              ? (JSON.parse(rawText) as PortalResp<T>)
              : {};
            if (payload && typeof payload === 'object') {
              if (isConferenceChatLoginExpiredPayload(payload)) {
                redirectToLogin();
              }
              return payload;
            }
          } catch (error) {
            // 登录态失效时网关可能返回非 JSON 的 401/403 页面，此时直接跳登录页。
            if (response.status === 401 || response.status === 403) {
              redirectToLogin();
            }
            return {
              code: response.status,
              msg: response.statusText,
            } as PortalResp<T>;
          }
        }
        if (!rawText.trim()) {
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              redirectToLogin();
            }
            return {
              code: response.status,
              msg: response.statusText,
            } as PortalResp<T>;
          }
          return { code: -1, msg: 'empty response' } as PortalResp<T>;
        }
        try {
          const payload = JSON.parse(rawText) as PortalResp<T>;
          // 下架/失效场景可能返回 HTTP 401/403，但业务 JSON 里带有可识别的 code/msg，
          // 这里直接返回业务体，交给页面状态机处理跳回 S 大会页或禁发态。
          if (payload && typeof payload === 'object') {
            if (isConferenceChatLoginExpiredPayload(payload)) {
              redirectToLogin();
            }
            return payload;
          }
        } catch (error) {
          // 非 JSON 响应按原有 HTTP 状态码处理。
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              redirectToLogin();
            }
            return {
              code: response.status,
              msg: rawText || response.statusText,
            } as PortalResp<T>;
          }
          console.error('[ConferenceChatPage api parse error]', error);
          return { code: -1, msg: 'invalid response' } as PortalResp<T>;
        }
        try {
          return JSON.parse(rawText) as PortalResp<T>;
        } catch (error) {
          // 本地代理偶发返回 HTML/空串时不让页面崩溃，统一走业务错误提示。
          console.error('[ConferenceChatPage api parse error]', error);
          return { code: -1, msg: 'invalid response' } as PortalResp<T>;
        }
      },
    [apiPrefix, redirectToLogin]
  );

  const redirectWhenNoAccess = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      accessDeniedRedirectHandledRef.current
    ) {
      return;
    }
    accessDeniedRedirectHandledRef.current = true;
    // 无权限统一回对应语言的 S 大会首页，避免登录回跳后再退回登录页。
    window.location.replace(
      getConferenceChatAccessFallbackURL(locale === 'en' ? 'en' : 'zh')
    );
  }, [locale]);

  const handleChatRuntimePayload = useCallback(
    <T,>(payload?: PortalResp<T>, fallbackMessage?: string) => {
      const msg = payload?.msg?.trim();
      if (isConferenceChatLoginExpiredPayload(payload)) {
        redirectToLogin();
        return {
          handled: true,
          type: 'login_expired' as const,
          message: msg || fallbackMessage || '',
        };
      }
      if (msg === CONFERENCE_CHAT_ACCESS_REVOKED_MSG) {
        if (!accessRevokedHandledRef.current) {
          accessRevokedHandledRef.current = true;
          MessagePlugin.warning(text.messageAccessRevoked);
        }
        setAuth((previous) => ({
          ...(previous || {}),
          CanAccess: false,
          DenyToast: text.messageAccessRevoked,
        }));
        redirectWhenNoAccess();
        return {
          handled: true,
          type: 'access_revoked' as const,
          message: text.messageAccessRevoked,
        };
      }
      if (msg === CONFERENCE_CHAT_SELF_CONTACT_NOT_ALLOWED_MSG) {
        return {
          handled: true,
          type: 'self_contact_not_allowed' as const,
          message: text.messageSelfContactNotAllowed,
        };
      }
      if (msg === CONFERENCE_CHAT_PEER_OFFLINE_MSG) {
        return {
          handled: true,
          type: 'peer_offline' as const,
          message: text.messagePeerOffline,
        };
      }
      if (msg && CONFERENCE_CHAT_CONTACT_NOT_ALLOWED_MSGS.has(msg)) {
        return {
          handled: true,
          type: 'contact_not_allowed' as const,
          message: text.messageContactNotAllowed,
        };
      }
      return {
        handled: false,
        type: 'unknown' as const,
        message: fallbackMessage || payload?.msg || '',
      };
    },
    [
      redirectWhenNoAccess,
      redirectToLogin,
      text.messageAccessRevoked,
      text.messageSelfContactNotAllowed,
      text.messageContactNotAllowed,
      text.messagePeerOffline,
    ]
  );

  const applyPeerOfflineConversation = useCallback(
    (conversation?: ChatConversationView) => {
      if (!conversation) {
        setMessageHint('');
        return;
      }
      setConversationItems((previous) =>
        previous.map((item) => {
          const key = buildConversationKey(
            item,
            normalizeParticipant(item.PeerParticipant)
          );
          if (key !== conversation.key) {
            return item;
          }
          return {
            ...item,
            PeerDisplayStatus: CONFERENCE_CHAT_PEER_DISPLAY_STATUS_OFFLINE,
            CanSendMessage: false,
            DisableReason: CONFERENCE_CHAT_DISABLE_REASON_PEER_OFFLINE,
            DisableHint: item.DisableHint || text.messagePeerOffline,
            PeerNameCN:
              locale === 'en' ? item.PeerNameCN : text.conversationPeerExited,
            PeerNameEN:
              locale === 'en' ? text.conversationPeerExited : item.PeerNameEN,
          };
        })
      );
      setMessageHint('');
    },
    [locale, text.conversationPeerExited, text.messagePeerOffline]
  );

  const conferenceYear = auth?.ConferenceYear;
  const conferenceSite = locale === 'en' ? 'en' : 'zh';
  const isEmbeddedDesktop = embedded && !isMobileViewport;
  const isMobileProfilePanelVisible = profileMode && isMobileViewport;
  const isMobileContactDetailVisible =
    isMobileViewport &&
    !profileMode &&
    mainTab === 'contacts' &&
    Boolean(selected);
  const selectedContactID = selected?.ContactID;
  const selectedContactType = selected?.ContactType;
  const selectedGuestDataSource = selected?.GuestDataSource;
  const defaultMainTab = isMobileViewport ? 'messages' : 'contacts';
  const chatAuthIdentity = useMemo(
    () => ({
      ContactID: auth?.ContactID,
      ContactType: auth?.ContactType,
      GuestDataSource: auth?.GuestDataSource,
    }),
    [auth?.ContactID, auth?.ContactType, auth?.GuestDataSource]
  );

  const syncMainTabToUrl = useCallback((nextTab: MainTab) => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get(MAIN_TAB_QUERY_KEY) === nextTab) {
      return;
    }
    url.searchParams.set(MAIN_TAB_QUERY_KEY, nextTab);
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }, []);

  const clearDirectChatUrlParams = useCallback((fallbackTab?: MainTab) => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(CONTACT_ID_QUERY_KEY);
    url.searchParams.delete(GUEST_DATA_SOURCE_QUERY_KEY);
    if (fallbackTab) {
      url.searchParams.set(MAIN_TAB_QUERY_KEY, fallbackTab);
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const updateViewport = () => setIsMobileViewport(media.matches);
    updateViewport();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', updateViewport);
      return () => media.removeEventListener('change', updateViewport);
    }
    media.addListener(updateViewport);
    return () => media.removeListener(updateViewport);
  }, []);

  useEffect(() => {
    const normalizedContactID = initialJumpContactID.trim();
    const normalizedGuestDataSource = normalizeGuestDataSource(
      initialJumpGuestDataSource
    );
    if (!normalizedContactID && !normalizedGuestDataSource) {
      return;
    }
    if (!normalizedContactID || !normalizedGuestDataSource) {
      setDirectChatJump({ status: 'invalid' });
      return;
    }
    setDirectChatJump({
      status: 'pending',
      contactId: normalizedContactID,
      guestDataSource: normalizedGuestDataSource,
    });
  }, [initialJumpContactID, initialJumpGuestDataSource]);

  useEffect(() => {
    syncMainTabToUrl(mainTab);
  }, [mainTab, syncMainTabToUrl]);

  const sidebarProfileTitles = useMemo(() => {
    return getProfileTitleLines(locale, selfProfile);
  }, [locale, selfProfile]);

  const visibleContacts = useMemo(
    () =>
      contacts.filter((item) =>
        matchContactByLocaleKeyword(locale, item, keyword)
      ),
    [contacts, keyword, locale]
  );

  const mobileGroupedContacts = useMemo(() => {
    const sortedContacts = [...visibleContacts].sort((left, right) =>
      compareContactByLocale(locale, left, right)
    );
    const groups = sortedContacts.reduce<
      Array<{ letter: string; items: ContactItem[] }>
    >((result, item) => {
      const letter = getContactIndexLetter(locale, item);
      const previousGroup = result[result.length - 1];
      if (!previousGroup || previousGroup.letter !== letter) {
        result.push({ letter, items: [item] });
      } else {
        previousGroup.items.push(item);
      }
      return result;
    }, []);
    return groups;
  }, [locale, visibleContacts]);

  const mobileContactIndexLetters = useMemo(
    () => mobileGroupedContacts.map((group) => group.letter),
    [mobileGroupedContacts]
  );

  useEffect(() => {
    const nextLetter = mobileContactIndexLetters[0] || '';
    activeMobileContactLetterRef.current = nextLetter;
    setActiveMobileContactLetter(nextLetter);
  }, [mobileContactIndexLetters]);

  useEffect(() => {
    if (!isMobileViewport || mainTab !== 'contacts' || selected) {
      return;
    }
    const container = mobileContactListRef.current;
    if (!container || !mobileContactIndexLetters.length) {
      return;
    }
    const refreshSectionOffsets = () => {
      mobileContactSectionOffsetsRef.current = mobileContactIndexLetters
        .map((letter) => {
          const section = mobileContactSectionRefs.current[letter];
          if (!section) {
            return undefined;
          }
          return {
            letter,
            top: section.offsetTop,
          };
        })
        .filter((item): item is { letter: string; top: number } =>
          Boolean(item)
        );
    };

    const updateActiveLetter = () => {
      const sectionOffsets = mobileContactSectionOffsetsRef.current;
      if (!sectionOffsets.length) {
        return;
      }
      const currentTop = container.scrollTop + 8;
      let currentLetter = sectionOffsets[0].letter;
      sectionOffsets.forEach((item) => {
        if (item.top <= currentTop) {
          currentLetter = item.letter;
        }
      });
      if (activeMobileContactLetterRef.current !== currentLetter) {
        activeMobileContactLetterRef.current = currentLetter;
        setActiveMobileContactLetter(currentLetter);
      }
    };
    const scheduleUpdateActiveLetter = () => {
      if (mobileContactScrollRafRef.current) {
        return;
      }
      mobileContactScrollRafRef.current = window.requestAnimationFrame(() => {
        mobileContactScrollRafRef.current = undefined;
        updateActiveLetter();
      });
    };
    refreshSectionOffsets();
    updateActiveLetter();
    container.addEventListener('scroll', scheduleUpdateActiveLetter, {
      passive: true,
    });
    window.addEventListener('resize', refreshSectionOffsets);
    return () => {
      container.removeEventListener('scroll', scheduleUpdateActiveLetter);
      window.removeEventListener('resize', refreshSectionOffsets);
      if (mobileContactScrollRafRef.current) {
        window.cancelAnimationFrame(mobileContactScrollRafRef.current);
        mobileContactScrollRafRef.current = undefined;
      }
    };
  }, [isMobileViewport, mainTab, mobileContactIndexLetters, selected]);

  const isSelfContact =
    Boolean(auth?.ContactID && detail?.ContactID) &&
    normalizeConferenceChatContactType(auth?.ContactType) ===
      normalizeConferenceChatContactType(detail?.ContactType) &&
    auth?.ContactID === detail?.ContactID &&
    (detail?.ContactType !== 'guest' ||
      normalizeGuestDataSource(auth?.GuestDataSource) ===
        normalizeGuestDataSource(detail?.GuestDataSource));

  const upsertContactSummaries = useCallback((list: ContactItem[]) => {
    if (!list.length) return;
    setContactSummaryMap((previous) => {
      const next = { ...previous };
      let changed = false;
      list.forEach((item) => {
        const participant = inferParticipantFromContact(item);
        const participantKey = buildParticipantKey(participant);
        if (participantKey) {
          const merged = mergeContactSummary(previous[participantKey], item);
          if (!isSameContactSummary(previous[participantKey], merged)) {
            next[participantKey] = merged as ContactItem;
            changed = true;
          }
        }
      });
      return changed ? next : previous;
    });
  }, []);

  const contactSummaryList = useMemo(
    () =>
      Object.values(contactSummaryMap).sort((left, right) =>
        compareContactByLocale(locale, left, right)
      ),
    [contactSummaryMap, locale]
  );

  const mobileSearchKeywordNormalized = useMemo(
    () => normalizeSearchKeyword(mobileContactSearchAppliedKeyword),
    [mobileContactSearchAppliedKeyword]
  );
  const renderTextSkeleton = useCallback(
    (
      rowCol: Array<
        | number
        | { width?: string; height?: string; type?: 'rect' | 'circle' | 'text' }
      >,
      className?: string
    ) => (
      <Skeleton animation="gradient" className={className} rowCol={rowCol} />
    ),
    []
  );

  const renderSidebarProfileSkeleton = useCallback(
    () => (
      <div className={styles.profileSkeleton}>
        <Skeleton
          animation="gradient"
          rowCol={[
            [{ type: 'circle', size: '64px' }],
            [{ width: '120px', height: '22px' }],
            [{ width: '156px', height: '16px' }],
            [{ width: '128px', height: '16px' }],
          ]}
        />
      </div>
    ),
    []
  );

  const renderDesktopContactCardSkeletons = useCallback(
    (count: number) =>
      Array.from({ length: count }).map((_, index) => (
        <article
          key={`contact-skeleton-${index}`}
          className={`${styles.contactPersonCard} ${styles.contactPersonCardSkeleton}`}
        >
          <Skeleton
            animation="gradient"
            rowCol={[
              [{ type: 'circle', size: '88px' }],
              [{ width: '96px', height: '28px', margin: '12px 0 0' }],
              [{ width: '100%', height: '20px' }],
              [{ width: '84%', height: '20px' }],
            ]}
          />
        </article>
      )),
    []
  );

  const renderMobileContactEntrySkeletons = useCallback(
    (count: number) => (
      <div className={styles.mobileContactFlatList}>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={`mobile-contact-skeleton-${index}`}
            className={`${styles.mobileContactEntry} ${styles.mobileContactEntrySkeleton}`}
          >
            <div className={styles.mobileContactEntrySkeletonAvatar}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ type: 'circle', size: '40px' }]]}
              />
            </div>
            <div className={styles.mobileContactEntrySkeletonContent}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '120px', height: '20px' }]]}
              />
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '176px', height: '16px' }]]}
              />
            </div>
          </div>
        ))}
      </div>
    ),
    []
  );

  const renderAgendaCardSkeletons = useCallback(
    (
      count: number,
      options: {
        className?: string;
        mobile?: boolean;
        mode?: AgendaCardMode;
      } = {}
    ) => {
      const { className, mobile = false, mode = 'detail' } = options;

      return (
        <div
          className={
            className ||
            (mobile
              ? styles.mobileContactDetailAgendaList
              : styles.detailAgendaList)
          }
        >
          {Array.from({ length: count }).map((_, index) => (
            <ConferenceChatAgendaCardSkeleton
              key={`detail-agenda-skeleton-${
                mobile ? 'mobile' : 'desktop'
              }-${index}`}
              mode={mode}
              mobile={mobile}
            />
          ))}
        </div>
      );
    },
    []
  );

  const renderDesktopContactDetailSkeleton = useCallback(
    () => (
      <div className={styles.detailWithBreadcrumb}>
        <Breadcrumb className={styles.detailBreadcrumb}>
          <Breadcrumb.BreadcrumbItem>{text.contacts}</Breadcrumb.BreadcrumbItem>
          <Breadcrumb.BreadcrumbItem>
            {text.guestDetail}
          </Breadcrumb.BreadcrumbItem>
        </Breadcrumb>
        <div className={styles.detailCard}>
          <div className={styles.detailHeader}>
            <div
              className={`${styles.detailHeaderMain} ${styles.detailHeaderMainSkeleton}`}
            >
              <Skeleton
                animation="gradient"
                rowCol={[
                  [{ type: 'circle', size: '88px' }],
                  [{ width: '120px', height: '28px' }],
                  [{ width: '200px', height: '18px' }],
                ]}
              />
            </div>
          </div>
          <div className={styles.detailDivider} />
          <section className={styles.detailSection}>
            <h3>
              {renderTextSkeleton(
                [{ width: '72px', height: '24px' }],
                styles.sectionTitleSkeleton
              )}
            </h3>
            {renderTextSkeleton(
              [
                { width: '100%', height: '16px' },
                { width: '100%', height: '16px' },
                { width: '72%', height: '16px' },
              ],
              styles.detailBioSkeleton
            )}
          </section>
          <div className={styles.detailDivider} />
          <section className={styles.detailSection}>
            <h3>
              {renderTextSkeleton(
                [{ width: '96px', height: '24px' }],
                styles.sectionTitleSkeleton
              )}
            </h3>
            {renderAgendaCardSkeletons(1)}
          </section>
        </div>
      </div>
    ),
    [
      renderAgendaCardSkeletons,
      renderTextSkeleton,
      text.contacts,
      text.guestDetail,
    ]
  );

  const renderMobileContactDetailSkeleton = useCallback(
    () => (
      <div className={styles.mobileContactDetailOverlay}>
        <img
          src={getMobileContactDetailBg(selected)}
          alt=""
          className={styles.mobilePageBackground}
        />
        <div className={styles.mobileContactDetailHeader}>
          <button
            className={styles.mobileContactDetailBack}
            onClick={() => {
              setSelected(undefined);
              setDetail(undefined);
            }}
            type="button"
            aria-label={text.backToContacts}
          >
            <BackChevronIcon />
          </button>
        </div>
        <div className={styles.mobileContactDetailScroll}>
          <div className={styles.mobileContactDetailHero}>
            <div className={styles.mobileContactDetailHeroTop}>
              <div className={styles.mobileContactDetailProfileBlock}>
                <Skeleton
                  animation="gradient"
                  rowCol={[
                    [{ type: 'circle', size: '60px' }],
                    [{ width: '128px', height: '28px', margin: '8px 0 0' }],
                  ]}
                />
              </div>
            </div>
            <div className={styles.mobileContactDetailTitles}>
              {renderTextSkeleton(
                [
                  { width: '100px', height: '16px' },
                  { width: '140px', height: '16px' },
                ],
                styles.mobileContactDetailTitlesSkeleton
              )}
            </div>
          </div>
          <div className={styles.mobileContactDetailBody}>
            <section className={styles.mobileContactDetailSection}>
              <h3>
                {renderTextSkeleton(
                  [{ width: '72px', height: '24px' }],
                  styles.sectionTitleSkeleton
                )}
              </h3>
              {renderTextSkeleton(
                [
                  { width: '100%', height: '16px' },
                  { width: '100%', height: '16px' },
                  { width: '76%', height: '16px' },
                ],
                styles.mobileContactDetailBioSkeleton
              )}
            </section>
            <section className={styles.mobileContactDetailSection}>
              <h3>
                {renderTextSkeleton(
                  [{ width: '96px', height: '24px' }],
                  styles.sectionTitleSkeleton
                )}
              </h3>
              {renderAgendaCardSkeletons(1, { mobile: true })}
            </section>
          </div>
        </div>
      </div>
    ),
    [
      renderAgendaCardSkeletons,
      renderTextSkeleton,
      selected,
      text.backToContacts,
    ]
  );

  const renderConversationCardSkeletons = useCallback(
    (count: number, mobile = false) => (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={`conversation-skeleton-${
              mobile ? 'mobile' : 'desktop'
            }-${index}`}
            className={
              mobile
                ? styles.mobileConversationCardSkeleton
                : styles.conversationCardSkeleton
            }
          >
            <div
              className={
                mobile
                  ? styles.mobileConversationCardSkeletonAvatar
                  : styles.conversationCardSkeletonAvatar
              }
            >
              <Skeleton
                animation="gradient"
                rowCol={[
                  [
                    {
                      type: 'circle',
                      size: mobile ? '48px' : '40px',
                    },
                  ],
                ]}
              />
            </div>
            <div
              className={
                mobile
                  ? styles.mobileConversationCardSkeletonContent
                  : styles.conversationCardSkeletonContent
              }
            >
              {mobile ? (
                <div className={styles.mobileConversationCardSkeletonTitleRow}>
                  <Skeleton
                    animation="gradient"
                    rowCol={[[{ width: '124px', height: '20px' }]]}
                  />
                  <Skeleton
                    animation="gradient"
                    rowCol={[[{ width: '52px', height: '16px' }]]}
                  />
                </div>
              ) : (
                <Skeleton
                  animation="gradient"
                  rowCol={[
                    [
                      {
                        width: isEmbeddedDesktop ? '104px' : '112px',
                        height: '18px',
                      },
                    ],
                  ]}
                />
              )}
              <Skeleton
                animation="gradient"
                rowCol={[
                  [
                    {
                      width: mobile
                        ? '180px'
                        : isEmbeddedDesktop
                        ? '164px'
                        : '172px',
                      height: mobile
                        ? '16px'
                        : isEmbeddedDesktop
                        ? '16px'
                        : '14px',
                    },
                  ],
                ]}
              />
            </div>
            {!mobile && (
              <div className={styles.conversationCardSkeletonTime}>
                <Skeleton
                  animation="gradient"
                  rowCol={[
                    [
                      {
                        width: isEmbeddedDesktop ? '52px' : '44px',
                        height: isEmbeddedDesktop ? '16px' : '14px',
                      },
                    ],
                  ]}
                />
              </div>
            )}
          </div>
        ))}
      </>
    ),
    [isEmbeddedDesktop]
  );

  const renderThreadPanelSkeleton = useCallback((mobile = false) => {
    if (mobile) {
      return (
        <div className={styles.mobileThreadSkeleton}>
          <div className={styles.threadSkeletonTime}>
            <Skeleton
              animation="gradient"
              rowCol={[[{ width: '96px', height: '20px' }]]}
            />
          </div>
          <div className={`${styles.messageRow} ${styles.messageRowOwn}`}>
            <div
              className={`${styles.messageBubble} ${styles.messageBubbleOwn} ${styles.messageBubbleSkeleton}`}
            >
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '160px', height: '20px' }]]}
              />
            </div>
            <span className={styles.messageAvatar}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ type: 'circle', size: '46px' }]]}
              />
            </span>
          </div>
          <div className={styles.messageRow}>
            <span className={styles.messageAvatar}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ type: 'circle', size: '46px' }]]}
              />
            </span>
            <div
              className={`${styles.messageBubble} ${styles.messageBubbleSkeleton}`}
            >
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '190px', height: '20px' }]]}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.threadPanelSkeleton}>
        <div className={styles.threadBody}>
          <div className={styles.threadSkeletonTime}>
            <Skeleton
              animation="gradient"
              rowCol={[[{ width: '96px', height: '20px' }]]}
            />
          </div>
          <div className={`${styles.messageRow} ${styles.messageRowOwn}`}>
            <div
              className={`${styles.messageBubble} ${styles.messageBubbleOwn} ${styles.messageBubbleSkeleton}`}
            >
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '220px', height: '24px' }]]}
              />
            </div>
            <span className={styles.messageAvatar}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ type: 'circle', size: '46px' }]]}
              />
            </span>
          </div>
          <div className={styles.messageRow}>
            <span className={styles.messageAvatar}>
              <Skeleton
                animation="gradient"
                rowCol={[[{ type: 'circle', size: '46px' }]]}
              />
            </span>
            <div
              className={`${styles.messageBubble} ${styles.messageBubbleSkeleton}`}
            >
              <Skeleton
                animation="gradient"
                rowCol={[[{ width: '260px', height: '24px' }]]}
              />
            </div>
          </div>
        </div>
        <div className={styles.composer}>
          <Skeleton
            animation="gradient"
            className={styles.composerTextareaSkeleton}
            rowCol={[[{ width: '100%', height: '100%', type: 'rect' }]]}
          />
        </div>
      </div>
    );
  }, []);

  const renderMobileSelfCardSkeleton = useCallback(
    () => (
      <div className={styles.mobileSelfCardSkeleton}>
        <div className={styles.mobileSelfCardSkeletonAvatar}>
          <Skeleton
            animation="gradient"
            rowCol={[[{ type: 'circle', size: '60px' }]]}
          />
        </div>
        <div className={styles.mobileSelfCardSkeletonContent}>
          <div className={styles.mobileSelfCardSkeletonTitleRow}>
            <Skeleton
              animation="gradient"
              rowCol={[[{ width: '132px', height: '24px' }]]}
            />
            <Skeleton
              animation="gradient"
              rowCol={[[{ width: '68px', height: '16px' }]]}
            />
          </div>
          <Skeleton
            animation="gradient"
            rowCol={[[{ width: '168px', height: '16px' }]]}
          />
        </div>
      </div>
    ),
    []
  );

  const renderAgendaSectionSkeleton = useCallback(
    (layout: 'desktop' | 'embeddedDesktop' | 'mobile' = 'desktop') => {
      const isMobileLayout = layout === 'mobile';

      return (
        <div
          className={
            isMobileLayout
              ? styles.mobileAgendaSkeleton
              : styles.agendaSectionSkeleton
          }
        >
          <div
            className={
              isMobileLayout
                ? styles.mobileAgendaPanel
                : styles.agendaSectionList
            }
          >
            {buildGuestAgendaSections([], text).map((section) => (
              <section
                key={section.key}
                className={
                  isMobileLayout
                    ? styles.mobileAgendaSection
                    : styles.agendaSection
                }
              >
                <h3>{section.title}</h3>
                {renderAgendaCardSkeletons(1, {
                  className: isMobileLayout
                    ? styles.mobileAgendaCardList
                    : layout === 'embeddedDesktop'
                    ? styles.detailAgendaList
                    : styles.agendaList,
                  mode: layout === 'embeddedDesktop' ? 'detail' : 'simple',
                  mobile: isMobileLayout,
                })}
              </section>
            ))}
            {isMobileLayout ? (
              <div className={styles.mobileConferenceAgendaWrap}>
                <ConferenceChatConferenceAgendaSection
                  loading
                  title={text.conferenceAgenda}
                  site={conferenceSite}
                />
              </div>
            ) : null}
          </div>
        </div>
      );
    },
    [conferenceSite, renderAgendaCardSkeletons, text]
  );

  const mobileSearchGuestResults = useMemo(() => {
    if (!mobileSearchKeywordNormalized) {
      return [];
    }
    return contactSummaryList
      .filter(
        (item) =>
          normalizeConferenceChatContactType(item.ContactType) ===
          CHAT_CONTACT_TYPE_GUEST
      )
      .filter((item) =>
        matchContactByLocaleKeyword(locale, item, mobileSearchKeywordNormalized)
      );
  }, [contactSummaryList, locale, mobileSearchKeywordNormalized]);

  const mobileSearchPartnerResults = useMemo(() => {
    if (!mobileSearchKeywordNormalized) {
      return [];
    }
    return contactSummaryList
      .filter((item) => {
        const type = normalizeConferenceChatContactType(item.ContactType);
        return type === CHAT_CONTACT_TYPE_PARTNER || type === 'audience';
      })
      .filter((item) =>
        matchContactByLocaleKeyword(locale, item, mobileSearchKeywordNormalized)
      );
  }, [contactSummaryList, locale, mobileSearchKeywordNormalized]);

  const activeMobileSearchResults = useMemo(
    () =>
      contactType === 'guest'
        ? mobileSearchGuestResults
        : mobileSearchPartnerResults,
    [contactType, mobileSearchGuestResults, mobileSearchPartnerResults]
  );

  useEffect(() => {
    if (!auth?.ContactID?.trim()) {
      return;
    }
    upsertContactSummaries([
      {
        ContactID: auth.ContactID,
        ContactType: auth.ContactType,
        GuestDataSource: auth.GuestDataSource,
        NameCN: auth.NameCN,
        NameEN: auth.NameEN,
        Avatar: auth.Avatar,
      },
    ]);
  }, [
    auth?.Avatar,
    auth?.ContactID,
    auth?.ContactType,
    auth?.GuestDataSource,
    auth?.NameCN,
    auth?.NameEN,
    upsertContactSummaries,
  ]);

  useEffect(() => {
    setMobileContactSearchHistory(safeReadMobileSearchHistory());
  }, []);

  useEffect(() => {
    if (!mobileContactSearchVisible || !isMobileViewport) {
      return;
    }
    const timer = window.setTimeout(() => {
      mobileSearchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isMobileViewport, mobileContactSearchVisible]);

  useEffect(() => {
    if (mobileContactSearchComposing) {
      return;
    }
    setMobileContactSearchAppliedKeyword(mobileContactSearchKeyword);
  }, [mobileContactSearchComposing, mobileContactSearchKeyword]);

  useEffect(() => {
    setChatSession(undefined);
    setConversationItems([]);
    setConversationPreviewMap({});
    setSelectedConversationKey('');
    setMessageDraftPeer(undefined);
    setPreferredConversationPeer(undefined);
    setThreadMessages([]);
    setComposerValue('');
    setMessageHint('');
    publicKeyCacheRef.current = {};
    sessionPromiseRef.current = null;
  }, [auth?.UserGUID, conferenceYear]);

  const buildPeerSummary = useCallback(
    (participant?: ChatParticipantRef): ChatPeerSummary | undefined => {
      if (!participant) return undefined;
      // 会话详情和草稿入口都复用同一套系统参与人摘要，避免空资料时退回“未知联系人”。
      const systemSummary = buildConferenceChatSystemParticipantSummary({
        participant,
        locale,
      });
      const draftContact =
        messageDraftPeer &&
        sameParticipant(
          participant,
          inferParticipantFromContact(messageDraftPeer)
        )
          ? messageDraftPeer
          : undefined;
      const cached = contactSummaryMap[buildParticipantKey(participant)];
      const source = draftContact || cached;
      if (source) {
        return {
          participant,
          name: getContactName(locale, source),
          title: getContactTitle(locale, source),
          avatar: source.Avatar,
        };
      }
      if (systemSummary) {
        return {
          participant,
          name: getContactName(locale, {
            NameCN: systemSummary.nameCN,
            NameEN: systemSummary.nameEN,
          }),
          title: getContactTitle(locale, {
            Titles: [
              {
                TitleCN: systemSummary.titleCN,
                TitleEN: systemSummary.titleEN,
              },
            ],
          }),
          avatar: systemSummary.avatar,
        };
      }
      return {
        participant,
        name: `${getParticipantTypeLabel(locale, participant.type)} · ${
          text.unknownContact
        }`,
        title: getParticipantRegionLabel(locale, participant.region),
      };
    },
    [contactSummaryMap, locale, messageDraftPeer, text.unknownContact]
  );

  const ensureChatSession = useCallback(async () => {
    if (chatSession && chatSession.conferenceYear === conferenceYear) {
      return chatSession;
    }
    if (sessionPromiseRef.current) {
      return sessionPromiseRef.current;
    }
    sessionPromiseRef.current = (async () => {
      if (!conferenceYear) {
        throw new Error('conference year is empty');
      }
      const bootstrapPayload = await postChatApi<ConferenceChatBootstrapResp>(
        '/session/bootstrap',
        {
          ConferenceYear: conferenceYear,
        }
      );
      const bootstrapRuntime = handleChatRuntimePayload(
        bootstrapPayload,
        'bootstrap failed'
      );
      if (bootstrapRuntime.handled) {
        throw new Error(bootstrapRuntime.message || 'bootstrap failed');
      }
      if (bootstrapPayload.code !== 0 || !bootstrapPayload.data) {
        throw new Error(bootstrapPayload.msg || 'bootstrap failed');
      }
      const currentParticipant = normalizeParticipant(
        bootstrapPayload.data.Participant
      );
      const userGuid =
        bootstrapPayload.data.UserGUID?.trim() || auth?.UserGUID?.trim() || '';
      if (!currentParticipant || !userGuid) {
        throw new Error('chat bootstrap response is invalid');
      }
      const sessionKeyPair = await generateChatSessionKeyPair();
      const unsealPayload = await postChatApi<ConferenceChatKeyUnsealResp>(
        '/key/unseal',
        {
          ConferenceYear:
            bootstrapPayload.data.ConferenceYear || conferenceYear,
          SessionPublicKey: sessionKeyPair.publicKeyPem,
          SessionPublicKeyAlg: CHAT_PUBLIC_KEY_ALG,
        }
      );
      const unsealRuntime = handleChatRuntimePayload(
        unsealPayload,
        'key unseal failed'
      );
      if (unsealRuntime.handled) {
        throw new Error(unsealRuntime.message || 'key unseal failed');
      }
      if (unsealPayload.code !== 0 || !unsealPayload.data) {
        throw new Error(unsealPayload.msg || 'key unseal failed');
      }
      const privateKey = await unsealChatPrivateKey(
        unsealPayload.data,
        sessionKeyPair.privateKey
      );
      const nextSession = {
        conferenceYear: bootstrapPayload.data.ConferenceYear || conferenceYear,
        userGuid,
        participant: currentParticipant,
        privateKey,
        storageMode: bootstrapPayload.data.StorageMode || 1,
      };
      setChatSession(nextSession);
      return nextSession;
    })();
    try {
      return await sessionPromiseRef.current;
    } finally {
      sessionPromiseRef.current = null;
    }
  }, [
    auth?.UserGUID,
    chatSession,
    conferenceYear,
    handleChatRuntimePayload,
    postChatApi,
  ]);

  const loadSelfProfile = useCallback(async () => {
    if (!conferenceYear) {
      return undefined;
    }
    const payload = await postChatApi<ConferenceChatProfileDetailResp>(
      '/profile/detail',
      {
        ConferenceYear: conferenceYear,
      }
    );
    if (payload.code !== 0 || !payload.data?.Profile) {
      throw new Error(payload.msg || text.profileLoadFailed);
    }
    setProfileDetail(payload.data);
    setSelfProfile(payload.data.Profile);
    return payload.data;
  }, [conferenceYear, postChatApi, text.profileLoadFailed]);

  const refreshAccountDeletionEmail = useCallback(async () => {
    try {
      const userInfoResp = await getUserInfo();
      const displayEmail = String(userInfoResp?.data?.email ?? '').trim();
      const verifyEmail = String(userInfoResp?.data?.rawEmail ?? '').trim();
      if (displayEmail || verifyEmail) {
        return {
          displayEmail,
          verifyEmail,
        };
      }
    } catch {
      // 注册页同源用户信息可能因会话状态失败，下面仅兜底展示邮箱，不用于发送验证码。
    }

    const payload = await loadSelfProfile();
    return {
      displayEmail: payload?.Profile?.email?.trim() || '',
      verifyEmail: '',
    };
  }, [loadSelfProfile]);

  const appendMobileSearchHistory = useCallback((value: string) => {
    const nextKeyword = value.trim();
    if (!nextKeyword) {
      return;
    }
    setMobileContactSearchHistory((previous) => {
      const next = [
        nextKeyword,
        ...previous.filter((item) => item !== nextKeyword),
      ].slice(0, MOBILE_CONTACT_SEARCH_HISTORY_LIMIT);
      safeWriteMobileSearchHistory(next);
      return next;
    });
  }, []);

  const clearMobileSearchHistory = useCallback(() => {
    setMobileContactSearchHistory([]);
    safeWriteMobileSearchHistory([]);
  }, []);

  const fetchPublicKey = useCallback(
    async (participant: ChatParticipantRef, session: ChatSessionState) => {
      const cacheKey = buildParticipantKey(participant);
      if (publicKeyCacheRef.current[cacheKey]) {
        return publicKeyCacheRef.current[cacheKey];
      }
      const payload = await postChatApi<ConferenceChatPublicKeyResp>(
        '/key/public',
        {
          ConferenceYear: session.conferenceYear,
          Participant: {
            Region: participant.region,
            Type: participant.type,
            SourceID: participant.sourceID,
          },
        }
      );
      if (payload.code !== 0 || !payload.data?.PublicKey?.trim()) {
        throw new Error(payload.msg || 'public key fetch failed');
      }
      publicKeyCacheRef.current[cacheKey] = payload.data;
      return payload.data;
    },
    [postChatApi]
  );

  const primeMessagePeerSummaries = useCallback(async () => {
    if (!conferenceYear) return;
    const results = await Promise.allSettled([
      postChatApi<ContactListResp>('/contact/list', {
        ConferenceYear: conferenceYear,
        ContactType: 'guest',
        Keyword: '',
        page: 1,
        pageSize: contactPageSize,
      }),
      postChatApi<ContactListResp>('/contact/list', {
        ConferenceYear: conferenceYear,
        ContactType: 'partner',
        Keyword: '',
        page: 1,
        pageSize: contactPageSize,
      }),
    ]);
    const nextContacts: ContactItem[] = [];
    results.forEach((result) => {
      if (result.status !== 'fulfilled') {
        return;
      }
      if (result.value.code === 0) {
        nextContacts.push(...(result.value.data?.list || []));
      }
    });
    upsertContactSummaries(nextContacts);
  }, [conferenceYear, contactPageSize, postChatApi, upsertContactSummaries]);

  const openMobileContactSearch = useCallback(async () => {
    setMobileContactSearchVisible(true);
    if (!conferenceYear) {
      return;
    }
    await primeMessagePeerSummaries();
  }, [conferenceYear, primeMessagePeerSummaries]);

  const closeMobileContactSearch = useCallback(() => {
    setMobileContactSearchVisible(false);
    setMobileContactSearchKeyword('');
    setMobileContactSearchAppliedKeyword('');
    setMobileContactSearchComposing(false);
  }, []);

  const clearMobileContactSearchKeyword = useCallback(() => {
    setMobileContactSearchKeyword('');
    setMobileContactSearchAppliedKeyword('');
    setMobileContactSearchComposing(false);
    mobileSearchInputRef.current?.focus();
  }, []);

  const changeContactType = useCallback(
    (nextType: ContactType) => {
      if (nextType === contactType) {
        return;
      }
      setContactsLoading(true);
      setContacts([]);
      setSelected(undefined);
      setDetail(undefined);
      setContactType(nextType);
    },
    [contactType]
  );

  const updateContactKeyword = useCallback((nextKeyword: string) => {
    setSelected(undefined);
    setDetail(undefined);
    setKeyword(nextKeyword);
  }, []);

  const renderContactToolbar = useCallback(
    (desktop = false) => (
      <div
        className={`${styles.contactToolbar} ${
          desktop ? styles.contactToolbarDesktop : ''
        }`}
      >
        <div className={styles.segmented}>
          <button
            className={contactType === 'guest' ? styles.activeSegment : ''}
            onClick={() => changeContactType('guest')}
          >
            {text.guest}
          </button>
          <button
            className={contactType === 'partner' ? styles.activeSegment : ''}
            onClick={() => changeContactType('partner')}
          >
            {text.partner}
          </button>
        </div>
        {desktop && renderEnPrivacyAlert(styles.privacyAlertDesktop)}
        <label
          className={`${styles.searchWrap} ${
            desktop ? styles.searchWrapDesktop : ''
          }`}
        >
          <input
            className={styles.search}
            value={keyword}
            placeholder={text.searchPlaceholder}
            onChange={(event) => updateContactKeyword(event.target.value)}
          />
          <span className={styles.searchIcon}>
            <SearchInputIcon />
          </span>
        </label>
      </div>
    ),
    [
      changeContactType,
      contactType,
      keyword,
      text.guest,
      text.partner,
      text.searchPlaceholder,
      renderEnPrivacyAlert,
      updateContactKeyword,
    ]
  );

  const renderDesktopMainLoadingSkeleton = useCallback(() => {
    if (mainTab === 'contacts') {
      return (
        <div className={`${styles.contactPanel} ${styles.contactPanelDesktop}`}>
          {renderContactToolbar(true)}
          <div className={styles.contactCardGrid}>
            {renderDesktopContactCardSkeletons(4)}
          </div>
        </div>
      );
    }

    if (mainTab === 'agenda') {
      return (
        <div className={styles.agendaPanel}>
          {renderAgendaSectionSkeleton(
            isEmbeddedDesktop ? 'embeddedDesktop' : 'desktop'
          )}
        </div>
      );
    }

    return (
      <div
        className={`${styles.messageGrid} ${
          isEmbeddedDesktop ? styles.messageGridDesktop : ''
        }`.trim()}
      >
        <div className={styles.conversationPanel}>
          {!isEmbeddedDesktop && (
            <div className={styles.messageToolbar}>
              <strong>{text.messages}</strong>
            </div>
          )}
          <div className={styles.conversationList}>
            {renderConversationCardSkeletons(2)}
          </div>
        </div>
        <div className={styles.threadPanel}>{renderThreadPanelSkeleton()}</div>
      </div>
    );
  }, [
    isEmbeddedDesktop,
    mainTab,
    renderAgendaSectionSkeleton,
    renderContactToolbar,
    renderConversationCardSkeletons,
    renderDesktopContactCardSkeletons,
    renderThreadPanelSkeleton,
    text.messages,
  ]);

  const renderMobileMainLoadingSkeleton = useCallback(() => {
    if (mainTab === 'contacts') {
      return (
        <div className={styles.mobileContactPanel}>
          <button className={styles.mobileContactSearchTrigger} type="button">
            <span className={styles.mobileContactSearchMain}>
              <span className={styles.mobileContactSearchIcon}>
                <SearchInputIcon />
              </span>
              <span className={styles.mobileContactSearchPlaceholder}>
                {text.mobileContactSearchPlaceholder}
              </span>
            </span>
          </button>
          <div className={styles.mobileContactSegmented}>
            <button
              className={contactType === 'guest' ? styles.activeSegment : ''}
              type="button"
            >
              {text.guest}
            </button>
            <button
              className={contactType === 'partner' ? styles.activeSegment : ''}
              type="button"
            >
              {text.partner}
            </button>
          </div>
          <div className={styles.mobileContactListWrap}>
            <div className={styles.mobileContactList}>
              {renderMobileContactEntrySkeletons(2)}
            </div>
          </div>
        </div>
      );
    }

    if (mainTab === 'agenda') {
      return (
        <div className={styles.agendaPanel}>
          {renderAgendaSectionSkeleton('mobile')}
        </div>
      );
    }

    return (
      <div className={styles.mobileMessageSection}>
        <div className={styles.mobileMessageContainer}>
          {renderMobileSelfCardSkeleton()}
          <div className={styles.mobileConversationList}>
            {renderConversationCardSkeletons(2, true)}
          </div>
        </div>
      </div>
    );
  }, [
    contactType,
    mainTab,
    renderAgendaSectionSkeleton,
    renderConversationCardSkeletons,
    renderMobileContactEntrySkeletons,
    renderMobileSelfCardSkeleton,
    text.guest,
    text.mobileContactSearchPlaceholder,
    text.partner,
  ]);

  const switchMainTab = useCallback(
    (nextTab: MainTab) => {
      if (nextTab === mainTab) {
        if (
          nextTab === 'contacts' &&
          !isMobileViewport &&
          (selected || profileMode)
        ) {
          setProfileMode(false);
          setDesktopProfileViewMode('display');
          setMobileProfileViewMode('display');
          setContactDetailLoading(false);
          setSelected(undefined);
          setDetail(undefined);
        }
        return;
      }
      setProfileMode(false);
      setMobileProfileViewMode('display');

      if (nextTab === 'contacts') {
        setContactsLoading(true);
        setContacts([]);
        setSelected(undefined);
        setDetail(undefined);
      }
      if (nextTab === 'messages') {
        setMessageBootstrapping(true);
        setConversationItems([]);
        setConversationPreviewMap({});
        setThreadMessages([]);
        setSelectedConversationKey('');
        setMessageHint('');
      }
      if (nextTab === 'agenda') {
        setAgendaLoading(true);
        setAgenda([]);
      }

      if (nextTab !== 'contacts') {
        // 移动端切出联系人详情时立即废弃未完成的详情请求，避免旧请求回填出“嘉宾议程”内容。
        contactDetailRequestIdRef.current += 1;
        setSelected(undefined);
        setDetail(undefined);
        setContactDetailLoading(false);
        closeMobileContactSearch();
      } else {
        if (isMobileViewport) {
          setSelected(undefined);
          setDetail(undefined);
          closeMobileContactSearch();
        }
      }

      if (nextTab !== 'messages') {
        setSelectedConversationKey('');
        setMessageDraftPeer(undefined);
        setPreferredConversationPeer(undefined);
      }

      setMainTab(nextTab);
    },
    [closeMobileContactSearch, isMobileViewport, mainTab, profileMode, selected]
  );

  useEffect(() => {
    const nextConversationSummaryContacts = conversationItems
      .map((item) => buildMessagePeerSummaryContact(locale, item))
      .filter((item): item is ContactItem => Boolean(item));
    if (!nextConversationSummaryContacts.length) {
      return;
    }
    // 消息列表允许展示观众对端姓名，但不把观众纳入联系人 tab；这里只补本地摘要缓存。
    upsertContactSummaries(nextConversationSummaryContacts);
  }, [conversationItems, locale, upsertContactSummaries]);

  const resetToDefaultMainTab = useCallback(
    (message: string) => {
      const normalizedMessage =
        locale === 'en'
          ? message === directChatInvalidText || message.includes('参数错误')
            ? directChatInvalidText
            : message.includes('加载失败')
            ? directChatFailedText
            : message
          : message;
      MessagePlugin.warning(normalizedMessage);
      setSelected(undefined);
      setDetail(undefined);
      setSelectedConversationKey('');
      setMessageDraftPeer(undefined);
      setPreferredConversationPeer(undefined);
      setDirectChatJump(undefined);
      switchMainTab(defaultMainTab);
      clearDirectChatUrlParams(defaultMainTab);
    },
    [
      clearDirectChatUrlParams,
      defaultMainTab,
      directChatFailedText,
      directChatInvalidText,
      locale,
      switchMainTab,
    ]
  );

  const openContactDetail = useCallback((item: ContactItem) => {
    setContactDetailLoading(true);
    setDetail(undefined);
    setSelected(item);
  }, []);

  const openConversation = useCallback(
    (key: string) => {
      if (key === selectedConversationKey) {
        return;
      }
      // 会话点击发生在新线程 effect 启动之前，先废弃旧请求，避免旧会话结果在清空后再次回填。
      threadRequestIdRef.current += 1;
      threadIdentityRef.current = '';
      selectedConversationRef.current = undefined;
      setPreferredConversationPeer(undefined);
      setMessageDraftPeer(undefined);
      setThreadLoading(true);
      setThreadMessages([]);
      setMessageHint('');
      setSelectedConversationKey(key);
    },
    [selectedConversationKey]
  );

  const conversationViews = useMemo(() => {
    const list = conversationItems.map((item) => {
      const peer = normalizeParticipant(item.PeerParticipant) || {
        region: 1,
        type: 1,
        sourceID: item.ConversationHash || item.ConversationID || 'unknown',
      };
      return {
        key: buildConversationKey(item, peer),
        peer,
        peerSummary: buildPeerSummary(peer) || {
          participant: peer,
          name: text.unknownContact,
          title: getParticipantRegionLabel(locale, peer.region),
          avatar: item.PeerAvatar,
        },
        unread: !!item.Unread,
        preview: conversationPreviewMap[buildConversationKey(item, peer)] || {
          text: '',
          status: 'empty',
        },
        lastMessageTime: item.LastMessageTime,
        conversationID: item.ConversationID || '',
        conversationHash: item.ConversationHash || '',
        storageMode: item.StorageMode,
        peerDisplayStatus: item.PeerDisplayStatus,
        // 大会通知是系统参与人写入的只读消息，详情页不能复用普通会话发送入口。
        canSendMessage:
          !isConferenceChatSystemParticipant(peer) &&
          item.CanSendMessage !== false,
        disableReason: item.DisableReason,
        disableHint: item.DisableHint,
        raw: item,
      } as ChatConversationView;
    });
    const systemNoticeIndex = list.findIndex((item) =>
      isConferenceChatSystemParticipant(item.peer)
    );
    const hasSystemNotice = systemNoticeIndex >= 0;
    if (systemNoticeIndex > 0) {
      // 接口顺序异常时仍固定大会通知首位，同时保持普通会话的相对顺序。
      const [systemNotice] = list.splice(systemNoticeIndex, 1);
      list.unshift(systemNotice);
    }
    const draftParticipant = inferParticipantFromContact(messageDraftPeer);
    if (
      draftParticipant &&
      !list.some((item) => sameParticipant(item.peer, draftParticipant))
    ) {
      // 草稿仅在没有大会通知时占据首位，避免覆盖固定置顶通知。
      const draftInsertIndex = hasSystemNotice ? 1 : 0;
      list.splice(draftInsertIndex, 0, {
        key: buildDraftConversationKey(draftParticipant),
        peer: draftParticipant,
        peerSummary: buildPeerSummary(draftParticipant) || {
          participant: draftParticipant,
          name: text.messageDraftTitle,
          title: getParticipantTypeLabel(locale, draftParticipant.type),
        },
        unread: false,
        preview: {
          text: '',
          status: 'empty',
        },
        lastMessageTime: '',
        conversationID: '',
        conversationHash: '',
        storageMode: chatSession?.storageMode,
        peerDisplayStatus: undefined,
        canSendMessage: true,
        disableReason: undefined,
        disableHint: undefined,
        isDraft: true,
      });
    }
    return list;
  }, [
    buildPeerSummary,
    chatSession?.storageMode,
    conversationItems,
    conversationPreviewMap,
    locale,
    messageDraftPeer,
    text.messageDraftTitle,
    text.unknownContact,
  ]);

  const selectedConversation = useMemo(
    () =>
      conversationViews.find((item) => item.key === selectedConversationKey),
    [conversationViews, selectedConversationKey]
  );
  const selectedConversationPeerOffline = isConferenceChatPeerOffline(
    selectedConversation?.peerDisplayStatus,
    selectedConversation?.disableReason
  );
  const selectedConversationPeerAvatar =
    getConversationPeerAvatar(selectedConversation);
  const selectedConversationIsSystemNotice = isConferenceChatSystemParticipant(
    selectedConversation?.peer
  );
  const selectedConversationCanSend =
    !selectedConversationPeerOffline &&
    !selectedConversationIsSystemNotice &&
    selectedConversation?.canSendMessage !== false;
  const selectedConversationDisableHint =
    selectedConversation?.disableHint?.trim() || '';
  const selectedConversationInputPlaceholder = selectedConversationPeerOffline
    ? text.messagePeerOffline
    : `${text.messageInputPlaceholder} ${
        selectedConversation?.peerSummary.name || ''
      }`;
  const selectedConversationForThreadKey = selectedConversation?.key || '';

  const isMobileMessageDetailVisible =
    isMobileViewport &&
    !profileMode &&
    mainTab === 'messages' &&
    Boolean(selectedConversation);

  useEffect(() => {
    if (!isMobileMessageDetailVisible || typeof window === 'undefined') {
      return;
    }
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }
    const root = document.documentElement;
    const updateViewportHeight = () => {
      root.style.setProperty(
        '--mobile-chat-viewport-height',
        `${viewport.height}px`
      );
      root.style.setProperty(
        '--mobile-chat-viewport-offset-top',
        `${viewport.offsetTop}px`
      );
    };
    const scheduleUpdateViewportHeight = () => {
      if (mobileMessageViewportRafRef.current) {
        return;
      }
      mobileMessageViewportRafRef.current = window.requestAnimationFrame(() => {
        mobileMessageViewportRafRef.current = undefined;
        updateViewportHeight();
      });
    };

    updateViewportHeight();
    viewport.addEventListener('resize', scheduleUpdateViewportHeight);
    viewport.addEventListener('scroll', scheduleUpdateViewportHeight);
    return () => {
      viewport.removeEventListener('resize', scheduleUpdateViewportHeight);
      viewport.removeEventListener('scroll', scheduleUpdateViewportHeight);
      if (mobileMessageViewportRafRef.current) {
        window.cancelAnimationFrame(mobileMessageViewportRafRef.current);
        mobileMessageViewportRafRef.current = undefined;
      }
      root.style.removeProperty('--mobile-chat-viewport-height');
      root.style.removeProperty('--mobile-chat-viewport-offset-top');
    };
  }, [isMobileMessageDetailVisible]);

  const unreadConversationCount = useMemo(
    () => conversationViews.filter((item) => item.unread).length,
    [conversationViews]
  );
  const threadMessageDividerFlags = useMemo(
    () => buildMessageDividerFlags(threadMessages),
    [threadMessages]
  );

  const mapCachedRecordToView = useCallback(
    (record: CachedChatMessageRecord): ChatMessageView => ({
      key: record.messageId,
      messageID: record.messageId,
      clientMsgID: record.messageId,
      plaintext: record.plaintext,
      fromSelf: record.fromSelf,
      sendTime: record.sendTime,
      readStatus: record.fromSelf ? 2 : 1,
      decryptStatus: 'ready',
      sender: {
        region: record.senderRegion,
        type: record.senderType,
        sourceID: record.senderSourceID,
      },
      receiver: {
        region: record.receiverRegion,
        type: record.receiverType,
        sourceID: record.receiverSourceID,
      },
    }),
    []
  );

  const loadConversationList = useCallback(
    async (options?: { silent?: boolean }) => {
      const session = await ensureChatSession();
      await primeMessagePeerSummaries();
      if (!options?.silent) {
        setConversationsLoading(true);
      }
      try {
        const payload = await postChatApi<ConferenceChatConversationListResp>(
          '/conversation/list',
          {
            ConferenceYear: session.conferenceYear,
            page: 1,
            pageSize: CHAT_CONVERSATION_PAGE_SIZE,
          }
        );
        const runtimeResult = handleChatRuntimePayload(
          payload,
          text.conversationFailed
        );
        if (runtimeResult.handled) {
          throw new Error(runtimeResult.message || text.conversationFailed);
        }
        if (payload.code !== 0) {
          throw new Error(payload.msg || text.conversationFailed);
        }
        const list = payload.data?.list || [];
        setConversationItems(list);
        const previewEntries = await Promise.all(
          list.map(async (item) => {
            const peer = normalizeParticipant(item.PeerParticipant);
            const conversationKey = buildConversationKey(item, peer);
            if (
              !item.LastMessageCiphertext ||
              !item.LastMessageNonce ||
              !item.LastEncryptedMessageKey
            ) {
              return [
                conversationKey,
                {
                  text: '',
                  status: 'empty',
                } as ChatConversationPreview,
              ] as const;
            }
            try {
              const plaintext = await decryptChatMessage(
                session.privateKey,
                item.LastEncryptedMessageKey,
                item.LastMessageCiphertext,
                item.LastMessageNonce
              );
              return [
                conversationKey,
                {
                  text: truncateMessage(plaintext.replace(/\s+/g, ' ').trim()),
                  status: 'ready',
                } as ChatConversationPreview,
              ] as const;
            } catch (error) {
              console.error(
                '[ConferenceChatPage preview decrypt error]',
                error
              );
              return [
                conversationKey,
                {
                  text: text.messagePreviewFallback,
                  status: 'failed',
                } as ChatConversationPreview,
              ] as const;
            }
          })
        );
        setConversationPreviewMap(Object.fromEntries(previewEntries));
        return list;
      } finally {
        if (!options?.silent) {
          setConversationsLoading(false);
        }
      }
    },
    [
      ensureChatSession,
      handleChatRuntimePayload,
      postChatApi,
      primeMessagePeerSummaries,
      text.conversationFailed,
      text.messagePreviewFallback,
    ]
  );

  const ackMessages = useCallback(
    async (
      session: ChatSessionState,
      conversation: ChatConversationView,
      messageIDs: string[]
    ) => {
      if (
        !messageIDs.length ||
        conversation.isDraft ||
        selectedConversationRef.current?.key !== conversation.key
      ) {
        return;
      }
      const payload = await postChatApi<Record<string, never>>('/message/ack', {
        ConferenceYear: session.conferenceYear,
        ConversationID: conversation.conversationID,
        ConversationHash: conversation.conversationHash,
        MessageIDs: messageIDs,
      });
      const runtimeResult = handleChatRuntimePayload(
        payload,
        text.messageAckFailed
      );
      if (runtimeResult.handled) {
        throw new Error(runtimeResult.message || text.messageAckFailed);
      }
      if (payload.code !== 0) {
        throw new Error(payload.msg || text.messageAckFailed);
      }
      if (selectedConversationRef.current?.key !== conversation.key) {
        return;
      }
      setConversationItems((previous) =>
        previous.map((item) =>
          buildConversationKey(
            item,
            normalizeParticipant(item.PeerParticipant)
          ) === conversation.key
            ? {
                ...item,
                Unread: false,
              }
            : item
        )
      );
      setThreadMessages((previous) =>
        previous.map((item) =>
          messageIDs.includes(item.messageID)
            ? {
                ...item,
                readStatus: 2,
              }
            : item
        )
      );
    },
    [handleChatRuntimePayload, postChatApi, text.messageAckFailed]
  );

  const isCurrentThreadRequest = useCallback(
    (
      requestID: number,
      conversation: ChatConversationView,
      threadIdentity: string
    ) =>
      isConferenceChatThreadRequestCurrent({
        requestID,
        latestRequestID: threadRequestIdRef.current,
        threadIdentity,
        latestThreadIdentity: threadIdentityRef.current,
        conversationKey: conversation.key,
        selectedConversationKey: selectedConversationRef.current?.key,
      }),
    []
  );

  const loadThreadMessages = useCallback(
    async (
      conversation: ChatConversationView,
      options?: { silent?: boolean }
    ) => {
      const threadIdentity = getThreadIdentity(conversation);
      // 旧轮询可能在会话切换后才开始执行；先验证真实选中项，禁止其改写请求状态。
      if (selectedConversationRef.current?.key !== conversation.key) {
        return;
      }
      const requestID = threadRequestIdRef.current + 1;
      threadRequestIdRef.current = requestID;
      threadIdentityRef.current = threadIdentity;
      if (!options?.silent) {
        // threadMessages 是页面级状态，新会话加载必须先隔离旧线程；仅同会话静默刷新允许增量合并。
        setThreadMessages([]);
        setMessageHint('');
      }
      const session = await ensureChatSession();
      if (!isCurrentThreadRequest(requestID, conversation, threadIdentity)) {
        return;
      }
      if (conversation.isDraft) {
        setThreadMessages([]);
        setMessageHint('');
        setThreadLoading(false);
        setThreadRefreshing(false);
        return;
      }
      if (options?.silent) {
        setThreadRefreshing(true);
      } else {
        setThreadLoading(true);
      }
      try {
        const conversationCacheKey = buildChatConversationCacheKey(
          session.userGuid,
          session.conferenceYear,
          conversation.conversationID,
          conversation.conversationHash
        );
        const cachedRecords = await listCachedChatMessages(
          conversationCacheKey
        ).catch(() => []);
        const cachedMap = new Map(
          cachedRecords.map((item) => [item.messageId, item])
        );
        if (
          cachedRecords.length &&
          isCurrentThreadRequest(requestID, conversation, threadIdentity)
        ) {
          const cachedMessages = cachedRecords.map((item) =>
            mapCachedRecordToView(item)
          );
          setThreadMessages((previous) =>
            options?.silent
              ? mergeChatMessages(
                  filterConferenceChatThreadMessages(
                    previous,
                    session.participant,
                    conversation.peer,
                    sameParticipant
                  ),
                  cachedMessages
                )
              : sortChatMessages(cachedMessages)
          );
        }
        const fetchedItems: ConferenceChatMessageItem[] = [];
        for (let page = 1; page <= CHAT_MESSAGE_PAGE_LIMIT; page += 1) {
          const payload = await postChatApi<ConferenceChatMessageListResp>(
            '/message/list',
            {
              ConferenceYear: session.conferenceYear,
              ConversationID: conversation.conversationID,
              ConversationHash: conversation.conversationHash,
              page,
              pageSize: CHAT_MESSAGE_PAGE_SIZE,
            }
          );
          if (
            !isCurrentThreadRequest(requestID, conversation, threadIdentity)
          ) {
            return;
          }
          const runtimeResult = handleChatRuntimePayload(
            payload,
            text.messageFailed
          );
          if (runtimeResult.handled) {
            if (runtimeResult.type === 'peer_offline') {
              applyPeerOfflineConversation(conversation);
            }
            throw new Error(runtimeResult.message || text.messageFailed);
          }
          if (payload.code !== 0) {
            throw new Error(payload.msg || text.messageFailed);
          }
          const list = payload.data?.list || [];
          fetchedItems.push(...list);
          if (list.length < CHAT_MESSAGE_PAGE_SIZE) {
            break;
          }
        }
        const deduped = new Map<string, ConferenceChatMessageItem>();
        fetchedItems.forEach((item) => {
          if (item.MessageID) {
            deduped.set(item.MessageID, item);
          }
        });
        const failedMessages: string[] = [];
        const saveRecords: CachedChatMessageRecord[] = [];
        const nextMessages = await Promise.all(
          [...deduped.values()].map(async (item) => {
            const sender =
              normalizeParticipant(item.Sender) || session.participant;
            const receiver =
              normalizeParticipant(item.Receiver) || conversation.peer;
            const fromSelf = sameParticipant(sender, session.participant);
            const cached = item.MessageID
              ? cachedMap.get(item.MessageID)
              : undefined;
            if (cached) {
              return mapCachedRecordToView(cached);
            }
            if (
              !item.MessageID ||
              !item.EncryptedMessageKey ||
              !item.Ciphertext ||
              !item.CiphertextNonce
            ) {
              failedMessages.push(item.MessageID || 'unknown');
              return {
                key:
                  item.MessageID || `${item.ClientMsgID || 'unknown'}-failed`,
                messageID: item.MessageID || item.ClientMsgID || 'unknown',
                clientMsgID: item.ClientMsgID,
                plaintext: text.messageDecryptFallback,
                fromSelf,
                sendTime: item.SendTime || '',
                readStatus: item.ReadStatus || (fromSelf ? 2 : 1),
                decryptStatus: 'failed',
                sender,
                receiver,
              } as ChatMessageView;
            }
            try {
              const plaintext = await decryptChatMessage(
                session.privateKey,
                item.EncryptedMessageKey,
                item.Ciphertext,
                item.CiphertextNonce
              );
              saveRecords.push({
                cacheKey: buildChatMessageCacheKey(
                  conversationCacheKey,
                  item.MessageID
                ),
                conversationCacheKey,
                userGuid: session.userGuid,
                conferenceYear: session.conferenceYear,
                conversationId: conversation.conversationID,
                conversationHash: conversation.conversationHash,
                messageId: item.MessageID,
                plaintext,
                sendTime: item.SendTime || '',
                senderRegion: sender.region,
                senderType: sender.type,
                senderSourceID: sender.sourceID,
                receiverRegion: receiver.region,
                receiverType: receiver.type,
                receiverSourceID: receiver.sourceID,
                fromSelf,
                updatedAt: Date.now(),
              });
              return {
                key: item.MessageID,
                messageID: item.MessageID,
                clientMsgID: item.ClientMsgID,
                plaintext,
                fromSelf,
                sendTime: item.SendTime || '',
                readStatus: item.ReadStatus || (fromSelf ? 2 : 1),
                decryptStatus: 'ready',
                sender,
                receiver,
              } as ChatMessageView;
            } catch (error) {
              console.error(
                '[ConferenceChatPage message decrypt error]',
                error
              );
              failedMessages.push(item.MessageID);
              return {
                key: item.MessageID,
                messageID: item.MessageID,
                clientMsgID: item.ClientMsgID,
                plaintext: text.messageDecryptFallback,
                fromSelf,
                sendTime: item.SendTime || '',
                readStatus: item.ReadStatus || (fromSelf ? 2 : 1),
                decryptStatus: 'failed',
                sender,
                receiver,
              } as ChatMessageView;
            }
          })
        );
        if (saveRecords.length) {
          void saveCachedChatMessages(saveRecords);
        }
        const sortedMessages = sortChatMessages(nextMessages);
        if (isCurrentThreadRequest(requestID, conversation, threadIdentity)) {
          setThreadMessages((previous) =>
            mergeChatMessages(
              options?.silent
                ? filterConferenceChatThreadMessages(
                    previous,
                    session.participant,
                    conversation.peer,
                    sameParticipant
                  )
                : [],
              sortedMessages
            )
          );
          setMessageHint(
            failedMessages.length ||
              (!sortedMessages.length && conversation.raw?.LastMessageID)
              ? text.messageWeakHint
              : ''
          );
        }
        const unreadMessageIDs = [...deduped.values()]
          .filter((item) => (item.ReadStatus || 0) === 1)
          .map((item) => item.MessageID || '')
          .filter(Boolean);
        if (unreadMessageIDs.length) {
          void ackMessages(session, conversation, unreadMessageIDs).catch(
            (error) => {
              console.error('[ConferenceChatPage ack error]', error);
            }
          );
        }
      } finally {
        // 只允许当前最新一次线程请求回收 loading，避免快速切换会话时被旧请求提前覆盖。
        if (isCurrentThreadRequest(requestID, conversation, threadIdentity)) {
          if (options?.silent) {
            setThreadRefreshing(false);
          } else {
            setThreadLoading(false);
          }
        }
      }
    },
    [
      ackMessages,
      ensureChatSession,
      applyPeerOfflineConversation,
      handleChatRuntimePayload,
      isCurrentThreadRequest,
      mapCachedRecordToView,
      postChatApi,
      text.messageDecryptFallback,
      text.messageFailed,
      text.messageWeakHint,
    ]
  );

  const refreshMessageArea = useCallback(async () => {
    try {
      await loadConversationList({ silent: true });
      if (selectedConversation) {
        await loadThreadMessages(selectedConversation, { silent: true });
      }
    } catch (error) {
      console.error('[ConferenceChatPage refresh error]', error);
      MessagePlugin.warning(text.conversationFailed);
    }
  }, [
    loadConversationList,
    loadThreadMessages,
    selectedConversation,
    text.conversationFailed,
  ]);

  const { desktopNavItems, mobileNavItems } = useMemo(() => {
    const messagesItem: ChatNavItem = {
      key: 'messages',
      label: text.messages,
      icon: <MessageMenuIcon />,
      dot: unreadConversationCount > 0,
    };
    const contactsItem: ChatNavItem = {
      key: 'contacts',
      label: text.contacts,
      icon: <ContactMenuIcon />,
    };
    const agendaItem: ChatNavItem = {
      key: 'agenda',
      label: text.agenda,
      icon: <AgendaMenuIcon />,
    };
    return {
      desktopNavItems: [contactsItem, messagesItem, agendaItem],
      mobileNavItems: [messagesItem, contactsItem, agendaItem],
    };
  }, [text.agenda, text.contacts, text.messages, unreadConversationCount]);
  const renderSidebarNavItems = (items: ChatNavItem[]) =>
    items.map((item) => (
      <button
        key={item.key}
        className={mainTab === item.key ? styles.activeNav : ''}
        onClick={() => switchMainTab(item.key)}
      >
        <span className={styles.navIconWrap}>
          {item.dot ? (
            <Badge count={1} dot>
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </span>
        {item.label}
      </button>
    ));

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // 页面入口再次校验权限，避免用户绕过导航地址直接进入会聊模块。
        const payload = await postChatApi<ChatAuthInfo>('/auth/info');
        const runtimeResult = handleChatRuntimePayload(payload, text.noAccess);
        if (runtimeResult.handled) {
          return;
        }
        if (payload.code !== 0 || !payload.data?.CanAccess) {
          MessagePlugin.warning(
            payload.data?.DenyToast || payload.msg || text.noAccess
          );
          setAuth(payload.data);
          if (payload.code === 0 && payload.data?.CanAccess === false) {
            redirectWhenNoAccess();
          }
          return;
        }
        setAuth(payload.data);
      } catch (error) {
        console.error('[ConferenceChatPage auth error]', error);
        MessagePlugin.error(text.loadAuthFailed);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [
    handleChatRuntimePayload,
    postChatApi,
    redirectWhenNoAccess,
    text.loadAuthFailed,
    text.noAccess,
  ]);

  useEffect(() => {
    if (!auth?.CanAccess || !conferenceYear) {
      setSelfProfile(undefined);
      return;
    }
    const loadProfile = async () => {
      try {
        await loadSelfProfile();
      } catch (error) {
        console.error('[ConferenceChatPage self profile error]', error);
      }
    };
    void loadProfile();
  }, [auth?.CanAccess, conferenceYear, loadSelfProfile]);

  useEffect(() => {
    if (directChatJump?.status !== 'invalid') {
      return;
    }
    resetToDefaultMainTab('目标联系人参数错误，已返回默认页面');
  }, [directChatInvalidText, directChatJump?.status, resetToDefaultMainTab]);

  useEffect(() => {
    if (directChatJump?.status !== 'pending') {
      return;
    }
    if (!auth?.CanAccess || !conferenceYear) {
      return;
    }
    if (
      isConferenceChatGuestSelf(chatAuthIdentity, {
        contactId: directChatJump.contactId,
        guestDataSource: directChatJump.guestDataSource,
      })
    ) {
      setSelected(undefined);
      setDetail(undefined);
      setSelectedConversationKey('');
      setMessageDraftPeer(undefined);
      setPreferredConversationPeer(undefined);
      setDirectChatJump(undefined);
      switchMainTab(defaultMainTab);
      clearDirectChatUrlParams(defaultMainTab);
      return;
    }
    let cancelled = false;
    const loadTargetContact = async () => {
      try {
        const payload = await postChatApi<ContactItem>('/contact/detail', {
          ConferenceYear: conferenceYear,
          ContactID: directChatJump.contactId,
          ContactType: CHAT_CONTACT_TYPE_GUEST,
          GuestDataSource: directChatJump.guestDataSource,
        });
        if (cancelled) {
          return;
        }
        const runtimeResult = handleChatRuntimePayload(
          payload,
          directChatFailedText
        );
        if (runtimeResult.handled) {
          return;
        }
        if (payload.code !== 0 || !payload.data?.ContactID) {
          resetToDefaultMainTab('目标联系人加载失败，已返回默认页面');
          return;
        }
        const targetContact: ContactItem = {
          ...payload.data,
          ContactType: CHAT_CONTACT_TYPE_GUEST,
          GuestDataSource: directChatJump.guestDataSource,
        };
        const participant = inferParticipantFromContact(targetContact);
        if (!participant) {
          resetToDefaultMainTab('目标联系人加载失败，已返回默认页面');
          return;
        }
        upsertContactSummaries([targetContact]);
        setPreferredConversationPeer(participant);
        setMessageDraftPeer(targetContact);
        setSelectedConversationKey(buildDraftConversationKey(participant));
        if (mainTab !== 'messages') {
          setMessageBootstrapping(true);
          setConversationItems([]);
          setConversationPreviewMap({});
          setThreadMessages([]);
          setMessageHint('');
          setMainTab('messages');
          setProfileMode(false);
          setMobileProfileViewMode('display');
          setSelected(undefined);
          setDetail(undefined);
          setComposerValue('');
          closeMobileContactSearch();
        }
        setDirectChatJump(undefined);
        clearDirectChatUrlParams();
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('[ConferenceChatPage direct chat error]', error);
        resetToDefaultMainTab('目标联系人加载失败，已返回默认页面');
      }
    };
    void loadTargetContact();
    return () => {
      cancelled = true;
    };
  }, [
    auth?.CanAccess,
    chatAuthIdentity,
    clearDirectChatUrlParams,
    closeMobileContactSearch,
    conferenceYear,
    defaultMainTab,
    directChatFailedText,
    directChatJump,
    handleChatRuntimePayload,
    mainTab,
    postChatApi,
    resetToDefaultMainTab,
    switchMainTab,
    upsertContactSummaries,
  ]);

  useEffect(() => {
    if (mainTab !== 'contacts') {
      contactListRequestIdRef.current += 1;
      setContactsLoading(false);
      return;
    }
    if (!conferenceYear) {
      setContactsLoading(true);
      setContactsLoaded(false);
      return;
    }
    setContactsLoaded(false);
    setContactsLoading(true);
    const requestId = contactListRequestIdRef.current + 1;
    contactListRequestIdRef.current = requestId;
    const timer = window.setTimeout(async () => {
      try {
        const payload = await postChatApi<ContactListResp>('/contact/list', {
          ConferenceYear: conferenceYear,
          ContactType: contactType,
          Keyword: '',
          page: 1,
          pageSize: contactPageSize,
        });
        if (contactListRequestIdRef.current !== requestId) {
          return;
        }
        const runtimeResult = handleChatRuntimePayload(
          payload,
          text.contactFailed
        );
        if (runtimeResult.handled) {
          return;
        }
        if (payload.code !== 0) {
          MessagePlugin.warning(payload.msg || text.contactFailed);
          return;
        }
        const list = payload.data?.list || [];
        upsertContactSummaries(list);
        setContacts(list);
        const selfContact = preferredContactID
          ? list.find((item) => item.ContactID === preferredContactID)
          : undefined;
        setSelected((previous) => {
          if (selfContact) {
            return selfContact;
          }
          if (
            preferredContactID &&
            previous?.ContactID === preferredContactID
          ) {
            return previous;
          }
          if (
            previous &&
            list.some((item) => isSameConferenceChatContact(item, previous))
          ) {
            return previous;
          }
          return undefined;
        });
        if (preferredContactID) {
          setPreferredContactID('');
        }
      } catch (error) {
        if (contactListRequestIdRef.current === requestId) {
          console.error('[ConferenceChatPage contacts error]', error);
        }
      } finally {
        if (contactListRequestIdRef.current === requestId) {
          setContactsLoading(false);
          setContactsLoaded(true);
        }
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    conferenceYear,
    contactPageSize,
    contactType,
    handleChatRuntimePayload,
    mainTab,
    postChatApi,
    preferredContactID,
    isMobileViewport,
    text.contactFailed,
    upsertContactSummaries,
  ]);

  useEffect(() => {
    if (!conferenceYear || !selectedContactID || mainTab !== 'contacts') {
      contactDetailRequestIdRef.current += 1;
      setContactDetailLoading(false);
      setDetail(undefined);
      return;
    }
    const requestId = contactDetailRequestIdRef.current + 1;
    contactDetailRequestIdRef.current = requestId;
    const loadDetail = async () => {
      setContactDetailLoading(true);
      try {
        const payload = await postChatApi<ContactItem>('/contact/detail', {
          ConferenceYear: conferenceYear,
          ContactID: selectedContactID,
          ContactType: selectedContactType,
          GuestDataSource: selectedGuestDataSource,
        });
        if (contactDetailRequestIdRef.current !== requestId) {
          return;
        }
        const runtimeResult = handleChatRuntimePayload(
          payload,
          text.detailFailed
        );
        if (runtimeResult.handled) {
          return;
        }
        if (payload.code !== 0) {
          MessagePlugin.warning(payload.msg || text.detailFailed);
          return;
        }
        if (payload.data) {
          const nextDetail = payload.data;
          upsertContactSummaries([nextDetail]);
          setContacts((previous) =>
            previous.map((item) => mergeContactListItem(item, nextDetail))
          );
          setSelected((previous) =>
            previous && isSameConferenceChatContact(previous, nextDetail)
              ? mergeContactListItem(previous, nextDetail)
              : previous
          );
        }
        setDetail(payload.data);
      } catch (error) {
        if (contactDetailRequestIdRef.current === requestId) {
          console.error('[ConferenceChatPage detail error]', error);
        }
      } finally {
        if (contactDetailRequestIdRef.current === requestId) {
          setContactDetailLoading(false);
        }
      }
    };
    void loadDetail();
  }, [
    conferenceYear,
    handleChatRuntimePayload,
    mainTab,
    postChatApi,
    selectedContactID,
    selectedContactType,
    selectedGuestDataSource,
    text.detailFailed,
    upsertContactSummaries,
  ]);

  useEffect(() => {
    if (mainTab !== 'agenda') {
      agendaRequestIdRef.current += 1;
      setAgendaLoading(false);
      return;
    }
    if (!isExplicitConferenceChatGuest(auth?.ContactType)) {
      agendaRequestIdRef.current += 1;
      setAgenda([]);
      setAgendaLoading(false);
      setAgendaLoaded(true);
      return;
    }
    if (!conferenceYear) {
      setAgendaLoading(true);
      setAgendaLoaded(false);
      return;
    }
    setAgendaLoaded(false);
    setAgendaLoading(true);
    const requestId = agendaRequestIdRef.current + 1;
    agendaRequestIdRef.current = requestId;
    const loadAgenda = async () => {
      try {
        // 嘉宾议程必须同取会聊当前届次，避免切到最新年份导致和当前大会上下文不一致。
        const payload = await postChatApi<AgendaMineResp>('/agenda/mine', {
          ConferenceYear: conferenceYear,
        });
        if (agendaRequestIdRef.current !== requestId) {
          return;
        }
        const runtimeResult = handleChatRuntimePayload(
          payload,
          text.agendaFailed
        );
        if (runtimeResult.handled) {
          return;
        }
        if (payload.code !== 0) {
          MessagePlugin.warning(payload.msg || text.agendaFailed);
          setAgenda([]);
          return;
        }
        setAgenda(
          sortAgendaItemsByStartTime(
            payload.data?.list || payload.data?.List || []
          )
        );
      } catch (error) {
        if (agendaRequestIdRef.current !== requestId) {
          return;
        }
        console.error('[ConferenceChatPage agenda error]', error);
        setAgenda([]);
      } finally {
        if (agendaRequestIdRef.current === requestId) {
          setAgendaLoading(false);
          setAgendaLoaded(true);
        }
      }
    };
    loadAgenda();
  }, [
    auth?.ContactType,
    conferenceYear,
    handleChatRuntimePayload,
    mainTab,
    postChatApi,
    text.agendaFailed,
  ]);

  useEffect(() => {
    if (!auth?.CanAccess || !isMobileViewport || mainTab !== 'agenda') {
      conferenceAgendaRequestIdRef.current += 1;
      setConferenceAgendaLoading(false);
      setConferenceAgendaLoaded(false);
      return;
    }
  }, [auth?.CanAccess, isMobileViewport, mainTab]);

  useEffect(() => {
    if (!auth?.CanAccess || !isMobileViewport || mainTab !== 'agenda') {
      return;
    }
    if (conferenceAgendaLoaded || conferenceAgendaLoading) {
      return;
    }

    setConferenceAgendaLoading(true);
    const requestId = conferenceAgendaRequestIdRef.current + 1;
    conferenceAgendaRequestIdRef.current = requestId;

    const loadConferenceAgenda = async () => {
      try {
        // 移动端新增的大会议程模块必须复用官网大会页的默认年份筛选逻辑，避免在会聊页另写一套届次判断。
        const result = await loadLatestConferenceAgendaSectionData({
          site: conferenceSite,
        });
        if (conferenceAgendaRequestIdRef.current !== requestId) {
          return;
        }
        setConferenceAgendaSection(result);
      } catch (error) {
        if (conferenceAgendaRequestIdRef.current !== requestId) {
          return;
        }
        console.error('[ConferenceChatPage conference agenda error]', error);
        setConferenceAgendaSection(undefined);
      } finally {
        if (conferenceAgendaRequestIdRef.current === requestId) {
          setConferenceAgendaLoading(false);
          setConferenceAgendaLoaded(true);
        }
      }
    };

    void loadConferenceAgenda();
  }, [
    auth?.CanAccess,
    conferenceAgendaLoaded,
    conferenceAgendaLoading,
    conferenceSite,
    isMobileViewport,
    mainTab,
  ]);

  useEffect(() => {
    if (mainTab !== 'messages') {
      return;
    }
    if (!auth?.CanAccess || !conferenceYear) {
      setMessageBootstrapping(true);
      setMessageListLoaded(false);
      return;
    }
    let mounted = true;
    setMessageListLoaded(false);
    const initMessages = async () => {
      setMessageBootstrapping(true);
      try {
        await ensureChatSession();
        await loadConversationList();
      } catch (error) {
        console.error('[ConferenceChatPage message init error]', error);
        if (mounted) {
          MessagePlugin.error(text.messageInitFailed);
        }
      } finally {
        if (mounted) {
          setMessageBootstrapping(false);
          setMessageListLoaded(true);
        }
      }
    };
    initMessages();
    return () => {
      mounted = false;
    };
  }, [
    auth?.CanAccess,
    conferenceYear,
    ensureChatSession,
    loadConversationList,
    mainTab,
    text.messageInitFailed,
  ]);

  useEffect(() => {
    if (!auth?.CanAccess || !conferenceYear || mainTab === 'messages') {
      return;
    }
    let mounted = true;
    const preloadConversationList = async () => {
      try {
        // 桌面端默认先落在联系人页；这里提前拉一次会话列表，只为拿到未读状态展示红点，不进入消息详情也不触发 ack。
        await ensureChatSession();
        await loadConversationList({ silent: true });
      } catch (error) {
        if (mounted) {
          console.error(
            '[ConferenceChatPage preload conversations error]',
            error
          );
        }
      }
    };
    void preloadConversationList();
    return () => {
      mounted = false;
    };
  }, [
    auth?.CanAccess,
    conferenceYear,
    ensureChatSession,
    loadConversationList,
    mainTab,
  ]);

  useEffect(() => {
    if (mainTab !== 'messages') {
      return;
    }
    const draftParticipant =
      preferredConversationPeer ||
      inferParticipantFromContact(messageDraftPeer);
    if (!conversationViews.length) {
      if (!draftParticipant) {
        setSelectedConversationKey('');
      }
      return;
    }
    if (draftParticipant) {
      const matched = conversationViews.find((item) =>
        sameParticipant(item.peer, draftParticipant)
      );
      if (matched) {
        if (selectedConversationKey !== matched.key) {
          setSelectedConversationKey(matched.key);
        }
        if (!matched.isDraft) {
          setPreferredConversationPeer(undefined);
          setMessageDraftPeer(undefined);
        }
        return;
      }
      const draftKey = buildDraftConversationKey(draftParticipant);
      const draftExists = conversationViews.some(
        (item) => item.key === draftKey
      );
      if (draftExists) {
        if (selectedConversationKey !== draftKey) {
          setSelectedConversationKey(draftKey);
        }
        return;
      }
    }
    const currentExists = conversationViews.some(
      (item) => item.key === selectedConversationKey
    );
    if (currentExists) {
      return;
    }
    if (isMobileViewport) {
      setSelectedConversationKey('');
      return;
    }
    setSelectedConversationKey(conversationViews[0].key);
  }, [
    conversationViews,
    isMobileViewport,
    mainTab,
    messageDraftPeer,
    preferredConversationPeer,
    selectedConversationKey,
  ]);

  useEffect(() => {
    // visibilitychange 与轮询 effect 分离，恢复可见时由依赖变化重建当前会话的轮询。
    const syncPageVisibility = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', syncPageVisibility);
    syncPageVisibility();
    return () => {
      document.removeEventListener('visibilitychange', syncPageVisibility);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    // 会话列表刷新会创建新的对象引用；ref 保留最新对象，而轮询仅由会话标识变化时重启。
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    const conversation = selectedConversationRef.current;
    if (!auth?.CanAccess || mainTab !== 'messages' || !conversation) {
      if (mainTab === 'messages') {
        setThreadMessages([]);
        setMessageHint('');
      }
      return;
    }
    if (!isPageVisible) {
      return;
    }
    if (conversation.isDraft) {
      // 草稿会话尚无 ConversationID，不能调用消息列表接口，也不应启动轮询。
      void loadThreadMessages(conversation).catch((error) => {
        console.error('[ConferenceChatPage thread load error]', error);
        MessagePlugin.warning(text.messageFailed);
      });
      return;
    }

    let disposed = false;
    const polling = createConferenceChatPolling(
      async () => {
        // 静默刷新继续复用既有解密、去重合并和已读确认逻辑。
        await loadThreadMessages(conversation, { silent: true });
      },
      CHAT_MESSAGE_POLL_INTERVAL_MS,
      (error) => {
        console.error('[ConferenceChatPage thread poll error]', error);
      }
    );

    const loadInitialThread = async () => {
      try {
        await loadThreadMessages(conversation);
      } catch (error) {
        console.error('[ConferenceChatPage thread load error]', error);
        MessagePlugin.warning(text.messageFailed);
      } finally {
        // 初次加载结束后才开始 5 秒计时，保证不会与首次查询并发。
        if (!disposed) {
          polling.start({ delayed: true });
        }
      }
    };

    void loadInitialThread();
    return () => {
      disposed = true;
      polling.stop();
    };
  }, [
    auth?.CanAccess,
    isPageVisible,
    loadThreadMessages,
    mainTab,
    selectedConversation?.conversationHash,
    selectedConversation?.conversationID,
    selectedConversation?.key,
    selectedConversation?.isDraft,
    text.messageFailed,
  ]);

  useEffect(() => {
    if (mainTab !== 'messages') {
      return;
    }
    if (!selectedConversation) {
      setMessageHint('');
      return;
    }
    if (selectedConversationIsSystemNotice) {
      setMessageHint('');
      return;
    }
    if (selectedConversationPeerOffline) {
      setMessageHint('');
      return;
    }
    if (!selectedConversationCanSend) {
      setMessageHint(
        selectedConversationDisableHint || text.messagePeerOffline
      );
      return;
    }
    setMessageHint((previous) =>
      previous === text.messagePeerOffline ? '' : previous
    );
  }, [
    mainTab,
    selectedConversation,
    selectedConversationCanSend,
    selectedConversationDisableHint,
    selectedConversationIsSystemNotice,
    selectedConversationPeerOffline,
    text.messagePeerOffline,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (
      mainTab !== 'messages' ||
      !selectedConversationForThreadKey ||
      !threadMessages.length
    ) {
      return;
    }
    scrollMessageThreadToBottom('auto');
  }, [
    mainTab,
    scrollMessageThreadToBottom,
    selectedConversationForThreadKey,
    threadLoading,
    threadMessages.length,
  ]);

  useEffect(() => {
    if (!auth?.CanAccess) {
      return;
    }
    const handleFocus = () => {
      if (mainTab === 'messages') {
        void refreshMessageArea();
        return;
      }
      // 非消息页聚焦时只刷新会话列表，让消息红点跟上远端新消息，不打断当前联系人/议程视图。
      void loadConversationList({ silent: true }).catch((error) => {
        console.error('[ConferenceChatPage conversation refresh error]', error);
      });
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [auth?.CanAccess, loadConversationList, mainTab, refreshMessageArea]);

  const openProfileEdit = async () => {
    // 资料审核中只禁止再次编辑，仍允许进入资料面板展示注销入口。
    if (!canEditConferenceChatProfile(auth?.ContactType)) {
      return;
    }
    setProfileLoading(true);
    try {
      const payload = await loadSelfProfile();
      if (!payload?.Profile) {
        MessagePlugin.warning(text.profileLoadFailed);
        return;
      }
      setProfileForm(buildProfileForm(payload.Profile, locale));
      setProfileAvatarFiles(
        buildProfileAvatarUploadFiles(
          payload.Profile.avatar || payload.Profile.avatarPath || ''
        )
      );
      if (!isMobileViewport) {
        switchMainTab('contacts');
      }
      setMobileProfileViewMode('display');
      setDesktopProfileViewMode('display');
      setProfileMode(true);
    } catch (error) {
      console.error('[ConferenceChatPage profile detail error]', error);
      MessagePlugin.error(text.profileLoadFailed);
    } finally {
      setProfileLoading(false);
    }
  };

  const updateProfileField = (
    field: keyof ConferenceChatProfileForm,
    value: string | number
  ) => {
    setProfileForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateProfileTitle = (
    index: number,
    field: 'titleCn' | 'titleEn',
    value: string
  ) => {
    setProfileForm((previous) => ({
      ...previous,
      titles: previous.titles.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const exitProfilePanel = () => {
    setProfileMode(false);
    setMobileProfileViewMode('display');
    setDesktopProfileViewMode('display');
  };

  const cancelMobileProfileEdit = () => {
    setProfileForm(
      buildProfileForm(profileDetail?.Profile || selfProfile, locale)
    );
    setProfileAvatarFiles(
      buildProfileAvatarUploadFiles(
        profileDetail?.Profile?.avatar ||
          profileDetail?.Profile?.avatarPath ||
          selfProfile?.avatar ||
          selfProfile?.avatarPath ||
          ''
      )
    );
    setMobileProfileViewMode('display');
  };

  const cancelDesktopProfileEdit = () => {
    setProfileForm(
      buildProfileForm(profileDetail?.Profile || selfProfile, locale)
    );
    setProfileAvatarFiles(
      buildProfileAvatarUploadFiles(
        profileDetail?.Profile?.avatar ||
          profileDetail?.Profile?.avatarPath ||
          selfProfile?.avatar ||
          selfProfile?.avatarPath ||
          ''
      )
    );
    setDesktopProfileViewMode('display');
  };

  const submitProfileEdit = async () => {
    const isOverseaGuest =
      profileDetail?.GuestDataSource === 'intl' ||
      Boolean(profileForm.firstName || profileForm.lastName);
    const validateMessage = validateConferenceChatProfileForm(
      profileForm,
      locale,
      isOverseaGuest
    );
    if (validateMessage) {
      MessagePlugin.warning(validateMessage);
      return;
    }

    const updatePayload = buildProfileUpdatePayload(profileForm, locale);
    const initialProfileForm = buildProfileForm(
      profileDetail?.Profile || selfProfile,
      locale
    );
    if (
      !isProfileUpdatePayloadChanged(profileForm, initialProfileForm, locale)
    ) {
      MessagePlugin.info(text.profileNoChange);
      return;
    }

    setProfileSubmitting(true);
    try {
      const payload = await postChatApi<Record<string, unknown>>(
        '/profile/update',
        updatePayload
      );
      if (payload.code !== 0) {
        MessagePlugin.warning(payload.msg || text.profileSubmitFailed);
        return;
      }
      MessagePlugin.success(text.profileSubmitSuccess);
      const nextSelfProfile = {
        ...profileForm,
        avatarPath: profileForm.avatarSubmitPath,
        avatar: profileForm.avatarPreviewUrl || profileForm.avatarSubmitPath,
        hasPendingProfileUpdate: true,
        profileUpdateReviewStatus: PROFILE_UPDATE_REVIEW_STATUS_PENDING,
      };
      setSelfProfile(nextSelfProfile);
      setProfileDetail((previous) =>
        previous
          ? {
              ...previous,
              Profile: {
                ...previous.Profile,
                ...nextSelfProfile,
              },
            }
          : previous
      );
      if (isMobileViewport) {
        setMobileProfileViewMode('display');
        return;
      }
      setDesktopProfileViewMode('display');
    } catch (error) {
      console.error('[ConferenceChatPage profile submit error]', error);
      MessagePlugin.error(text.profileSubmitFailed);
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleProfileEditSubmit = (
    event?:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    void submitProfileEdit();
  };

  const uploadAvatarRequestMethod = useCallback(
    async (file: UploadFile | UploadFile[]) => {
      const currentFile = Array.isArray(file) ? file[0] : file;

      if (!currentFile?.raw) {
        return {
          status: 'fail' as const,
          error:
            locale === 'en'
              ? AVATAR_UPLOAD_ERROR_MESSAGE_EN
              : AVATAR_UPLOAD_ERROR_MESSAGE_ZH,
          response: {},
        };
      }

      try {
        if (locale !== 'en') {
          const uploadData = await uploadDomesticAvatar(
            currentFile,
            profileDetail?.ContactType || auth?.ContactType
          );
          return {
            status: 'success' as const,
            response: {
              url: uploadData.url || uploadData.path,
              value: uploadData.path || uploadData.url,
              path: uploadData.path,
              name: currentFile.name,
              size: currentFile.size,
            },
          };
        }

        const contentType = currentFile.type || 'application/octet-stream';
        const urlencoded = new URLSearchParams();
        urlencoded.append('module', AVATAR_UPLOAD_MODULE);
        urlencoded.append(
          'filename',
          normalizeFileName(String(currentFile.name ?? 'avatar.png'))
        );
        urlencoded.append('contentType', contentType);
        // 海外上传签名接口会校验文件大小，必须传真实 size，缺失时后端会按超限处理。
        urlencoded.append('size', String(currentFile.size ?? 0));

        const itemRes = await postUploadFileEn(urlencoded);
        const result = await ClientApi.put<{ status: number }>(
          itemRes.url,
          {},
          {
            body: currentFile.raw,
            headers: {
              'Content-Type': contentType,
              'x-cos-acl': 'private',
            },
            noRedirect: true,
          }
        );

        if (result.status !== 200) {
          throw new Error('upload failed');
        }

        const [originUrl] = String(itemRes.url).split('?');
        const previewPayload = await getPreviewUrlsEn({
          paths: [itemRes.path],
        });
        const previewUrl = previewPayload?.urls?.[0] ?? originUrl;

        return {
          status: 'success' as const,
          response: {
            url: previewUrl,
            value: itemRes.path,
            path: itemRes.path,
            name: currentFile.name,
            size: currentFile.size,
          },
        };
      } catch (error) {
        return {
          status: 'fail' as const,
          error:
            locale === 'en'
              ? AVATAR_UPLOAD_ERROR_MESSAGE_EN
              : AVATAR_UPLOAD_ERROR_MESSAGE_ZH,
          response: {},
        };
      }
    },
    [auth?.ContactType, locale, profileDetail?.ContactType]
  );

  const profileAvatarUploadProps = useMemo<UploadProps>(
    () => ({
      theme: 'image',
      accept: AVATAR_ACCEPT,
      autoUpload: true,
      max: 1,
      multiple: false,
      disabled:
        profileSubmitting ||
        profileLoading ||
        (mobileProfileViewMode !== 'edit' &&
          !(!isMobileViewport && desktopProfileViewMode === 'edit')),
      allowUploadDuplicateFile: true,
      showUploadProgress: false,
      showImageFileName: false,
      files: profileAvatarFiles,
      requestMethod: uploadAvatarRequestMethod,
      beforeUpload: (file) => {
        if (
          isValidAvatarFileType(file) &&
          Number(file.size || 0) <= AVATAR_SIZE_LIMIT_BYTES
        ) {
          return true;
        }
        MessagePlugin.error(
          isValidAvatarFileType(file)
            ? locale === 'en'
              ? `Please upload a JPG/PNG image smaller than ${AVATAR_SIZE_LIMIT_MB} MB`
              : `请上传 JPG/PNG 格式图片，大小不超过 ${AVATAR_SIZE_LIMIT_MB} M`
            : locale === 'en'
            ? AVATAR_FILE_TYPE_ERROR_MESSAGE_EN
            : AVATAR_FILE_TYPE_ERROR_MESSAGE_ZH
        );
        return false;
      },
      onChange: (files) => {
        setProfileAvatarFiles(files);
        const nextAvatarUrl = getProfileAvatarUrlFromUpload(files);
        const nextAvatarSubmitPath =
          getProfileAvatarSubmitPathFromUpload(files);
        setProfileForm((previous) => ({
          ...previous,
          avatarPreviewUrl: nextAvatarUrl,
          avatarSubmitPath: nextAvatarSubmitPath,
        }));
      },
      onFail: () => {
        MessagePlugin.error(
          locale === 'en'
            ? AVATAR_UPLOAD_ERROR_MESSAGE_EN
            : AVATAR_UPLOAD_ERROR_MESSAGE_ZH
        );
      },
    }),
    [
      desktopProfileViewMode,
      isMobileViewport,
      locale,
      mobileProfileViewMode,
      profileAvatarFiles,
      profileLoading,
      profileSubmitting,
      uploadAvatarRequestMethod,
    ]
  );

  useEffect(() => {
    const currentContactType = (auth?.ContactType || '').trim().toLowerCase();
    const previousContactType = previousProfileContactTypeRef.current;
    previousProfileContactTypeRef.current = currentContactType;
    if (
      !profileMode ||
      !previousContactType ||
      previousContactType === currentContactType
    ) {
      return;
    }
    setProfileMode(false);
    setMobileProfileViewMode('display');
    setDesktopProfileViewMode('display');
    setProfileAvatarFiles([]);
    setProfileForm(buildProfileForm(undefined, locale));
  }, [auth?.ContactType, locale, profileMode]);

  const openMessageWithContact = (contact?: ContactItem) => {
    if (!contact) return;
    const participant = inferParticipantFromContact(contact);
    if (!participant) return;
    const matchedConversation = conversationViews.find((item) =>
      sameParticipant(item.peer, participant)
    );
    const nextConversationKey =
      matchedConversation?.key || buildDraftConversationKey(participant);
    if (nextConversationKey !== selectedConversationKey) {
      // 联系人入口可能在消息页内直接切换会话，必须同步阻断旧线程请求并清空旧消息。
      threadRequestIdRef.current += 1;
      threadIdentityRef.current = '';
      selectedConversationRef.current = undefined;
      setThreadMessages([]);
      setMessageHint('');
    }
    setPreferredConversationPeer(participant);
    setMessageDraftPeer(contact);
    setSelectedConversationKey(nextConversationKey);
    setComposerValue('');
    if (mainTab !== 'messages') {
      setMessageBootstrapping(true);
      setConversationItems([]);
      setConversationPreviewMap({});
      setThreadMessages([]);
      setMessageHint('');
      setMainTab('messages');
      setProfileMode(false);
      setMobileProfileViewMode('display');
      setSelected(undefined);
      setDetail(undefined);
      closeMobileContactSearch();
      return;
    }
    switchMainTab('messages');
  };

  const handleSendMessage = async () => {
    if (sendingRef.current) {
      return;
    }
    if (!selectedConversationCanSend) {
      if (selectedConversationPeerOffline) {
        setMessageHint('');
        return;
      }
      MessagePlugin.warning(
        selectedConversationDisableHint || text.messagePeerOffline
      );
      setMessageHint(
        selectedConversationDisableHint || text.messagePeerOffline
      );
      return;
    }
    const content = composerValue.trim();
    if (!content) {
      MessagePlugin.warning({
        className: styles.mobileMessageToast,
        content: text.messageEmpty,
      });
      return;
    }
    if (content.length > CHAT_MAX_MESSAGE_LENGTH) {
      MessagePlugin.warning(text.messageTooLong);
      return;
    }
    sendingRef.current = true;
    setSending(true);
    let optimisticMessageKey = '';
    try {
      const session = await ensureChatSession();
      const receiverParticipant =
        selectedConversation?.peer ||
        inferParticipantFromContact(messageDraftPeer);
      if (!receiverParticipant) {
        MessagePlugin.warning(text.messageSelectEmpty);
        return;
      }
      const clientMsgID = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      optimisticMessageKey = `optimistic-${clientMsgID}`;
      const optimisticMessage: ChatMessageView = {
        key: optimisticMessageKey,
        messageID: clientMsgID,
        clientMsgID,
        plaintext: content,
        fromSelf: true,
        sendTime: new Date().toISOString(),
        readStatus: 2,
        decryptStatus: 'sending',
        sender: session.participant,
        receiver: receiverParticipant,
      };
      setThreadMessages((previous) =>
        sortChatMessages([...previous, optimisticMessage])
      );
      setMessageHint('');
      setComposerValue('');
      window.requestAnimationFrame(() => {
        scrollMessageThreadToBottom('smooth');
      });
      const [senderPublicKey, receiverPublicKey] = await Promise.all([
        fetchPublicKey(session.participant, session),
        fetchPublicKey(receiverParticipant, session),
      ]);
      const encryptedPayload = await encryptChatMessage(
        content,
        senderPublicKey.PublicKey || '',
        receiverPublicKey.PublicKey || ''
      );
      const payload = await postChatApi<ConferenceChatMessageSendResp>(
        '/message/send',
        {
          ConferenceYear: session.conferenceYear,
          Receiver: {
            Region: receiverParticipant.region,
            Type: receiverParticipant.type,
            SourceID: receiverParticipant.sourceID,
          },
          ClientMsgID: clientMsgID,
          Ciphertext: encryptedPayload.Ciphertext,
          CiphertextNonce: encryptedPayload.CiphertextNonce,
          CiphertextAlg: CHAT_MESSAGE_ALG,
          SenderEncryptedMessageKey: encryptedPayload.SenderEncryptedMessageKey,
          ReceiverEncryptedMessageKey:
            encryptedPayload.ReceiverEncryptedMessageKey,
          MessageKeyAlg: CHAT_MESSAGE_KEY_ALG,
          ReceiverKeyVersion: receiverPublicKey.KeyVersion || 1,
        }
      );
      const runtimeResult = handleChatRuntimePayload(
        payload,
        text.messageSendFailed
      );
      if (runtimeResult.handled) {
        if (runtimeResult.type === 'peer_offline') {
          applyPeerOfflineConversation(selectedConversation);
          void loadConversationList({ silent: true }).catch(() => undefined);
          throw new Error(runtimeResult.message || text.messagePeerOffline);
        }
        throw new Error(runtimeResult.message || text.messageSendFailed);
      }
      if (payload.code !== 0 || !payload.data) {
        throw new Error(payload.msg || text.messageSendFailed);
      }
      const sentMessage: ChatMessageView = {
        key: optimisticMessageKey || payload.data.MessageID || clientMsgID,
        messageID: payload.data.MessageID || clientMsgID,
        clientMsgID,
        plaintext: content,
        fromSelf: true,
        sendTime: payload.data.SendTime || new Date().toISOString(),
        readStatus: 2,
        decryptStatus: 'ready',
        sender: session.participant,
        receiver: receiverParticipant,
      };
      setThreadMessages((previous) =>
        sortChatMessages(
          previous.some((item) => item.key === optimisticMessageKey)
            ? previous.map((item) =>
                item.key === optimisticMessageKey ? sentMessage : item
              )
            : [...previous, sentMessage]
        )
      );
      const optimisticConversationKey = buildChatConversationCacheKey(
        session.userGuid,
        session.conferenceYear,
        payload.data.ConversationID ||
          selectedConversation?.conversationID ||
          '',
        selectedConversation?.conversationHash || ''
      );
      void saveCachedChatMessages([
        {
          cacheKey: buildChatMessageCacheKey(
            optimisticConversationKey,
            sentMessage.messageID
          ),
          conversationCacheKey: optimisticConversationKey,
          userGuid: session.userGuid,
          conferenceYear: session.conferenceYear,
          conversationId:
            payload.data.ConversationID ||
            selectedConversation?.conversationID ||
            '',
          conversationHash: selectedConversation?.conversationHash || '',
          messageId: sentMessage.messageID,
          plaintext: content,
          sendTime: sentMessage.sendTime,
          senderRegion: sentMessage.sender.region,
          senderType: sentMessage.sender.type,
          senderSourceID: sentMessage.sender.sourceID,
          receiverRegion: sentMessage.receiver.region,
          receiverType: sentMessage.receiver.type,
          receiverSourceID: sentMessage.receiver.sourceID,
          fromSelf: true,
          updatedAt: Date.now(),
        },
      ]);
      try {
        // 发送成功后再静默刷新一次会话列表，把 draft 会话切换成后端落库后的正式会话。
        const list = await loadConversationList({ silent: true });
        const matched = (list || []).find(
          (item) =>
            item.ConversationID === payload.data?.ConversationID ||
            sameParticipant(
              normalizeParticipant(item.PeerParticipant),
              receiverParticipant
            )
        );
        if (matched) {
          setSelectedConversationKey(
            buildConversationKey(
              matched,
              normalizeParticipant(matched.PeerParticipant)
            )
          );
          setMessageDraftPeer(undefined);
        }
      } catch (refreshError) {
        // 会话刷新失败不应回滚“发送成功”的结果，避免用户误以为消息未发出而重复发送。
        console.error(
          '[ConferenceChatPage send refresh conversation error]',
          refreshError
        );
      }
    } catch (error) {
      if (optimisticMessageKey) {
        setThreadMessages((previous) =>
          previous.filter((item) => item.key !== optimisticMessageKey)
        );
      }
      console.error('[ConferenceChatPage send error]', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === text.messagePeerOffline) {
        MessagePlugin.warning(text.messagePeerOffline);
      } else if (errorMessage === text.messageSelfContactNotAllowed) {
        MessagePlugin.warning(text.messageSelfContactNotAllowed);
      } else if (errorMessage === text.messageContactNotAllowed) {
        MessagePlugin.warning(text.messageContactNotAllowed);
      } else if (errorMessage === text.messageAccessRevoked) {
        return;
      } else {
        MessagePlugin.error(errorMessage || text.messageSendFailed);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const renderAgendaItems = (
    items: AgendaItem[],
    mode: AgendaCardMode = 'detail'
  ) =>
    items.map((item, index) => (
      <ConferenceChatAgendaCard
        key={
          item.AgendaID ||
          item.AgendaItemID ||
          item.agendaId ||
          item.agendaItemId ||
          index
        }
        item={item}
        locale={locale}
        mode={mode}
      />
    ));

  const renderEmbeddedDesktopAgenda = () => {
    const agendaSections = buildGuestAgendaSections(agenda, text);

    if (agendaLoading) {
      return renderAgendaSectionSkeleton('embeddedDesktop');
    }

    if (!agendaLoaded) {
      return renderAgendaSectionSkeleton('embeddedDesktop');
    }

    return (
      <div className={styles.agendaSectionList}>
        {agendaSections.map((section) => (
          <section key={section.key} className={styles.agendaSection}>
            <h3>{section.title}</h3>
            <div className={styles.detailAgendaList}>
              {section.items.length ? (
                renderAgendaItems(section.items)
              ) : (
                <div className={styles.empty}>{text.emptyAgenda}</div>
              )}
            </div>
          </section>
        ))}
      </div>
    );
  };

  const renderMobileConferenceAgenda = () => {
    if (!auth?.CanAccess || !isMobileViewport || mainTab !== 'agenda') {
      return null;
    }

    if (conferenceAgendaLoading || !conferenceAgendaLoaded) {
      return (
        <div className={styles.mobileConferenceAgendaWrap}>
          <ConferenceChatConferenceAgendaSection
            loading
            title={text.conferenceAgenda}
            site={conferenceSite}
          />
        </div>
      );
    }

    return (
      <div className={styles.mobileConferenceAgendaWrap}>
        <ConferenceChatConferenceAgendaSection
          data={conferenceAgendaSection?.data}
          title={text.conferenceAgenda}
          site={conferenceSite}
        />
      </div>
    );
  };

  const renderMobileAgenda = () => {
    // 主议程 Tab 仅展示当前嘉宾身份关联议程，不复用联系人详情里的“嘉宾议程 / 报名议程”分组。
    const mobileAgendaSections = buildGuestAgendaSections(agenda, text);
    const showGuestAgendaSkeleton = agendaLoading || !agendaLoaded;

    return (
      <div className={styles.mobileAgendaPanel}>
        {showGuestAgendaSkeleton
          ? buildGuestAgendaSections([], text).map((section) => (
              <section key={section.key} className={styles.mobileAgendaSection}>
                <h3>{section.title}</h3>
                {renderAgendaCardSkeletons(1, {
                  className: styles.mobileAgendaCardList,
                  mode: 'simple',
                  mobile: true,
                })}
              </section>
            ))
          : mobileAgendaSections.map((section) => (
              <section key={section.key} className={styles.mobileAgendaSection}>
                <h3>{section.title}</h3>
                <div className={styles.mobileAgendaCardList}>
                  {section.items.length ? (
                    renderAgendaItems(section.items)
                  ) : (
                    <div className={styles.empty}>{text.emptyAgenda}</div>
                  )}
                </div>
              </section>
            ))}
        {renderMobileConferenceAgenda()}
      </div>
    );
  };

  const renderProfileEdit = () => {
    const isOverseaGuest =
      profileDetail?.GuestDataSource === 'intl' ||
      Boolean(profileForm.firstName || profileForm.lastName);
    const isMobileProfilePanel = isMobileViewport;
    const isMobileProfileEdit =
      isMobileProfilePanel && mobileProfileViewMode === 'edit';
    const isDesktopProfileEditing =
      !isMobileProfilePanel && desktopProfileViewMode === 'edit';
    const isProfileEditing = isMobileProfileEdit || isDesktopProfileEditing;
    const isEnProfilePanel = locale === 'en';
    const isEnDesktopProfilePanel = isEnProfilePanel && !isMobileProfilePanel;
    const isEnProfileDisplay = isEnProfilePanel && !isProfileEditing;
    const isProfileReviewBlocked =
      isProfileDTOUpdateBlocked(profileDetail?.Profile) ||
      isProfileDTOUpdateBlocked(selfProfile);
    const enDesktopProfileText = {
      panelTitle: 'Personal Details',
      editButtonText: 'Edit Information',
      exitButtonText: 'Exit',
      submitButtonText: 'Submit Information',
      avatarLabel: 'Profile Picture',
      avatarHint: 'Please upload a clear, recent headshot. Max file size: 2MB.',
      nameCnLabel: 'Chinese Name',
      primaryTitleLabel: 'Primary Title',
      additionalTitleLabel: 'Additional Title (optional)',
      primaryTitleEnPlaceholder: 'Primary Title in English',
      primaryTitleCnPlaceholder: 'Primary Title in Chinese',
      additionalTitleEnPlaceholder: 'Additional Title in English (optional)',
      additionalTitleCnPlaceholder: 'Additional Title in Chinese (optional)',
      titleAddButtonText: 'Add Title',
      titleMaxMessage: 'Maximum of 3 titles allowed',
      nationalityLabel: 'Country/Region',
      fieldPlaceholder: 'Please enter content',
      introLabel: 'Personal Bio',
      allowContactLabel:
        'Allow contact via platform messages (Receive in-site notifications, connection requests, messages, and other information about the summit)',
      bioEnPlaceholder:
        'Please insert your personal bio in English. (Max 600 characters)',
      bioCnPlaceholder:
        'Please provide a Chinese version of your bio. AI-generated translations are welcome. (Max 600 characters)',
    };
    const profileSectionTitle = isEnProfilePanel
      ? enDesktopProfileText.panelTitle
      : text.profileBaseInfoTitle;
    const profileNameRows = isOverseaGuest
      ? [
          {
            key: 'first-name',
            label: text.profileFirstName,
            value: profileForm.firstName.trim() || '-',
            required: true,
          },
          {
            key: 'last-name',
            label: text.profileLastName,
            value: profileForm.lastName.trim() || '-',
            required: true,
          },
          {
            key: 'name-cn',
            label: text.profileNameCN,
            value: profileForm.nameCn.trim() || '-',
          },
        ]
      : [
          {
            key: 'name-cn',
            label: '姓名',
            value: profileForm.nameCn.trim() || '-',
            required: true,
          },
          {
            key: 'name-en',
            label: text.profileNameEN,
            value: profileForm.nameEn.trim() || '-',
            required: true,
          },
        ];
    const enDesktopTitleRows = ensureProfileTitleCount(
      profileForm.titles,
      EN_PROFILE_BASE_TITLE_COUNT
    ).map((item, index) => ({
      key: `title-${index}`,
      label:
        index === 0
          ? enDesktopProfileText.primaryTitleLabel
          : enDesktopProfileText.additionalTitleLabel,
      titleEn: item.titleEn,
      titleCn: item.titleCn,
      required: index === 0,
      removable: index >= EN_PROFILE_BASE_TITLE_COUNT,
    }));
    const profileTitleRows = profileForm.titles
      .map((item, index) => ({
        key: `title-${index}`,
        label:
          profileForm.titles.length > 1
            ? `${text.profileTitle}${index + 1}`
            : text.profileTitle,
        value: item.titleCn.trim() || '-',
        extraValue: item.titleEn.trim() || '-',
        required: index === 0,
      }))
      .filter((item) => item.value !== '-' || item.extraValue !== '-');
    const desktopDisplayNameRows = [...profileNameRows];
    const desktopDisplayContactRows = [
      {
        key: 'phone',
        label: isEnProfilePanel
          ? enDesktopProfileText.nationalityLabel
          : text.profilePhone,
        value: isEnProfilePanel
          ? profileForm.nationality.trim() || '-'
          : profileForm.phone.trim() || '-',
        required: !isEnProfilePanel,
      },
      {
        key: 'email',
        label: text.profileEmail,
        value: profileForm.email.trim() || '-',
        required: isOverseaGuest,
      },
    ];
    const contactPermissionOptions = [
      {
        label: text.profileAllow,
        value: 1,
      },
      {
        label: text.profileReject,
        value: 0,
      },
    ];
    return (
      <div
        className={`${styles.profileEditPanel} ${
          isMobileProfileEdit ? styles.mobileProfileEditPanel : ''
        }`.trim()}
      >
        {!isMobileProfilePanel && (
          <div className={styles.profileEditHeader}>
            <h2>{profileSectionTitle}</h2>
            <div className={styles.profileHeaderActions}>
              {isEnDesktopProfilePanel && !isDesktopProfileEditing ? (
                <AccountDeletionDangerEntry
                  locale="en"
                  onClick={() => setAccountDeletionOpen(true)}
                />
              ) : null}
              {isProfileReviewBlocked ? (
                <button
                  type="button"
                  className={styles.profileEditTriggerDisabled}
                  disabled
                >
                  <span
                    className={styles.profilePendingIcon}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 3H9.5V4H2.5V3Z" fill="currentColor" />
                      <path d="M7 5.5H2.5V6.5H7V5.5Z" fill="currentColor" />
                      <path
                        d="M1 0C0.447715 0 0 0.447715 0 0.999999V11C0 11.5523 0.447715 12 0.999999 12H11C11.5523 12 12 11.5523 12 11V1C12 0.447715 11.5523 0 11 0H1ZM11 1V11H0.999999L1 1H11Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span>{text.profileUpdateReviewing}</span>
                </button>
              ) : isDesktopProfileEditing ? (
                <>
                  <Button
                    variant="outline"
                    type="button"
                    disabled={profileSubmitting}
                    onClick={cancelDesktopProfileEdit}
                  >
                    {isEnDesktopProfilePanel
                      ? enDesktopProfileText.exitButtonText
                      : text.profileExit}
                  </Button>
                  <Button
                    theme="primary"
                    type="submit"
                    form="conference-chat-profile-form"
                    loading={profileSubmitting}
                    icon={
                      isEnDesktopProfilePanel ? (
                        <SaveIcon size={16} />
                      ) : undefined
                    }
                  >
                    {isEnDesktopProfilePanel
                      ? enDesktopProfileText.submitButtonText
                      : text.profileSubmit}
                  </Button>
                </>
              ) : (
                <button
                  type="button"
                  className={styles.profileEditTrigger}
                  disabled={profileSubmitting || profileLoading}
                  onClick={() => setDesktopProfileViewMode('edit')}
                >
                  <Edit1Icon size={16} />
                  <span>
                    {isEnDesktopProfilePanel
                      ? enDesktopProfileText.editButtonText
                      : text.editInfo}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
        {profileLoading ? (
          <div className={styles.profileEditSkeleton}>
            <Skeleton
              animation="gradient"
              rowCol={[
                [{ width: '100%', height: '44px' }],
                [
                  {
                    width: 'calc(50% - 8px)',
                    height: '44px',
                    marginRight: '16px',
                  },
                  { width: 'calc(50% - 8px)', height: '44px' },
                ],
                [
                  {
                    width: 'calc(50% - 8px)',
                    height: '44px',
                    marginRight: '16px',
                  },
                  { width: 'calc(50% - 8px)', height: '44px' },
                ],
                [{ width: '100%', height: '120px' }],
              ]}
            />
          </div>
        ) : (
          <form
            id="conference-chat-profile-form"
            className={`${styles.profileForm} ${
              isMobileProfilePanel ? styles.mobileProfileForm : ''
            } ${!isProfileEditing ? styles.profileFormDisplay : ''}`.trim()}
            onSubmit={handleProfileEditSubmit}
          >
            <div className={styles.profileAvatarSection}>
              <div className={styles.profileFieldLabelRow}>
                <ProfileFieldLabel
                  text={
                    isEnProfilePanel
                      ? enDesktopProfileText.avatarLabel
                      : text.profileAvatar
                  }
                  required
                />
              </div>
              {isEnProfilePanel || isProfileEditing ? (
                <>
                  <div className={styles.profileAvatarUploaderFrame}>
                    <Upload
                      className={styles.profileAvatarUploader}
                      {...profileAvatarUploadProps}
                    />
                  </div>
                  <p className={styles.profileAvatarHint}>
                    {isEnProfilePanel
                      ? enDesktopProfileText.avatarHint
                      : text.profileAvatarHint}
                  </p>
                </>
              ) : (
                <div className={styles.profileAvatarDisplay}>
                  <img
                    src={profileForm.avatarPreviewUrl || auth?.Avatar}
                    alt={getContactName(locale, auth)}
                    className={styles.profileAvatarDisplayImage}
                  />
                </div>
              )}
            </div>
            {isEnProfilePanel ? (
              <>
                <div
                  className={`${styles.profileFormGrid} ${styles.profileFormGridTriple}`}
                >
                  <label>
                    <ProfileFieldLabel text={text.profileFirstName} required />
                    <Input
                      value={profileForm.firstName}
                      maxlength={50}
                      allowInputOverMax
                      placeholder={enDesktopProfileText.fieldPlaceholder}
                      disabled={isEnProfileDisplay}
                      onChange={(value) =>
                        updateProfileField('firstName', value)
                      }
                    />
                  </label>
                  <label>
                    <ProfileFieldLabel text={text.profileLastName} required />
                    <Input
                      value={profileForm.lastName}
                      maxlength={50}
                      allowInputOverMax
                      placeholder={enDesktopProfileText.fieldPlaceholder}
                      disabled={isEnProfileDisplay}
                      onChange={(value) =>
                        updateProfileField('lastName', value)
                      }
                    />
                  </label>
                  <label>
                    <ProfileFieldLabel
                      text={enDesktopProfileText.nameCnLabel}
                    />
                    <Input
                      value={profileForm.nameCn}
                      maxlength={20}
                      allowInputOverMax
                      placeholder={enDesktopProfileText.fieldPlaceholder}
                      disabled={isEnProfileDisplay}
                      onChange={(value) => updateProfileField('nameCn', value)}
                    />
                  </label>
                </div>
                <div className={styles.profileTitleList}>
                  {enDesktopTitleRows.map((item, index) => (
                    <div className={styles.profileTitleItem} key={item.key}>
                      <div className={styles.profileTitleGroup}>
                        <div className={styles.profileFieldLabelRow}>
                          <ProfileFieldLabel
                            text={item.label}
                            required={item.required}
                          />
                          {item.removable && !isEnProfileDisplay ? (
                            <button
                              type="button"
                              className={styles.profileInlineDeleteButton}
                              aria-label={text.profileRemoveTitle}
                              onClick={() =>
                                setProfileForm((previous) => ({
                                  ...previous,
                                  titles: previous.titles.filter(
                                    (_, itemIndex) => itemIndex !== index
                                  ),
                                }))
                              }
                            >
                              <DeleteIcon size={16} />
                            </button>
                          ) : null}
                        </div>
                        <div className={styles.profileFormGrid}>
                          <label>
                            <Input
                              value={item.titleEn}
                              maxlength={100}
                              allowInputOverMax
                              placeholder={
                                index === 0
                                  ? enDesktopProfileText.primaryTitleEnPlaceholder
                                  : enDesktopProfileText.additionalTitleEnPlaceholder
                              }
                              disabled={isEnProfileDisplay}
                              onChange={(value) =>
                                updateProfileTitle(index, 'titleEn', value)
                              }
                            />
                          </label>
                          <label>
                            <Input
                              value={item.titleCn}
                              maxlength={50}
                              allowInputOverMax
                              placeholder={
                                index === 0
                                  ? enDesktopProfileText.primaryTitleCnPlaceholder
                                  : enDesktopProfileText.additionalTitleCnPlaceholder
                              }
                              disabled={isEnProfileDisplay}
                              onChange={(value) =>
                                updateProfileTitle(index, 'titleCn', value)
                              }
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!isEnProfileDisplay ? (
                    <div className={styles.profileAddRow}>
                      {profileForm.titles.length < PROFILE_TITLE_MAX_COUNT ? (
                        <button
                          type="button"
                          className={styles.profileAddButton}
                          onClick={() =>
                            setProfileForm((previous) => ({
                              ...previous,
                              titles: [
                                ...previous.titles,
                                createEmptyProfileTitle(),
                              ],
                            }))
                          }
                        >
                          <AddIcon size={16} />
                          <span>{enDesktopProfileText.titleAddButtonText}</span>
                        </button>
                      ) : null}
                      <span className={styles.profileAddHint}>
                        {enDesktopProfileText.titleMaxMessage}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className={styles.profileFormGrid}>
                  <label>
                    <ProfileFieldLabel
                      text={enDesktopProfileText.nationalityLabel}
                    />
                    <Input
                      value={profileForm.nationality}
                      disabled={isEnProfileDisplay}
                      placeholder={enDesktopProfileText.fieldPlaceholder}
                      onChange={(value) =>
                        updateProfileField('nationality', value)
                      }
                    />
                  </label>
                  <label>
                    <ProfileFieldLabel text={text.profileEmail} required />
                    <Input
                      value={profileForm.email}
                      maxlength={200}
                      allowInputOverMax
                      placeholder={enDesktopProfileText.fieldPlaceholder}
                      disabled={isEnProfileDisplay}
                      onChange={(value) => updateProfileField('email', value)}
                    />
                  </label>
                </div>
                <div className={styles.profileBioSection}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel
                      text={enDesktopProfileText.introLabel}
                      required
                    />
                  </div>
                  <div className={styles.profileBioFieldList}>
                    <label>
                      <Textarea
                        value={profileForm.bioEn}
                        maxlength={600}
                        allowInputOverMax
                        placeholder={enDesktopProfileText.bioEnPlaceholder}
                        disabled={isEnProfileDisplay}
                        onChange={(value) => updateProfileField('bioEn', value)}
                      />
                    </label>
                    <label>
                      <Textarea
                        value={profileForm.bioCn}
                        maxlength={600}
                        allowInputOverMax
                        placeholder={enDesktopProfileText.bioCnPlaceholder}
                        disabled={isEnProfileDisplay}
                        onChange={(value) => updateProfileField('bioCn', value)}
                      />
                    </label>
                  </div>
                </div>
                <div className={styles.profileRadioGroup}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel
                      text={enDesktopProfileText.allowContactLabel}
                      required
                    />
                  </div>
                  <RadioGroup
                    className={
                      isEnProfileDisplay
                        ? styles.profileDisplayRadioGroup
                        : styles.profileRadioGroupControl
                    }
                    options={contactPermissionOptions}
                    value={profileForm.allowContact}
                    disabled={isEnProfileDisplay}
                    onChange={(value) =>
                      updateProfileField('allowContact', Number(value))
                    }
                  />
                </div>
              </>
            ) : isProfileEditing ? (
              <>
                {isOverseaGuest ? (
                  <div className={styles.profileFormGrid}>
                    <label>
                      <ProfileFieldLabel
                        text={text.profileFirstName}
                        required
                      />
                      <Input
                        value={profileForm.firstName}
                        maxlength={50}
                        allowInputOverMax
                        placeholder="请输入内容"
                        onChange={(value) =>
                          updateProfileField('firstName', value)
                        }
                      />
                    </label>
                    <label>
                      <ProfileFieldLabel text={text.profileLastName} required />
                      <Input
                        value={profileForm.lastName}
                        maxlength={50}
                        allowInputOverMax
                        placeholder="请输入内容"
                        onChange={(value) =>
                          updateProfileField('lastName', value)
                        }
                      />
                    </label>
                  </div>
                ) : (
                  <div className={styles.profileFormGrid}>
                    <label>
                      <ProfileFieldLabel text="姓名" required />
                      <Input
                        value={profileForm.nameCn}
                        maxlength={20}
                        allowInputOverMax
                        placeholder="请输入内容"
                        onChange={(value) =>
                          updateProfileField('nameCn', value)
                        }
                      />
                    </label>
                    <label>
                      <ProfileFieldLabel text={text.profileNameEN} required />
                      <Input
                        value={profileForm.nameEn}
                        maxlength={50}
                        allowInputOverMax
                        placeholder="请输入内容"
                        onChange={(value) =>
                          updateProfileField('nameEn', value)
                        }
                      />
                    </label>
                  </div>
                )}
                {isOverseaGuest && (
                  <label>
                    <ProfileFieldLabel text="中文姓名" />
                    <Input
                      value={profileForm.nameCn}
                      maxlength={20}
                      allowInputOverMax
                      placeholder="请输入内容"
                      onChange={(value) => updateProfileField('nameCn', value)}
                    />
                  </label>
                )}
                <div className={styles.profileTitleList}>
                  {profileForm.titles.map((item, index) => (
                    <div className={styles.profileTitleItem} key={index}>
                      <div className={styles.profileTitleGroup}>
                        <div className={styles.profileFieldLabelRow}>
                          <ProfileFieldLabel
                            text={
                              profileForm.titles.length > 1
                                ? `${text.profileTitle}${index + 1}`
                                : text.profileTitle
                            }
                            required={index === 0}
                          />
                          {profileForm.titles.length > 1 ? (
                            <button
                              type="button"
                              className={styles.profileInlineDeleteButton}
                              aria-label={text.profileRemoveTitle}
                              onClick={() =>
                                setProfileForm((previous) => ({
                                  ...previous,
                                  titles: previous.titles.filter(
                                    (_, itemIndex) => itemIndex !== index
                                  ),
                                }))
                              }
                            >
                              <DeleteIcon size={16} />
                            </button>
                          ) : null}
                        </div>
                        <div className={styles.profileFormGrid}>
                          <label>
                            <Input
                              value={item.titleCn}
                              maxlength={50}
                              allowInputOverMax
                              placeholder="请输入中文内容"
                              onChange={(value) =>
                                updateProfileTitle(index, 'titleCn', value)
                              }
                            />
                          </label>
                          <label>
                            <Input
                              value={item.titleEn}
                              maxlength={100}
                              allowInputOverMax
                              placeholder="请输入英文内容"
                              onChange={(value) =>
                                updateProfileTitle(index, 'titleEn', value)
                              }
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  {profileForm.titles.length < PROFILE_TITLE_MAX_COUNT && (
                    <button
                      type="button"
                      className={styles.profileAddButton}
                      onClick={() =>
                        setProfileForm((previous) => ({
                          ...previous,
                          titles: [
                            ...previous.titles,
                            createEmptyProfileTitle(),
                          ],
                        }))
                      }
                    >
                      <AddIcon size={16} />
                      <span>{text.profileAddTitle}</span>
                    </button>
                  )}
                </div>
                <div className={styles.profileFormGrid}>
                  <label>
                    <ProfileFieldLabel text={text.profilePhone} required />
                    <Input value={profileForm.phone} disabled />
                  </label>
                  <label>
                    <ProfileFieldLabel
                      text={text.profileEmail}
                      required={isOverseaGuest}
                    />
                    <Input
                      value={profileForm.email}
                      maxlength={200}
                      allowInputOverMax
                      placeholder="请输入邮箱地址"
                      onChange={(value) => updateProfileField('email', value)}
                    />
                  </label>
                </div>
                <div className={styles.profileBioSection}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel text={text.profileIntro} required />
                  </div>
                  <div className={styles.profileBioFieldList}>
                    <label>
                      <Textarea
                        value={profileForm.bioCn}
                        maxlength={600}
                        allowInputOverMax
                        placeholder="请输入中文内容"
                        onChange={(value) => updateProfileField('bioCn', value)}
                      />
                    </label>
                    <label>
                      <Textarea
                        value={profileForm.bioEn}
                        maxlength={600}
                        allowInputOverMax
                        placeholder="请输入英文内容"
                        onChange={(value) => updateProfileField('bioEn', value)}
                      />
                    </label>
                  </div>
                </div>
                <div className={styles.profileRadioGroup}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel
                      text={text.profileAllowContact}
                      required
                    />
                  </div>
                  <RadioGroup
                    className={styles.profileRadioGroupControl}
                    options={contactPermissionOptions}
                    value={profileForm.allowContact}
                    onChange={(value) =>
                      updateProfileField('allowContact', Number(value))
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <div className={styles.profileDisplayGrid}>
                  {desktopDisplayNameRows.map((item) => (
                    <div className={styles.profileDisplayItem} key={item.key}>
                      <span className={styles.profileDisplayLabel}>
                        <ProfileFieldLabel
                          text={item.label}
                          required={Boolean(item.required)}
                        />
                      </span>
                      <span className={styles.profileDisplayValue}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
                {profileTitleRows.length > 0 && (
                  <div className={styles.profileTitleList}>
                    {profileTitleRows.map((item) => (
                      <div className={styles.profileTitleItem} key={item.key}>
                        <div className={styles.profileTitleGroup}>
                          <div className={styles.profileFieldLabelRow}>
                            <ProfileFieldLabel
                              text={item.label}
                              required={item.required}
                            />
                          </div>
                          <div className={styles.profileDisplayGrid}>
                            <div className={styles.profileDisplayItem}>
                              <span className={styles.profileDisplayValue}>
                                {item.value}
                              </span>
                            </div>
                            <div className={styles.profileDisplayItem}>
                              <span className={styles.profileDisplayValue}>
                                {item.extraValue}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.profileDisplayGrid}>
                  {desktopDisplayContactRows.map((item) => (
                    <div className={styles.profileDisplayItem} key={item.key}>
                      <span className={styles.profileDisplayLabel}>
                        <ProfileFieldLabel
                          text={item.label}
                          required={Boolean(item.required)}
                        />
                      </span>
                      <span className={styles.profileDisplayValue}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={styles.profileDisplayIntroSection}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel text={text.profileIntro} required />
                  </div>
                  <div className={styles.profileDisplayIntroContent}>
                    <p>{profileForm.bioCn.trim() || '-'}</p>
                    {profileForm.bioEn.trim() ? (
                      <p>{profileForm.bioEn.trim()}</p>
                    ) : null}
                  </div>
                </div>
                <div className={styles.profileDisplayContactSection}>
                  <div className={styles.profileFieldLabelRow}>
                    <ProfileFieldLabel
                      text={text.profileAllowContact}
                      required
                    />
                  </div>
                  <RadioGroup
                    className={styles.profileDisplayRadioGroup}
                    options={contactPermissionOptions}
                    value={profileForm.allowContact}
                    disabled
                  />
                </div>
              </>
            )}
          </form>
        )}
      </div>
    );
  };

  const renderMobileProfilePanel = () => {
    const meetingMeta = getMobileProfileMeetingMeta(locale, auth?.ContactType);
    const canEdit = canEditConferenceChatProfile(auth?.ContactType);
    const showBlockedState =
      Boolean(detail && isProfileUpdateBlocked(detail)) ||
      isProfileDTOUpdateBlocked(profileDetail?.Profile);
    const isEnglishMobileProfile = locale === 'en';

    if (mobileProfileViewMode === 'edit') {
      return (
        <div className={styles.mobileProfileOverlay}>
          <img
            src={MOBILE_PROFILE_HERO_BG_SRC}
            alt=""
            aria-hidden="true"
            className={styles.mobileProfileHeroBg}
          />
          <img
            src={MOBILE_PROFILE_HERO_OVERLAY_SRC}
            alt=""
            aria-hidden="true"
            className={styles.mobileProfileHeroOverlay}
          />
          <div className={styles.mobileProfileScroll}>
            <div className={styles.mobileProfileTopBar}>
              <button
                type="button"
                className={styles.mobileProfileBack}
                onClick={cancelMobileProfileEdit}
                aria-label={text.profileBack}
              >
                <BackChevronIcon />
              </button>
            </div>
            <section
              className={`${styles.mobileProfileHeroSection} ${
                isEnglishMobileProfile ? styles.mobileProfileHeroSectionEn : ''
              }`.trim()}
            >
              <div
                className={`${styles.mobileProfileHeroContent} ${
                  isEnglishMobileProfile
                    ? styles.mobileProfileHeroContentEn
                    : ''
                }`.trim()}
              >
                <div
                  className={`${styles.mobileProfileHeroTitleBlock} ${
                    isEnglishMobileProfile
                      ? styles.mobileProfileHeroTitleBlockEn
                      : ''
                  }`.trim()}
                >
                  <h1>{meetingMeta.heroTitle}</h1>
                  <p>{meetingMeta.heroSubtitle}</p>
                </div>
                <div
                  className={`${styles.mobileProfileHeroMeta} ${
                    isEnglishMobileProfile ? styles.mobileProfileHeroMetaEn : ''
                  }`.trim()}
                >
                  <div
                    className={`${styles.mobileProfileHeroMetaItem} ${
                      isEnglishMobileProfile
                        ? styles.mobileProfileHeroMetaItemEn
                        : ''
                    }`.trim()}
                  >
                    <AgendaTimeIcon />
                    <span>{meetingMeta.meetingDateText}</span>
                  </div>
                  <div
                    className={`${styles.mobileProfileHeroMetaItem} ${
                      isEnglishMobileProfile
                        ? styles.mobileProfileHeroMetaItemEn
                        : ''
                    }`.trim()}
                  >
                    <AgendaLocationIcon />
                    <span>
                      {meetingMeta.meetingLocationText}
                      {meetingMeta.meetingLocationSubline ? (
                        <>
                          <br />
                          {meetingMeta.meetingLocationSubline}
                        </>
                      ) : null}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <div className={styles.mobileProfileContentCard}>
              <section className={styles.mobileProfileSummarySection}>
                <h2>{meetingMeta.meetingTitle}</h2>
                <p>{meetingMeta.meetingDescription}</p>
              </section>
              <section className={styles.mobileProfileInfoSection}>
                <div className={styles.mobileProfileSectionTitle}>
                  <span className={styles.mobileProfileSectionMarker} />
                  <strong>{text.profileEditTitle}</strong>
                </div>
                {renderProfileEdit()}
              </section>
            </div>
          </div>
          <div className={styles.mobileProfileEditFooter}>
            <button
              type="button"
              className={styles.mobileProfilePrimaryButton}
              disabled={profileSubmitting}
              onClick={handleProfileEditSubmit}
            >
              {text.profileDisplaySubmit}
            </button>
            <button
              type="button"
              className={styles.mobileProfileSecondaryButton}
              disabled={profileSubmitting}
              onClick={cancelMobileProfileEdit}
            >
              {text.profileBack}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.mobileProfileOverlay}>
        <img
          src={MOBILE_PROFILE_HERO_BG_SRC}
          alt=""
          aria-hidden="true"
          className={styles.mobileProfileHeroBg}
        />
        <img
          src={MOBILE_PROFILE_HERO_OVERLAY_SRC}
          alt=""
          aria-hidden="true"
          className={styles.mobileProfileHeroOverlay}
        />
        <div className={styles.mobileProfileScroll}>
          <div className={styles.mobileProfileTopBar}>
            <button
              type="button"
              className={styles.mobileProfileBack}
              onClick={exitProfilePanel}
              aria-label={text.profileDisplayClose}
            >
              <BackChevronIcon />
            </button>
          </div>
          <section
            className={`${styles.mobileProfileHeroSection} ${
              isEnglishMobileProfile ? styles.mobileProfileHeroSectionEn : ''
            }`.trim()}
          >
            <div
              className={`${styles.mobileProfileHeroContent} ${
                isEnglishMobileProfile ? styles.mobileProfileHeroContentEn : ''
              }`.trim()}
            >
              <div
                className={`${styles.mobileProfileHeroTitleBlock} ${
                  isEnglishMobileProfile
                    ? styles.mobileProfileHeroTitleBlockEn
                    : ''
                }`.trim()}
              >
                <h1>{meetingMeta.heroTitle}</h1>
                <p>{meetingMeta.heroSubtitle}</p>
              </div>
              <div
                className={`${styles.mobileProfileHeroMeta} ${
                  isEnglishMobileProfile ? styles.mobileProfileHeroMetaEn : ''
                }`.trim()}
              >
                <div
                  className={`${styles.mobileProfileHeroMetaItem} ${
                    isEnglishMobileProfile
                      ? styles.mobileProfileHeroMetaItemEn
                      : ''
                  }`.trim()}
                >
                  <AgendaTimeIcon />
                  <span>{meetingMeta.meetingDateText}</span>
                </div>
                <div
                  className={`${styles.mobileProfileHeroMetaItem} ${
                    isEnglishMobileProfile
                      ? styles.mobileProfileHeroMetaItemEn
                      : ''
                  }`.trim()}
                >
                  <AgendaLocationIcon />
                  <span>
                    {meetingMeta.meetingLocationText}
                    {meetingMeta.meetingLocationSubline ? (
                      <>
                        <br />
                        {meetingMeta.meetingLocationSubline}
                      </>
                    ) : null}
                  </span>
                </div>
              </div>
            </div>
          </section>
          <div className={styles.mobileProfileContentCard}>
            <section className={styles.mobileProfileSummarySection}>
              <h2>{meetingMeta.meetingTitle}</h2>
              <p>{meetingMeta.meetingDescription}</p>
            </section>
            <section className={styles.mobileProfileInfoSection}>
              <div className={styles.mobileProfileSectionTitle}>
                <span className={styles.mobileProfileSectionMarker} />
                <strong>{text.profileBaseInfoTitle}</strong>
              </div>
              {renderProfileEdit()}
            </section>
            {isEnglishMobileProfile ? (
              <button
                type="button"
                className={styles.mobileProfileDeleteEntry}
                onClick={() => setAccountDeletionOpen(true)}
              >
                <PoweroffIcon size={14} />
                <span>Delete Account</span>
              </button>
            ) : null}
          </div>
        </div>
        {canEdit && (
          <div className={styles.mobileProfileFooter}>
            <button
              type="button"
              className={styles.mobileProfilePrimaryButton}
              disabled={profileLoading || showBlockedState}
              onClick={() => {
                setMobileProfileViewMode('edit');
              }}
            >
              {showBlockedState
                ? text.profileUpdateReviewing
                : meetingMeta.editButtonText || text.editInfo}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderContacts = () =>
    (() => {
      const detailAgendaSections = buildAgendaSections(
        detail?.Agenda || [],
        text,
        detail?.ContactType
      );
      const detailAvatarDecorated =
        shouldDecorateConferenceChatAvatarByContactType(detail?.ContactType);

      const renderContactDetailPanel = (showBackAction = false) => (
        <div className={styles.detailPanel}>
          {selected && contactDetailLoading && !detail ? (
            showBackAction ? (
              renderDesktopContactDetailSkeleton()
            ) : (
              <div className={styles.detailLoadingPanel}>
                <div className={styles.detailHeader}>
                  <Skeleton
                    animation="gradient"
                    rowCol={[
                      [
                        { type: 'circle', size: '72px', marginRight: '18px' },
                        { width: '132px', height: '24px' },
                      ],
                      [
                        {
                          width: '220px',
                          height: '16px',
                          margin: '6px 0 0 90px',
                        },
                      ],
                    ]}
                  />
                </div>
                <section>
                  <h3>
                    {renderTextSkeleton(
                      [{ width: '72px', height: '24px' }],
                      styles.sectionTitleSkeleton
                    )}
                  </h3>
                  {renderTextSkeleton(
                    [
                      { width: '100%', height: '16px' },
                      { width: '100%', height: '16px' },
                      { width: '76%', height: '16px' },
                    ],
                    styles.detailBioSkeleton
                  )}
                </section>
                <section>
                  <h3>
                    {renderTextSkeleton(
                      [{ width: '96px', height: '24px' }],
                      styles.sectionTitleSkeleton
                    )}
                  </h3>
                  {renderAgendaCardSkeletons(1, {
                    className: styles.agendaList,
                    mode: 'simple',
                  })}
                </section>
              </div>
            )
          ) : detail ? (
            showBackAction ? (
              <div className={styles.detailWithBreadcrumb}>
                <Breadcrumb className={styles.detailBreadcrumb}>
                  <Breadcrumb.BreadcrumbItem
                    onClick={() => {
                      setSelected(undefined);
                      setDetail(undefined);
                    }}
                  >
                    {text.contacts}
                  </Breadcrumb.BreadcrumbItem>
                  <Breadcrumb.BreadcrumbItem>
                    {text.guestDetail}
                  </Breadcrumb.BreadcrumbItem>
                </Breadcrumb>
                <div className={styles.detailCard}>
                  <div className={styles.detailHeader}>
                    <div className={styles.detailHeaderMain}>
                      <div
                        className={`${styles.detailAvatarWrap} ${
                          detailAvatarDecorated
                            ? styles.detailAvatarWrapDecorated
                            : ''
                        }`.trim()}
                      >
                        <Avatar
                          src={detail.Avatar}
                          name={getContactName(locale, detail)}
                        />
                        {detailAvatarDecorated ? (
                          <span className={styles.detailAvatarDecor}>
                            <ProfileDecorIcon />
                          </span>
                        ) : null}
                      </div>
                      <div className={styles.detailHeaderContent}>
                        <h2>{getContactName(locale, detail)}</h2>
                        <div className={styles.detailTitleRow}>
                          {getContactTitleLines(locale, detail).map((item) => (
                            <span key={item}>{item}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {isSelfContact &&
                      canEditConferenceChatProfile(auth?.ContactType) && (
                        <Button
                          theme="primary"
                          disabled={isProfileUpdateBlocked(detail)}
                          loading={profileLoading}
                          onClick={() => void openProfileEdit()}
                        >
                          {isProfileUpdateBlocked(detail)
                            ? text.profileUpdateReviewing
                            : text.editInfo}
                        </Button>
                      )}
                  </div>
                  <div className={styles.detailDivider} />
                  <section className={styles.detailSection}>
                    <h3>{text.intro}</h3>
                    <p className={styles.bio}>
                      {pickLocaleText(locale, detail.BioCN, detail.BioEN)}
                    </p>
                  </section>
                  {detailAgendaSections.length ? (
                    <>
                      <div className={styles.detailDivider} />
                      {detailAgendaSections.map((section, index) => (
                        <section
                          key={section.key}
                          className={styles.detailSection}
                        >
                          {index > 0 ? (
                            <div className={styles.detailDivider} />
                          ) : null}
                          <h3>{section.title}</h3>
                          <div className={styles.detailAgendaList}>
                            {renderAgendaItems(section.items)}
                          </div>
                        </section>
                      ))}
                    </>
                  ) : null}
                  <button
                    className={styles.detailChatButton}
                    aria-label={text.startChat}
                    onClick={() => openMessageWithContact(detail)}
                  >
                    <HoverChatIcon />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.detailHeader}>
                  <span
                    className={`${styles.detailHeaderAvatarWrap} ${
                      detailAvatarDecorated
                        ? styles.detailHeaderAvatarWrapDecorated
                        : ''
                    }`.trim()}
                  >
                    <Avatar
                      src={detail.Avatar}
                      name={getContactName(locale, detail)}
                    />
                    {detailAvatarDecorated ? (
                      <span className={styles.detailHeaderAvatarDecor}>
                        <ProfileDecorIcon />
                      </span>
                    ) : null}
                  </span>
                  <div>
                    <h2>{getContactName(locale, detail)}</h2>
                    <p>{getContactTitle(locale, detail)}</p>
                  </div>
                  {isSelfContact &&
                    canEditConferenceChatProfile(auth?.ContactType) && (
                      <Button
                        theme="primary"
                        disabled={isProfileUpdateBlocked(detail)}
                        loading={profileLoading}
                        onClick={() => void openProfileEdit()}
                      >
                        {isProfileUpdateBlocked(detail)
                          ? text.profileUpdateReviewing
                          : text.editInfo}
                      </Button>
                    )}
                </div>
                <section>
                  <h3>{text.intro}</h3>
                  <p className={styles.bio}>
                    {pickLocaleText(locale, detail.BioCN, detail.BioEN)}
                  </p>
                </section>
                {detailAgendaSections.length ? (
                  detailAgendaSections.map((section) => (
                    <section key={section.key}>
                      <h3>{section.title}</h3>
                      <div className={styles.agendaList}>
                        {renderAgendaItems(section.items)}
                      </div>
                    </section>
                  ))
                ) : (
                  <section>
                    <h3>{text.guestAgenda}</h3>
                    <div className={styles.empty}>{text.emptyAgenda}</div>
                  </section>
                )}
                <button
                  className={styles.chatButton}
                  onClick={() => openMessageWithContact(detail)}
                >
                  {text.startChat}
                </button>
              </>
            )
          ) : (
            <div className={styles.empty}>{text.selectContact}</div>
          )}
        </div>
      );

      const renderMobileContactDetailPanel = () => {
        if (selected && contactDetailLoading && !detail) {
          return renderMobileContactDetailSkeleton();
        }

        if (!detail) {
          return (
            <div className={styles.mobileContactDetailOverlay}>
              <div className={styles.mobileContactDetailLoading}>
                {text.selectContact}
              </div>
            </div>
          );
        }

        const detailTitles = getContactTitleLines(locale, detail);
        const introText = pickLocaleText(
          locale,
          detail.BioCN,
          detail.BioEN,
          ''
        );
        const mobileAgendaSections = buildAgendaSections(
          detail.Agenda || [],
          text,
          detail?.ContactType
        );

        return (
          <div className={styles.mobileContactDetailOverlay}>
            <img
              src={getMobileContactDetailBg(detail)}
              alt=""
              className={styles.mobilePageBackground}
            />
            <div className={styles.mobileContactDetailHeader}>
              <button
                className={styles.mobileContactDetailBack}
                onClick={() => {
                  setSelected(undefined);
                  setDetail(undefined);
                }}
                type="button"
                aria-label={text.backToContacts}
              >
                <BackChevronIcon />
              </button>
            </div>
            <div className={styles.mobileContactDetailScroll}>
              <div className={styles.mobileContactDetailHero}>
                <div className={styles.mobileContactDetailHeroTop}>
                  <div className={styles.mobileContactDetailProfileBlock}>
                    <div
                      className={`${styles.mobileContactDetailAvatarWrap} ${
                        detailAvatarDecorated
                          ? styles.mobileContactDetailAvatarWrapDecorated
                          : ''
                      }`.trim()}
                    >
                      <Avatar
                        src={detail.Avatar}
                        name={getContactName(locale, detail)}
                      />
                      {detailAvatarDecorated ? (
                        <span className={styles.mobileContactDetailAvatarDecor}>
                          <ProfileDecorIcon />
                        </span>
                      ) : null}
                    </div>
                    <div className={styles.mobileContactDetailIdentity}>
                      <strong>{getContactName(locale, detail)}</strong>
                    </div>
                  </div>
                  <button
                    className={styles.mobileContactDetailChatButton}
                    onClick={() => openMessageWithContact(detail)}
                    type="button"
                  >
                    {text.startChat}
                  </button>
                </div>
                <div className={styles.mobileContactDetailTitles}>
                  {detailTitles.length
                    ? detailTitles.map((item) => <span key={item}>{item}</span>)
                    : null}
                </div>
              </div>

              <div className={styles.mobileContactDetailBody}>
                <section
                  className={`${styles.mobileContactDetailSection} ${
                    locale === 'en' ? styles.mobileContactDetailSectionEn : ''
                  }`.trim()}
                >
                  <h3>{text.intro}</h3>
                  <p className={styles.mobileContactDetailBio}>
                    {introText || '-'}
                  </p>
                </section>

                {mobileAgendaSections.map((section) => (
                  <section
                    key={section.key}
                    className={`${styles.mobileContactDetailSection} ${
                      locale === 'en' ? styles.mobileContactDetailSectionEn : ''
                    }`.trim()}
                  >
                    <h3>{section.title}</h3>
                    <div className={styles.mobileContactDetailAgendaList}>
                      {renderAgendaItems(section.items)}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        );
      };

      const renderMobileContactEmptyState = (
        textValue: string,
        search = false
      ) => (
        <div
          className={`${styles.mobileContactEmptyState} ${
            search ? styles.mobileContactEmptyStateSearch : ''
          }`}
        >
          <img
            src={MOBILE_CONTACT_EMPTY_STATE_SRC}
            alt=""
            className={styles.mobileContactEmptyStateImage}
          />
          <p>{textValue}</p>
        </div>
      );

      const renderMobileContactEntries = (
        list: ContactItem[],
        options?: {
          search?: boolean;
          emptyText?: string;
          onPick?: (item: ContactItem) => void;
        }
      ) => {
        if (!list.length) {
          return renderMobileContactEmptyState(
            options?.emptyText || text.emptyContacts,
            options?.search
          );
        }
        return (
          <div className={styles.mobileContactFlatList}>
            {list.map((item) => {
              const contactAvatarDecorated =
                shouldDecorateConferenceChatAvatarByContactType(
                  item.ContactType
                );
              return (
                <button
                  key={`${item.ContactType}-${item.ContactID}-${
                    item.GuestDataSource || ''
                  }`}
                  className={styles.mobileContactEntry}
                  onClick={() => {
                    options?.onPick?.(item);
                    openContactDetail(item);
                  }}
                  type="button"
                >
                  <span
                    className={`${styles.mobileContactEntryAvatar} ${
                      contactAvatarDecorated
                        ? styles.mobileContactEntryAvatarDecorated
                        : ''
                    }`.trim()}
                  >
                    <Avatar
                      src={item.Avatar}
                      name={getContactName(locale, item)}
                    />
                    {contactAvatarDecorated ? (
                      <span className={styles.mobileContactEntryAvatarDecor}>
                        <ProfileDecorIcon />
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.mobileContactEntryMain}>
                    <strong>{getContactName(locale, item)}</strong>
                    <em>{getContactTitle(locale, item)}</em>
                  </span>
                </button>
              );
            })}
          </div>
        );
      };

      const renderMobileContactSearchOverlay = () => (
        <div className={styles.mobileContactSearchOverlay}>
          <div className={styles.mobileContactSearchHeader}>
            <button
              className={styles.mobileContactSearchBack}
              onClick={closeMobileContactSearch}
              type="button"
              aria-label={text.backToContacts}
            >
              <BackChevronIcon />
            </button>
            <h3>{text.mobileContactSearchPageTitle}</h3>
          </div>
          <div className={styles.mobileContactSearchBar}>
            <label className={styles.mobileContactSearchInputWrap}>
              <span className={styles.mobileContactSearchInputIcon}>
                <SearchInputIcon />
              </span>
              <input
                ref={mobileSearchInputRef}
                className={styles.mobileContactSearchInput}
                value={mobileContactSearchKeyword}
                placeholder={text.mobileContactSearchInputPlaceholder}
                onChange={(event) =>
                  setMobileContactSearchKeyword(event.target.value)
                }
                onCompositionStart={() => setMobileContactSearchComposing(true)}
                onCompositionEnd={(event) => {
                  setMobileContactSearchComposing(false);
                  setMobileContactSearchAppliedKeyword(
                    event.currentTarget.value
                  );
                }}
              />
              {mobileContactSearchKeyword ? (
                <button
                  className={styles.mobileContactSearchInputClear}
                  onClick={clearMobileContactSearchKeyword}
                  type="button"
                  aria-label="清除搜索内容"
                >
                  <CloseIcon size={14} />
                </button>
              ) : null}
            </label>
            <button
              className={styles.mobileContactSearchCancel}
              onClick={closeMobileContactSearch}
              type="button"
            >
              {text.mobileContactSearchCancel}
            </button>
          </div>
          <div className={styles.mobileContactSearchContent}>
            {mobileSearchKeywordNormalized ? (
              <>
                <div className={styles.mobileContactSegmented}>
                  <button
                    className={
                      contactType === 'guest' ? styles.activeSegment : ''
                    }
                    onClick={() => changeContactType('guest')}
                    type="button"
                  >
                    {text.guest}
                  </button>
                  <button
                    className={
                      contactType === 'partner' ? styles.activeSegment : ''
                    }
                    onClick={() => changeContactType('partner')}
                    type="button"
                  >
                    {text.partner}
                  </button>
                </div>
                <div className={styles.mobileContactSearchResultList}>
                  {renderMobileContactEntries(activeMobileSearchResults, {
                    search: true,
                    emptyText: text.mobileContactSearchEmpty,
                    onPick: () => {
                      appendMobileSearchHistory(
                        mobileContactSearchAppliedKeyword
                      );
                      closeMobileContactSearch();
                    },
                  })}
                </div>
              </>
            ) : mobileContactSearchHistory.length > 0 ? (
              <div className={styles.mobileContactSearchHistory}>
                <div className={styles.mobileContactSearchHistoryHeader}>
                  <strong>{text.mobileContactSearchHistory}</strong>
                  <button
                    className={styles.mobileContactSearchHistoryClear}
                    onClick={clearMobileSearchHistory}
                    type="button"
                    aria-label={text.clearHistory}
                  >
                    <DeleteIcon />
                  </button>
                </div>
                <div className={styles.mobileContactSearchHistoryTags}>
                  {mobileContactSearchHistory.map((item) => (
                    <button
                      key={item}
                      className={styles.mobileContactSearchHistoryTag}
                      onClick={() => {
                        setMobileContactSearchKeyword(item);
                        setMobileContactSearchAppliedKeyword(item);
                      }}
                      type="button"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );

      const renderMobileContactList = () => (
        <div className={styles.mobileContactPanel}>
          <button
            className={styles.mobileContactSearchTrigger}
            onClick={() => void openMobileContactSearch()}
            type="button"
          >
            <span className={styles.mobileContactSearchMain}>
              <span className={styles.mobileContactSearchIcon}>
                <SearchInputIcon />
              </span>
              <span className={styles.mobileContactSearchPlaceholder}>
                {text.mobileContactSearchPlaceholder}
              </span>
            </span>
          </button>
          <div className={styles.mobileContactSegmented}>
            <button
              className={contactType === 'guest' ? styles.activeSegment : ''}
              onClick={() => changeContactType('guest')}
              type="button"
            >
              {text.guest}
            </button>
            <button
              className={contactType === 'partner' ? styles.activeSegment : ''}
              onClick={() => changeContactType('partner')}
              type="button"
            >
              {text.partner}
            </button>
          </div>
          <div className={styles.mobileContactListWrap}>
            <div
              ref={mobileContactListRef}
              className={styles.mobileContactList}
            >
              {contactsLoading
                ? renderMobileContactEntrySkeletons(2)
                : visibleContacts.length
                ? mobileGroupedContacts.map((group) => (
                    <section
                      key={group.letter}
                      ref={(node) => {
                        mobileContactSectionRefs.current[group.letter] = node;
                      }}
                      className={styles.mobileContactGroup}
                    >
                      <div
                        className={`${styles.mobileContactGroupTitle} notranslate`}
                        data-letter={group.letter}
                        translate="no"
                      />
                      <div className={styles.mobileContactGroupBody}>
                        {renderMobileContactEntries(group.items)}
                      </div>
                    </section>
                  ))
                : contactsLoaded
                ? renderMobileContactEmptyState(text.emptyContacts)
                : renderMobileContactEntrySkeletons(2)}
            </div>
            {mobileContactIndexLetters.length > 0 && (
              <div className={styles.mobileContactIndex}>
                {mobileContactIndexLetters.map((letter) => (
                  <button
                    key={letter}
                    onClick={() => {
                      setActiveMobileContactLetter(letter);
                      mobileContactSectionRefs.current[letter]?.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                      });
                    }}
                    aria-pressed={activeMobileContactLetter === letter}
                    className={`${styles.mobileContactIndexButton} ${
                      activeMobileContactLetter === letter
                        ? styles.mobileContactIndexButtonActive
                        : ''
                    }`}
                    aria-label={letter}
                    data-letter={letter}
                    translate="no"
                    type="button"
                  />
                ))}
              </div>
            )}
          </div>
          {mobileContactSearchVisible && renderMobileContactSearchOverlay()}
        </div>
      );

      const renderDesktopContactList = () => (
        <div className={`${styles.contactPanel} ${styles.contactPanelDesktop}`}>
          {renderContactToolbar(true)}
          <div className={styles.contactCardGrid}>
            {contactsLoading
              ? renderDesktopContactCardSkeletons(4)
              : visibleContacts.map((item) => {
                  const contactAvatarDecorated =
                    shouldDecorateConferenceChatAvatarByContactType(
                      item.ContactType
                    );
                  return (
                    <article
                      key={`${item.ContactType}-${item.ContactID}-${
                        item.GuestDataSource || ''
                      }`}
                      className={styles.contactPersonCard}
                    >
                      <button
                        className={styles.contactPersonCardMain}
                        onClick={() => {
                          openContactDetail(item);
                        }}
                      >
                        <span
                          className={`${styles.contactPersonAvatarWrap} ${
                            contactAvatarDecorated
                              ? styles.contactPersonAvatarWrapDecorated
                              : ''
                          }`.trim()}
                        >
                          <Avatar
                            src={item.Avatar}
                            name={getContactName(locale, item)}
                          />
                          {contactAvatarDecorated ? (
                            <span className={styles.contactPersonDecor}>
                              <ProfileDecorIcon />
                            </span>
                          ) : null}
                        </span>
                        <span className={styles.contactPersonContent}>
                          <strong>{getContactName(locale, item)}</strong>
                          <span className={styles.contactPersonTitles}>
                            {getContactTitleLines(locale, item).map(
                              (title, index) => (
                                <em key={`${title}-${index}`}>{title}</em>
                              )
                            )}
                          </span>
                        </span>
                      </button>
                      <button
                        className={styles.contactPersonChatButton}
                        onClick={() => openMessageWithContact(item)}
                        aria-label={text.startChat}
                      >
                        <HoverChatIcon />
                      </button>
                    </article>
                  );
                })}
            {!contactsLoading && contactsLoaded && !visibleContacts.length && (
              <DesktopEmptyState
                className={styles.contactGridEmpty}
                message={text.emptyContacts}
              />
            )}
          </div>
        </div>
      );

      if (isMobileViewport) {
        return selected
          ? renderMobileContactDetailPanel()
          : renderMobileContactList();
      }

      return selected
        ? renderContactDetailPanel(true)
        : renderDesktopContactList();
    })();

  const renderMessageList = () => {
    const selfAvatarDecorated = shouldDecorateConferenceChatAvatarByContactType(
      auth?.ContactType
    );
    const showMobileMessageListSkeleton =
      (messageBootstrapping || conversationsLoading) &&
      !conversationViews.length;

    const renderMobileMessageDetailPanel = () =>
      selectedConversation ? (
        <div className={styles.mobileMessageDetailOverlay}>
          <div className={styles.mobileMessageDetailHeader}>
            <button
              className={styles.mobileMessageDetailBack}
              onClick={() => {
                if (
                  selectedConversation.isDraft &&
                  !threadMessages.length &&
                  !composerValue.trim()
                ) {
                  setMessageDraftPeer(undefined);
                }
                setSelectedConversationKey('');
              }}
              aria-label={text.backToContacts}
            >
              <BackChevronIcon />
            </button>
            <h3>
              {selectedConversationPeerOffline
                ? text.conversationPeerExited
                : selectedConversation.peerSummary.name}
            </h3>
          </div>
          <div
            ref={mobileMessageDetailBodyRef}
            className={styles.mobileMessageDetailBody}
          >
            <div className={styles.mobileMessageThread}>
              {threadLoading && !threadMessages.length
                ? renderThreadPanelSkeleton(true)
                : null}
              {!threadLoading && !threadMessages.length && (
                <div className={styles.threadEmpty}>
                  {text.messageThreadEmpty}
                </div>
              )}
              {threadMessages.map((item, index) => {
                const needDivider = threadMessageDividerFlags[index];
                const messageAvatar = item.fromSelf
                  ? auth?.Avatar
                  : selectedConversationPeerAvatar;
                const messageName = item.fromSelf
                  ? getContactName(locale, auth)
                  : selectedConversation.peerSummary.name;
                const messageAvatarDecorated = item.fromSelf
                  ? selfAvatarDecorated
                  : !selectedConversationPeerOffline &&
                    shouldDecorateConferenceChatAvatarByParticipantType(
                      selectedConversation.peerSummary.participant.type
                    );
                return (
                  <div key={item.key}>
                    {needDivider && (
                      <div className={styles.messageDivider}>
                        {formatMessageDividerTime(locale, item.sendTime)}
                      </div>
                    )}
                    <div
                      className={`${styles.messageRow} ${
                        item.fromSelf ? styles.messageRowOwn : ''
                      }`}
                    >
                      {!item.fromSelf && (
                        <span
                          className={`${styles.messageAvatar} ${
                            messageAvatarDecorated
                              ? styles.messageAvatarDecorated
                              : ''
                          }`.trim()}
                        >
                          <Avatar src={messageAvatar} name={messageName} />
                          {messageAvatarDecorated ? (
                            <span className={styles.messageAvatarDecor}>
                              <ProfileDecorIcon />
                            </span>
                          ) : null}
                        </span>
                      )}
                      <div
                        className={`${styles.messageBubble} ${
                          item.fromSelf ? styles.messageBubbleOwn : ''
                        } ${
                          item.decryptStatus === 'failed'
                            ? styles.messageBubbleFailed
                            : ''
                        }`}
                      >
                        <p>{item.plaintext}</p>
                      </div>
                      {item.fromSelf && (
                        <span
                          className={`${styles.messageAvatar} ${
                            messageAvatarDecorated
                              ? styles.messageAvatarDecorated
                              : ''
                          }`.trim()}
                        >
                          <Avatar src={messageAvatar} name={messageName} />
                          {messageAvatarDecorated ? (
                            <span className={styles.messageAvatarDecor}>
                              <ProfileDecorIcon />
                            </span>
                          ) : null}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {messageHint && (
              <div className={styles.messageHint}>{messageHint}</div>
            )}
          </div>
          {!selectedConversationIsSystemNotice && (
            <div className={styles.mobileMessageComposer}>
              {threadLoading && !threadMessages.length ? (
                <Skeleton
                  animation="gradient"
                  className={styles.mobileMessageComposerSkeleton}
                  rowCol={[[{ width: '100%', height: '100%', type: 'rect' }]]}
                />
              ) : (
                <input
                  className={styles.mobileMessageComposerInput}
                  value={composerValue}
                  maxLength={CHAT_MAX_MESSAGE_LENGTH}
                  disabled={!selectedConversationCanSend}
                  placeholder={selectedConversationInputPlaceholder}
                  enterKeyHint="send"
                  onChange={(event) => setComposerValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      !event.nativeEvent.isComposing
                    ) {
                      event.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
      ) : null;

    const renderThreadContent = (mobile = false) => {
      if (!selectedConversation) {
        if (
          !messageListLoaded ||
          messageBootstrapping ||
          conversationsLoading
        ) {
          return renderThreadPanelSkeleton(mobile);
        }
        return <div className={styles.empty}>{text.messageSelectEmpty}</div>;
      }

      const threadPeerAvatarDecorated =
        !selectedConversationPeerOffline &&
        shouldDecorateConferenceChatAvatarByParticipantType(
          selectedConversation.peerSummary.participant.type
        );

      return (
        <>
          {mobile ? (
            <div className={styles.mobileThreadHeader}>
              <button
                className={styles.mobileThreadBack}
                onClick={() => {
                  if (
                    selectedConversation.isDraft &&
                    !threadMessages.length &&
                    !composerValue.trim()
                  ) {
                    setMessageDraftPeer(undefined);
                  }
                  setSelectedConversationKey('');
                }}
                aria-label={text.backToContacts}
              >
                <BackChevronIcon />
              </button>
              <div className={styles.threadPeer}>
                <span
                  className={`${styles.threadPeerAvatarWrap} ${
                    threadPeerAvatarDecorated
                      ? styles.threadPeerAvatarWrapDecorated
                      : ''
                  }`.trim()}
                >
                  <Avatar
                    src={selectedConversationPeerAvatar}
                    name={selectedConversation.peerSummary.name}
                  />
                  {threadPeerAvatarDecorated ? (
                    <span className={styles.threadPeerAvatarDecor}>
                      <ProfileDecorIcon />
                    </span>
                  ) : null}
                </span>
                <span>
                  <strong>
                    {selectedConversationPeerOffline
                      ? text.conversationPeerExited
                      : selectedConversation.peerSummary.name}
                  </strong>
                  <em>{selectedConversation.peerSummary.title}</em>
                </span>
              </div>
            </div>
          ) : (
            !isEmbeddedDesktop && (
              <div className={styles.threadHeader}>
                <div className={styles.threadPeer}>
                  <span
                    className={`${styles.threadPeerAvatarWrap} ${
                      threadPeerAvatarDecorated
                        ? styles.threadPeerAvatarWrapDecorated
                        : ''
                    }`.trim()}
                  >
                    <Avatar
                      src={selectedConversationPeerAvatar}
                      name={selectedConversation.peerSummary.name}
                    />
                    {threadPeerAvatarDecorated ? (
                      <span className={styles.threadPeerAvatarDecor}>
                        <ProfileDecorIcon />
                      </span>
                    ) : null}
                  </span>
                  <span>
                    <strong>
                      {selectedConversationPeerOffline
                        ? text.conversationPeerExited
                        : selectedConversation.peerSummary.name}
                    </strong>
                    <em>{selectedConversation.peerSummary.title}</em>
                  </span>
                </div>
              </div>
            )
          )}
          <div ref={threadBodyRef} className={styles.threadBody}>
            {threadLoading &&
              !threadMessages.length &&
              renderThreadPanelSkeleton()}
            {!threadLoading && !threadMessages.length && (
              <div className={styles.threadEmpty}>
                {text.messageThreadEmpty}
              </div>
            )}
            {threadMessages.map((item, index) => {
              const needDivider = threadMessageDividerFlags[index];
              const messageAvatar = item.fromSelf
                ? auth?.Avatar
                : selectedConversationPeerAvatar;
              const messageName = item.fromSelf
                ? getContactName(locale, auth)
                : selectedConversation.peerSummary.name;
              const messageAvatarDecorated = item.fromSelf
                ? selfAvatarDecorated
                : !selectedConversationPeerOffline &&
                  shouldDecorateConferenceChatAvatarByParticipantType(
                    selectedConversation.peerSummary.participant.type
                  );
              return (
                <div key={item.key}>
                  {needDivider && (
                    <div className={styles.messageDivider}>
                      {formatMessageDividerTime(locale, item.sendTime)}
                    </div>
                  )}
                  <div
                    className={`${styles.messageRow} ${
                      item.fromSelf ? styles.messageRowOwn : ''
                    }`}
                  >
                    {(isEmbeddedDesktop || mobile) && !item.fromSelf && (
                      <span
                        className={`${styles.messageAvatar} ${
                          messageAvatarDecorated
                            ? styles.messageAvatarDecorated
                            : ''
                        }`.trim()}
                      >
                        <Avatar src={messageAvatar} name={messageName} />
                        {messageAvatarDecorated ? (
                          <span className={styles.messageAvatarDecor}>
                            <ProfileDecorIcon />
                          </span>
                        ) : null}
                      </span>
                    )}
                    <div
                      className={`${styles.messageBubble} ${
                        item.fromSelf ? styles.messageBubbleOwn : ''
                      } ${
                        item.decryptStatus === 'failed'
                          ? styles.messageBubbleFailed
                          : ''
                      }`}
                    >
                      <p>{item.plaintext}</p>
                      {!isEmbeddedDesktop && !mobile && (
                        <time>{formatChatTime(item.sendTime)}</time>
                      )}
                    </div>
                    {(isEmbeddedDesktop || mobile) && item.fromSelf && (
                      <span
                        className={`${styles.messageAvatar} ${
                          messageAvatarDecorated
                            ? styles.messageAvatarDecorated
                            : ''
                        }`.trim()}
                      >
                        <Avatar src={messageAvatar} name={messageName} />
                        {messageAvatarDecorated ? (
                          <span className={styles.messageAvatarDecor}>
                            <ProfileDecorIcon />
                          </span>
                        ) : null}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {messageHint && (
            <div className={styles.messageHint}>{messageHint}</div>
          )}
          {!selectedConversationIsSystemNotice && (
            <div className={styles.composer}>
              {isEmbeddedDesktop ? (
                <input
                  type="text"
                  className={styles.composerTextarea}
                  value={composerValue}
                  maxLength={CHAT_MAX_MESSAGE_LENGTH}
                  disabled={!selectedConversationCanSend}
                  placeholder={selectedConversationInputPlaceholder}
                  onChange={(event) => setComposerValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      !event.shiftKey &&
                      !event.nativeEvent.isComposing
                    ) {
                      event.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                />
              ) : (
                <textarea
                  className={styles.composerTextarea}
                  value={composerValue}
                  maxLength={CHAT_MAX_MESSAGE_LENGTH}
                  disabled={!selectedConversationCanSend}
                  placeholder={selectedConversationInputPlaceholder}
                  onChange={(event) => setComposerValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      !event.shiftKey &&
                      !event.nativeEvent.isComposing
                    ) {
                      event.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                />
              )}
              {!isEmbeddedDesktop && (
                <div className={styles.composerFooter}>
                  <span>
                    {composerValue.length}/{CHAT_MAX_MESSAGE_LENGTH}
                  </span>
                  <Button
                    theme="primary"
                    icon={<SendIcon />}
                    loading={sending}
                    disabled={!selectedConversationCanSend}
                    onClick={() => void handleSendMessage()}
                  >
                    {text.messageSend}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      );
    };

    const messageContent = (
      <div
        className={`${styles.messageGrid} ${
          isEmbeddedDesktop ? styles.messageGridDesktop : ''
        }`.trim()}
      >
        <div className={styles.conversationPanel}>
          {!isEmbeddedDesktop && (
            <div className={styles.messageToolbar}>
              <strong>{text.messages}</strong>
              <Button
                variant="outline"
                size="small"
                icon={<RefreshIcon />}
                loading={conversationsLoading || threadRefreshing}
                onClick={() => void refreshMessageArea()}
              >
                {text.refresh}
              </Button>
            </div>
          )}
          <div className={styles.conversationList}>
            {(messageBootstrapping || conversationsLoading) &&
              !conversationViews.length &&
              renderConversationCardSkeletons(2)}
            {!messageBootstrapping &&
              !conversationsLoading &&
              messageListLoaded &&
              !conversationViews.length && (
                <div className={styles.empty}>{text.messageListEmpty}</div>
              )}
            {conversationViews.map((item) => {
              const isPeerOffline = isConferenceChatPeerOffline(
                item.peerDisplayStatus,
                item.disableReason
              );
              const conversationAvatarDecorated =
                !isPeerOffline &&
                shouldDecorateConferenceChatAvatarByParticipantType(
                  item.peerSummary.participant.type
                );
              return (
                <button
                  key={item.key}
                  className={`${styles.conversationCard} ${
                    selectedConversationKey === item.key
                      ? styles.selectedCard
                      : ''
                  }`}
                  onClick={() => openConversation(item.key)}
                >
                  <span
                    className={`${styles.conversationAvatar} ${
                      conversationAvatarDecorated
                        ? styles.conversationAvatarDecorated
                        : ''
                    }`.trim()}
                  >
                    <Avatar
                      src={getConversationPeerAvatar(item)}
                      name={item.peerSummary.name}
                    />
                    {conversationAvatarDecorated ? (
                      <span className={styles.conversationAvatarDecor}>
                        <ProfileDecorIcon />
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.conversationMain}>
                    <span className={styles.conversationTitleRow}>
                      <strong>
                        {isPeerOffline
                          ? text.conversationPeerExited
                          : item.peerSummary.name}
                      </strong>
                      {isEmbeddedDesktop ? (
                        <time>
                          {formatConversationCardTime(
                            locale,
                            item.lastMessageTime
                          )}
                        </time>
                      ) : (
                        item.unread && <i className={styles.unreadDot} />
                      )}
                    </span>
                    <small>
                      {item.preview.status === 'empty'
                        ? ''
                        : item.preview.text || text.messagePreviewFallback}
                    </small>
                  </span>
                  {isEmbeddedDesktop ? (
                    item.unread && <i className={styles.unreadDot} />
                  ) : (
                    <time>{formatChatTime(item.lastMessageTime)}</time>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.threadPanel}>{renderThreadContent()}</div>
      </div>
    );

    if (!isMobileViewport) {
      return messageContent;
    }

    if (selectedConversation) {
      return renderMobileMessageDetailPanel();
    }

    return (
      <div className={styles.mobileMessageSection}>
        <div className={styles.mobileMessageContainer}>
          {showMobileMessageListSkeleton ? (
            renderMobileSelfCardSkeleton()
          ) : (
            <button
              className={styles.mobileSelfCard}
              onClick={() => {
                if (
                  auth?.ContactID &&
                  normalizeConferenceChatContactType(auth.ContactType) !==
                    CHAT_CONTACT_TYPE_AUDIENCE
                ) {
                  void openProfileEdit();
                }
              }}
            >
              <span
                className={`${styles.mobileSelfAvatar} ${
                  selfAvatarDecorated ? styles.mobileSelfAvatarDecorated : ''
                }`.trim()}
              >
                <Avatar
                  src={auth?.Avatar}
                  name={getContactName(locale, auth)}
                />
                {selfAvatarDecorated ? (
                  <span className={styles.mobileSelfAvatarDecor}>
                    <ProfileDecorIcon />
                  </span>
                ) : null}
              </span>
              <span className={styles.mobileSelfCardContent}>
                <span className={styles.mobileSelfCardTitleRow}>
                  <strong>{getContactName(locale, auth)}</strong>
                  <span className={styles.mobileSelfIdentityTag}>
                    <span className={styles.mobileSelfIdentityIcon}>
                      {getMobileSelfIdentityIcon(auth?.ContactType)}
                    </span>
                    <span>
                      {getSelfIdentityLabel(locale, auth?.ContactType)}
                    </span>
                  </span>
                </span>
                {sidebarProfileTitles[0] && <em>{sidebarProfileTitles[0]}</em>}
              </span>
            </button>
          )}
          <div className={styles.mobileConversationList}>
            {showMobileMessageListSkeleton &&
              renderConversationCardSkeletons(2, true)}
            {!messageBootstrapping &&
              !conversationsLoading &&
              messageListLoaded &&
              !conversationViews.length && (
                <div className={styles.empty}>{text.messageListEmpty}</div>
              )}
            {conversationViews.map((item) => {
              const isPeerOffline = isConferenceChatPeerOffline(
                item.peerDisplayStatus,
                item.disableReason
              );
              const conversationAvatarDecorated =
                !isPeerOffline &&
                shouldDecorateConferenceChatAvatarByParticipantType(
                  item.peerSummary.participant.type
                );
              return (
                <button
                  key={item.key}
                  className={styles.mobileConversationCard}
                  onClick={() => openConversation(item.key)}
                >
                  <span
                    className={`${styles.conversationAvatar} ${
                      conversationAvatarDecorated
                        ? styles.conversationAvatarDecorated
                        : ''
                    }`.trim()}
                  >
                    <Avatar
                      src={getConversationPeerAvatar(item)}
                      name={item.peerSummary.name}
                    />
                    {conversationAvatarDecorated ? (
                      <span className={styles.conversationAvatarDecor}>
                        <ProfileDecorIcon />
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.mobileConversationMain}>
                    <span className={styles.mobileConversationTitleRow}>
                      <strong>
                        {isPeerOffline
                          ? text.conversationPeerExited
                          : item.peerSummary.name}
                      </strong>
                      <time>
                        {formatConversationCardTime(
                          locale,
                          item.lastMessageTime
                        )}
                      </time>
                    </span>
                    <small>
                      {item.preview.status === 'empty'
                        ? ''
                        : item.preview.text || text.messagePreviewFallback}
                    </small>
                  </span>
                  {item.unread && (
                    <i className={styles.mobileConversationUnreadDot} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const mobileMainBackgroundSrc =
    (isMobileViewport || loading) &&
    !profileMode &&
    !isMobileProfilePanelVisible &&
    !isMobileContactDetailVisible &&
    !mobileContactSearchVisible &&
    !isMobileMessageDetailVisible
      ? getMobileMainBackgroundSrc(mainTab)
      : '';

  return (
    <main
      className={`${styles.page} ${embedded ? styles.pageEmbedded : ''}`.trim()}
    >
      {mobileMainBackgroundSrc && (
        <img
          src={mobileMainBackgroundSrc}
          alt=""
          className={styles.mobilePageBackground}
        />
      )}
      <div className={styles.pageInner}>
        {!isMobileProfilePanelVisible && (
          <aside className={styles.sidebar}>
            {loading && !isMobileViewport ? (
              renderSidebarProfileSkeleton()
            ) : (
              <button
                className={styles.profile}
                onClick={() => {
                  if (
                    auth?.ContactID &&
                    canEditConferenceChatProfile(auth.ContactType)
                  ) {
                    void openProfileEdit();
                  }
                }}
              >
                <span
                  className={`${styles.profileAvatarWrap} ${
                    shouldDecorateConferenceChatAvatarByContactType(
                      auth?.ContactType
                    )
                      ? styles.profileAvatarWrapDecorated
                      : ''
                  }`.trim()}
                >
                  <Avatar
                    src={auth?.Avatar}
                    name={getContactName(locale, auth)}
                  />
                  {shouldDecorateConferenceChatAvatarByContactType(
                    auth?.ContactType
                  ) ? (
                    <span className={styles.profileDecorIcon}>
                      <ProfileDecorIcon />
                    </span>
                  ) : null}
                </span>
                <span className={styles.profileContent}>
                  <strong>{getContactName(locale, auth)}</strong>
                  {sidebarProfileTitles.length > 0 && (
                    <span className={styles.profileSubtitleList}>
                      {sidebarProfileTitles.map((item, index) => (
                        <em key={`${item}-${index}`}>{item}</em>
                      ))}
                    </span>
                  )}
                </span>
              </button>
            )}
            <nav className={`${styles.sidebarNav} ${styles.sidebarNavDesktop}`}>
              {renderSidebarNavItems(desktopNavItems)}
            </nav>
            <nav className={`${styles.sidebarNav} ${styles.sidebarNavMobile}`}>
              {renderSidebarNavItems(mobileNavItems)}
            </nav>
          </aside>
        )}
        <section className={styles.main}>
          {!embedded &&
            !isMobileContactDetailVisible &&
            !isMobileProfilePanelVisible && (
              <header className={styles.header}>
                <div>
                  <h1>{text.title}</h1>
                  <p>
                    {conferenceYear
                      ? `${conferenceYear} ${text.summitName}`
                      : text.subtitle}
                  </p>
                </div>
              </header>
            )}
          {loading && (
            <div className={styles.pageLoadingSkeleton}>
              <div className={styles.desktopLoadingSkeleton}>
                {renderDesktopMainLoadingSkeleton()}
              </div>
              <div className={styles.mobileLoadingSkeleton}>
                {renderMobileMainLoadingSkeleton()}
              </div>
            </div>
          )}
          {!loading && !auth?.CanAccess && (
            <div className={styles.empty}>
              {auth?.DenyToast || text.noAccess}
            </div>
          )}
          {!loading &&
            auth?.CanAccess &&
            isMobileProfilePanelVisible &&
            renderMobileProfilePanel()}
          {!loading &&
            auth?.CanAccess &&
            profileMode &&
            !isMobileProfilePanelVisible &&
            renderProfileEdit()}
          {!loading &&
            auth?.CanAccess &&
            !profileMode &&
            mainTab === 'contacts' &&
            !isMobileContactDetailVisible &&
            renderContacts()}
          {!loading &&
            auth?.CanAccess &&
            !profileMode &&
            mainTab === 'messages' &&
            !isMobileMessageDetailVisible &&
            renderMessageList()}
          {!loading &&
            auth?.CanAccess &&
            !profileMode &&
            mainTab === 'agenda' && (
              <div className={styles.agendaPanel}>
                {isMobileViewport ? (
                  renderMobileAgenda()
                ) : isEmbeddedDesktop ? (
                  renderEmbeddedDesktopAgenda()
                ) : agendaLoading ? (
                  renderAgendaSectionSkeleton('desktop')
                ) : (
                  <div className={styles.agendaSectionList}>
                    {buildGuestAgendaSections(agenda, text).map((section) => (
                      <section
                        key={section.key}
                        className={styles.agendaSection}
                      >
                        <h3>{section.title}</h3>
                        <div className={styles.agendaList}>
                          {section.items.length ? (
                            renderAgendaItems(section.items)
                          ) : (
                            <div className={styles.empty}>
                              {text.emptyAgenda}
                            </div>
                          )}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            )}
          {isMobileContactDetailVisible && renderContacts()}
          {isMobileMessageDetailVisible && renderMessageList()}
          {!loading &&
            auth?.CanAccess &&
            !profileMode &&
            mainTab === 'contacts' &&
            isMobileViewport &&
            !isMobileContactDetailVisible &&
            !mobileContactSearchVisible &&
            renderEnPrivacyAlert(styles.privacyAlertMobile)}
        </section>
      </div>
      <AccountDeletionFlow
        locale={locale}
        mode={isMobileViewport ? 'mobile' : 'desktop'}
        accountEmail={profileForm.email}
        refreshAccountEmail={
          locale === 'en' ? refreshAccountDeletionEmail : undefined
        }
        open={accountDeletionOpen}
        onClose={() => setAccountDeletionOpen(false)}
      />
    </main>
  );
};
